import React from "react";
import { toast } from "sonner";
import { useLocale } from "../../../../locales";
import type { EditorElement } from "../../../../editor/types";
import type {
  ChoiceAppearanceExtended,
  ChoiceOption,
  ImageStyle,
  AudioStyle,
  VideoStyle,
  MathTypeStyle,
  DragSourceAppearance,
  DropTargetAppearance,
  OrderListAppearance,
  OrderListItem,
  MatchingPointAppearance,
  DropAreaConfig,
  ObjectFitOption,
  FillInTheBlanksAppearance,
  PaintAreaAppearance,
  PaintSwatchAppearance,
  PushToTalkAppearance,
  TableAppearance,
  TableCell,
  ElementTemplateKey,
  KeypadLayout,
  // SubmitButtonStyle,
  TextStyle,
  Blank,
  BlankInputAppearance,
  // TimerStyle,
} from "../../types";
import { createElementFromTemplate } from "../../lib/templates";
import type { ToolbarShortcut } from "../form-controls/ShortcutableControl";
import { ShortcutableControl } from "../form-controls/ShortcutableControl";
import {
  resolveChoiceAppearanceExtended,
  resolveImageStyle,
  resolveAudioStyle,
  resolveVideoStyle,
  resolveMathTypeStyle,
  resolveDragSourceAppearance,
  resolveDropTargetAppearance,
  resolveOrderListAppearance,
  resolveMatchingPointAppearance,
  DEFAULT_DROP_AREA,
  resolveFillInTheBlanksAppearance,
  resolvePaintAreaAppearance,
  resolvePaintSwatchAppearance,
  resolvePushToTalkAppearance,
  resolveTableAppearance,
  // resolveSubmitButtonStyle,
  resolveTextStyle,
  // resolveTimerStyle,
} from "../../lib/appearance";
import {
  PAINT_SHAPE_PICKER_OPTIONS,
  DEFAULT_FREE_POLYGON_POINTS,
} from "../../lib/paintShapes";
import { plainTextToHtml } from "../../lib/text";
import { deriveElementLabel } from "../../lib/elements";
import {
  FONT_FAMILY_OPTIONS,
  normalizeFontFamilyValue,
} from "../../lib/fonts";
import { AccordionSection, AccordionContent } from "../AccordionSection";
import { StandardColorPicker } from "../form-controls/StandardColorPicker";
import {
  canExplodeToPolygon,
  isRoundableShape,
  POLYGON_POINTS_FOR_SHAPE,
  type ShapeKind,
} from "../../lib/shapes";
import MathTypeEditorModal from "../MathTypeEditorModal";
import MediaLibraryModal from "../MediaLibraryModal";
import SoundLibraryModal from "../SoundLibraryModal";
import VideoLibraryModal from "../VideoLibraryModal";
import ImagePreviewModal from "../ImagePreviewModal";

const FONT_OPTIONS = FONT_FAMILY_OPTIONS;

interface ElementPropertiesPanelProps {
  element: EditorElement | null;
  elements: EditorElement[];
  selectedElementId: string | null;
  onMove: (elementId: string, position: { x: number; y: number }) => void;
  onResize: (
    elementId: string,
    size: { width: number; height?: number; minWidth?: number; minHeight?: number }
  ) => void;
  onUpdate: (elementId: string, changes: Partial<EditorElement>) => void;
  onDelete: () => void;
  onSelect: (elementId: string) => void;
  onReorder: (
    elementId: string,
    direction: "forward" | "backward" | "front" | "back"
  ) => void;
  onAddShortcut?: (shortcut: Omit<ToolbarShortcut, "id">) => void;
  getShortcutsForElement?: (elementType: string) => ToolbarShortcut[];
  onRemoveShortcut?: (elementType: string, shortcutId: string) => void;
}

// Tahta Düzeni — canvas'taki tüm matching-point'leri satır/sütun olarak
// kümeleyip eşit aralıkla yeniden dizer + kare boyutunu eşitler. İlk
// (en sol/üst) noktaların konumu sabit kalır.
const MatchingBoardLayoutControl: React.FC<{
  elements: EditorElement[];
  onMove: (id: string, position: { x: number; y: number }) => void;
  onResize: (id: string, size: { width: number; height?: number }) => void;
}> = ({ elements, onMove, onResize }) => {
  const mpoints = React.useMemo(
    () => elements.filter((el) => el.type === "matching-point"),
    [elements]
  );

  const [layoutSnap, setLayoutSnap] = React.useState<{
    grid: Map<string, { rowIdx: number; colIdx: number }>;
    currentXGap: number;
    currentYGap: number;
    currentSize: number;
  } | null>(null);

  const elementsKey = React.useMemo(
    () =>
      mpoints
        .map((e) => e.id)
        .sort()
        .join("|"),
    [mpoints]
  );

  React.useEffect(() => {
    if (mpoints.length < 2) {
      setLayoutSnap(null);
      return;
    }
    const ROW_TOL = 30;
    const COL_TOL = 30;
    const sortedByY = [...mpoints].sort((a, b) => a.position.y - b.position.y);
    const rows: EditorElement[][] = [];
    for (const el of sortedByY) {
      const lastRow = rows[rows.length - 1];
      if (lastRow && Math.abs(el.position.y - lastRow[0].position.y) <= ROW_TOL) {
        lastRow.push(el);
      } else {
        rows.push([el]);
      }
    }
    rows.forEach((row) => row.sort((a, b) => a.position.x - b.position.x));
    const sortedByX = [...mpoints].sort((a, b) => a.position.x - b.position.x);
    const cols: EditorElement[][] = [];
    for (const el of sortedByX) {
      const lastCol = cols[cols.length - 1];
      if (lastCol && Math.abs(el.position.x - lastCol[0].position.x) <= COL_TOL) {
        lastCol.push(el);
      } else {
        cols.push([el]);
      }
    }
    const currentXGap =
      cols.length >= 2
        ? Math.round(cols[1][0].position.x - cols[0][0].position.x)
        : 200;
    const currentYGap =
      rows.length >= 2
        ? Math.round(rows[1][0].position.y - rows[0][0].position.y)
        : 100;
    const currentSize = Math.round(mpoints[0].size.width || 80);

    const grid = new Map<string, { rowIdx: number; colIdx: number }>();
    rows.forEach((rowArr, rowIdx) => {
      rowArr.forEach((el) => {
        const colIdx = cols.findIndex((c) => c.some((e) => e.id === el.id));
        if (colIdx >= 0) grid.set(el.id, { rowIdx, colIdx });
      });
    });

    setLayoutSnap({ grid, currentXGap, currentYGap, currentSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementsKey]);

  const [xGapInput, setXGapInput] = React.useState<string>("");
  const [yGapInput, setYGapInput] = React.useState<string>("");
  const [sizeInput, setSizeInput] = React.useState<string>("");

  React.useEffect(() => {
    if (layoutSnap) {
      setXGapInput(String(layoutSnap.currentXGap));
      setYGapInput(String(layoutSnap.currentYGap));
      setSizeInput(String(layoutSnap.currentSize));
    }
  }, [layoutSnap]);

  const apply = (xGap: number, yGap: number, sizePx: number) => {
    if (!layoutSnap) return;
    const { grid } = layoutSnap;
    const baseX = Math.min(...mpoints.map((el) => el.position.x));
    const baseY = Math.min(...mpoints.map((el) => el.position.y));
    mpoints.forEach((el) => {
      const idx = grid.get(el.id);
      if (!idx) return;
      const nextX = baseX + idx.colIdx * xGap;
      const nextY = baseY + idx.rowIdx * yGap;
      if (nextX !== el.position.x || nextY !== el.position.y) {
        onMove(el.id, { x: nextX, y: nextY });
      }
      if (sizePx > 0 && (el.size.width !== sizePx || el.size.height !== sizePx)) {
        onResize(el.id, { width: sizePx, height: sizePx });
      }
    });
  };

  if (!layoutSnap) {
    return (
      <small style={{ color: "#94a3b8", fontSize: 11 }}>
        En az 2 eşleştirme noktası olmalı.
      </small>
    );
  }

  const commit = () => {
    const xGap = Math.max(
      0,
      Math.min(1000, Number(xGapInput) || layoutSnap.currentXGap)
    );
    const yGap = Math.max(
      0,
      Math.min(1000, Number(yGapInput) || layoutSnap.currentYGap)
    );
    const sizePx = Math.max(
      16,
      Math.min(400, Number(sizeInput) || layoutSnap.currentSize)
    );
    apply(xGap, yGap, sizePx);
  };

  const gapInputProps = (
    value: string,
    setValue: (v: string) => void
  ): React.InputHTMLAttributes<HTMLInputElement> => ({
    className: "qe-input",
    type: "number",
    value,
    onChange: (e) => setValue(e.target.value),
    onBlur: commit,
    onKeyDown: (e) => {
      if (e.key === "Enter") {
        commit();
        (e.target as HTMLInputElement).blur();
      }
    },
  });

  return (
    <div
      style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}
    >
      <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
        <span style={{ color: "#94a3b8" }}>Sütun Mesafesi (px)</span>
        <input min={0} max={1000} step={1} {...gapInputProps(xGapInput, setXGapInput)} />
      </label>
      <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
        <span style={{ color: "#94a3b8" }}>Satır Mesafesi (px)</span>
        <input min={0} max={1000} step={1} {...gapInputProps(yGapInput, setYGapInput)} />
      </label>
      <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
        <span style={{ color: "#94a3b8" }}>Kare Boyutu (px)</span>
        <input min={16} max={400} step={1} {...gapInputProps(sizeInput, setSizeInput)} />
      </label>
      <small style={{ gridColumn: "1 / -1", color: "#94a3b8", fontSize: 11 }}>
        Yazdıktan sonra Enter'a veya başka bir alana tıkla. Tüm karelere
        topluca uygulanır.
      </small>
    </div>
  );
};

const ElementPropertiesPanel: React.FC<ElementPropertiesPanelProps> = ({
  element,
  elements,
  selectedElementId,
  onMove,
  onResize,
  onUpdate,
  onDelete,
  onSelect,
  onReorder,
  onAddShortcut,
  getShortcutsForElement,
  onRemoveShortcut,
}) => {
  const { t } = useLocale();
  const [openSection, setOpenSection] = React.useState<string | null>(null);
  const [isLayersAccordionOpen, setIsLayersAccordionOpen] =
    React.useState(true);
  const [draggedElementId, setDraggedElementId] = React.useState<string | null>(
    null
  );
  const [isMathEditorOpen, setIsMathEditorOpen] = React.useState(false);
  const [editingMathOptionId, setEditingMathOptionId] = React.useState<
    string | null
  >(null);
  const [editingMathOrderListItemId, setEditingMathOrderListItemId] =
    React.useState<string | null>(null);
  const [draggedOrderListItemId, setDraggedOrderListItemId] = React.useState<
    string | null
  >(null);
  const [isOrderListExpanded, setIsOrderListExpanded] = React.useState(false);
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = React.useState(false);
  const [mediaLibraryContext, setMediaLibraryContext] = React.useState<{
    type:
      | "image"
      | "video"
      | "choice-option"
      | "drag-source"
      | "drop-target"
      | "order-list-item"
      | "matching-point";
    onSelect: (url: string) => void;
    currentUrl?: string;
  } | null>(null);
  const [isSoundLibraryOpen, setIsSoundLibraryOpen] = React.useState(false);
  const [soundLibraryContext, setSoundLibraryContext] = React.useState<{
    onSelect: (url: string) => void;
    currentUrl?: string;
  } | null>(null);
  const [isVideoLibraryOpen, setIsVideoLibraryOpen] = React.useState(false);
  const [videoLibraryContext, setVideoLibraryContext] = React.useState<{
    onSelect: (url: string) => void;
    currentUrl?: string;
  } | null>(null);
  const [imagePreview, setImagePreview] = React.useState<{
    url: string;
    name?: string;
  } | null>(null);
  const [connectionWidthInput, setConnectionWidthInput] = React.useState<
    string | null
  >(null);

  // Element değiştiğinde local state'i temizle
  React.useEffect(() => {
    setConnectionWidthInput(null);
  }, [selectedElementId]);

  // Helper function to create number input handlers (simple version without local state)
  const createNumberInputHandlers = React.useCallback(
    (
      currentValue: number,
      min: number | null,
      max: number | null,
      onChange: (value: number) => void
    ) => {
      const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = e.target.value.trim();
        if (value === "" || value === "-" || isNaN(Number(value))) {
          // Set to min value if empty or invalid (or currentValue if no min)
          onChange(min !== null ? min : currentValue);
          e.target.value = String(min !== null ? min : currentValue);
        } else {
          // Clamp and update
          let numValue = Number(value);
          let clamped = numValue;
          if (min !== null) {
            clamped = Math.max(min, clamped);
          }
          if (max !== null) {
            clamped = Math.min(max, clamped);
          }
          if (clamped !== numValue) {
            onChange(clamped);
            e.target.value = String(clamped);
          } else {
            // Valid and within bounds → persist value on blur
            onChange(clamped);
          }
        }
      };

      const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // The up/down spinner arrows (and stepper keys) fire an input event
        // with no `inputType`, whereas typing/pasting/deleting always sets one
        // ("insertText", "deleteContentBackward", ...). Commit stepper changes
        // immediately so clicking the arrows applies the value at once, while
        // free typing is still only validated/persisted on blur.
        const inputType = (e.nativeEvent as InputEvent).inputType;
        if (inputType) {
          return;
        }
        const value = e.target.value.trim();
        if (value === "" || value === "-" || isNaN(Number(value))) {
          return;
        }
        let clamped = Number(value);
        if (min !== null) {
          clamped = Math.max(min, clamped);
        }
        if (max !== null) {
          clamped = Math.min(max, clamped);
        }
        onChange(clamped);
      };

      const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          e.currentTarget.blur();
        }
      };

      return {
        // Uncontrolled input so the user can type any value freely.
        // Typing is validated/clamped on blur; spinner-arrow steps commit live.
        defaultValue: String(currentValue),
        key: String(currentValue),
        onChange: handleChange,
        onBlur: handleBlur,
        onKeyDown: handleKeyDown,
      };
    },
    []
  );

  // Sort elements by zIndex (descending - topmost first) - define this early
  const sortedElements = [...elements].sort((a, b) => b.zIndex - a.zIndex);

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, elementId: string) => {
    e.dataTransfer.setData("text/plain", elementId);
    e.dataTransfer.effectAllowed = "move";
    setDraggedElementId(elementId);
  };

  const handleDragEnd = () => {
    setDraggedElementId(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, targetElementId: string) => {
    e.preventDefault();

    const draggedId = e.dataTransfer.getData("text/plain");
    if (!draggedId || draggedId === targetElementId) {
      handleDragEnd();
      return;
    }

    const draggedIndex = sortedElements.findIndex((el) => el.id === draggedId);
    const targetIndex = sortedElements.findIndex(
      (el) => el.id === targetElementId
    );

    if (draggedIndex === -1 || targetIndex === -1) {
      handleDragEnd();
      return;
    }

    // Create new array with dragged element moved to target position
    const newSortedElements = [...sortedElements];
    const [draggedElement] = newSortedElements.splice(draggedIndex, 1);
    newSortedElements.splice(targetIndex, 0, draggedElement);

    // Update all zIndex values to be sequential (starting from 1 at bottom)
    const updates = newSortedElements.map((element, index) => ({
      elementId: element.id,
      changes: { zIndex: newSortedElements.length - index },
    }));

    // Apply all updates
    updates.forEach(({ elementId, changes }) => {
      onUpdate(elementId, changes);
    });

    handleDragEnd();
  };

  const handleLayerReorder = (
    elementId: string,
    direction: "forward" | "backward"
  ) => {
    const currentIndex = sortedElements.findIndex((el) => el.id === elementId);
    if (currentIndex === -1) return;

    let newIndex;
    if (direction === "forward") {
      newIndex = Math.max(0, currentIndex - 1); // Move up in array = higher zIndex
    } else {
      newIndex = Math.min(sortedElements.length - 1, currentIndex + 1); // Move down in array = lower zIndex
    }

    if (newIndex === currentIndex) return; // Already at boundary

    // Create new array with element moved
    const newSortedElements = [...sortedElements];
    const [element] = newSortedElements.splice(currentIndex, 1);
    newSortedElements.splice(newIndex, 0, element);

    // Update all zIndex values to be sequential (starting from 1 at bottom)
    const updates = newSortedElements.map((el, index) => ({
      elementId: el.id,
      changes: { zIndex: newSortedElements.length - index },
    }));

    // Apply all updates
    updates.forEach(({ elementId, changes }) => {
      onUpdate(elementId, changes);
    });
  };

  let noElementContent: React.ReactNode = null;

  if (!element) {
    // If no element is selected but there are elements in the canvas, show only the layers panel
    if (elements.length > 0) {
      noElementContent = (
        <form>
          {/* Katmanlar */}
          <AccordionSection
            title={`${t("props.layers")} (${elements.length})`}
            isOpen={isLayersAccordionOpen}
            onToggle={() => setIsLayersAccordionOpen(!isLayersAccordionOpen)}
          >
            <div className="qe-layer-list">
              {sortedElements.map((element) => {
                const isActive = false; // No element selected, so none are active
                const isDragged = element.id === draggedElementId;
                const label = deriveElementLabel(element);

                return (
                  <div
                    key={element.id}
                    role="button"
                    tabIndex={0}
                    className={`qe-layer-item ${
                      isActive ? "qe-layer-item--active" : ""
                    } ${isDragged ? "qe-layer-item--dragging" : ""}`}
                    draggable={!isDragged}
                    onDragStart={(e) => handleDragStart(e, element.id)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, element.id)}
                    onClick={() => onSelect(element.id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onSelect(element.id);
                      }
                    }}
                  >
                    <div className="qe-layer-item__body">
                      <div className="qe-layer-item__name">{label}</div>
                      <div className="qe-layer-item__meta">
                        {element.type} • Z: {element.zIndex}
                      </div>
                    </div>
                    <div className="qe-layer-item__actions">
                      <button
                        type="button"
                        className="qe-icon-button qe-icon-button--small"
                        title={t("props.layerMoveUp")}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleLayerReorder(element.id, "forward");
                        }}
                      >
                        △
                      </button>
                      <button
                        type="button"
                        className="qe-icon-button qe-icon-button--small"
                        title={t("props.layerMoveDown")}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleLayerReorder(element.id, "backward");
                        }}
                      >
                        ▽
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </AccordionSection>
        </form>
      );
    } else {
      // No elements in canvas at all
      noElementContent = (
        <div>{t("props.noElementSelected")}</div>
      );
    }
  }

  // Return noElementContent if no element is selected
  if (noElementContent) {
    return noElementContent;
  }

  // At this point, element is guaranteed to be non-null
  let nonNullElement = element!; // Non-null assertion
  let elementData = nonNullElement.data as Record<string, unknown>;

  // Check if this is a table with a selected cell element
  let isTableWithSelectedCellElement = false;
  let parentTableId: string | null = null;
  if (nonNullElement.type === "table" && elementData.selectedCellElementId) {
    const cellElements = elementData.cellElements as Record<string, EditorElement> | undefined;
    const selectedCellElementId = elementData.selectedCellElementId as string;

    if (cellElements && cellElements[selectedCellElementId]) {
      // We're showing properties for a cell element, not the table
      isTableWithSelectedCellElement = true;
      parentTableId = nonNullElement.id;
      nonNullElement = cellElements[selectedCellElementId];
      elementData = nonNullElement.data as Record<string, unknown>;
    }
  }
  const isMathTypeElement =
    nonNullElement.type === "mathtype" || nonNullElement.type === "mathlive";
  const textStyle =
    nonNullElement.type === "text" ? resolveTextStyle(nonNullElement) : null;
  const imageStyle =
    nonNullElement.type === "image" ? resolveImageStyle(nonNullElement) : null;
  const audioStyle =
    nonNullElement.type === "audio" ? resolveAudioStyle(nonNullElement) : null;
  const videoStyle =
    nonNullElement.type === "slide" ? resolveVideoStyle(nonNullElement) : null;
  const mathTypeStyle = isMathTypeElement
    ? resolveMathTypeStyle(nonNullElement)
    : null;
  // const timerStyle =
  //   nonNullElement.type === "timer" ? resolveTimerStyle(nonNullElement) : null;
  // const submitButtonStyle =
  //   nonNullElement.type === "submit-button"
  //     ? resolveSubmitButtonStyle(nonNullElement)
  //     : null;
  const choiceAppearance =
    nonNullElement.type === "choice"
      ? resolveChoiceAppearanceExtended(nonNullElement)
      : null;
  const dragSourceAppearance =
    nonNullElement.type === "drag-source"
      ? resolveDragSourceAppearance(nonNullElement)
      : null;
  const dropTargetAppearance =
    nonNullElement.type === "drop-target"
      ? resolveDropTargetAppearance(nonNullElement)
      : null;
  const orderListAppearance =
    nonNullElement.type === "order-list"
      ? resolveOrderListAppearance(nonNullElement)
      : null;
  const matchingPointAppearance =
    nonNullElement.type === "matching-point"
      ? resolveMatchingPointAppearance(nonNullElement)
      : null;
  const matchingPointType =
    nonNullElement.type === "matching-point"
      ? (nonNullElement.data as { pointType?: "source" | "target" })
          ?.pointType || "source"
      : null;
  const fillInTheBlanksAppearance =
    nonNullElement.type === "fill-in-the-blanks"
      ? resolveFillInTheBlanksAppearance(nonNullElement)
      : null;
  const paintAreaAppearance =
    nonNullElement.type === "paint-area"
      ? resolvePaintAreaAppearance(nonNullElement)
      : null;
  const paintSwatchAppearance =
    nonNullElement.type === "paint-swatch"
      ? resolvePaintSwatchAppearance(nonNullElement)
      : null;
  const pushToTalkAppearance =
    nonNullElement.type === "push-to-talk"
      ? resolvePushToTalkAppearance(nonNullElement)
      : null;
  const tableAppearance =
    nonNullElement.type === "table"
      ? resolveTableAppearance(nonNullElement)
      : null;
  const tableCells =
    nonNullElement.type === "table"
      ? (elementData.cells as TableCell[] | undefined) || []
      : [];
  const tableRows =
    nonNullElement.type === "table"
      ? (elementData.rows as number | undefined) || 3
      : 3;
  const tableColumns =
    nonNullElement.type === "table"
      ? (elementData.columns as number | undefined) || 3
      : 3;
  const dropAreaConfig =
    nonNullElement.type === "drop-target"
      ? {
          ...DEFAULT_DROP_AREA,
          ...(elementData.dropArea as DropAreaConfig | undefined),
        }
      : null;
  // Normalize string "0"/"1" to boolean for checkbox
  const dropAreaEnabled = dropAreaConfig?.enabled
    ? (dropAreaConfig.enabled as any) === "1" ||
      (dropAreaConfig.enabled as any) === true ||
      (dropAreaConfig.enabled as any) === 1
    : false;
  const dropTargetPlaceholder =
    nonNullElement.type === "drop-target"
      ? (() => {
          const raw = elementData.placeholder as
            | string
            | { preview?: string | null; canvas?: string | null }
            | undefined;
          // If placeholder is explicitly set as string (including empty string), use it
          if (typeof raw === "string") {
            return raw;
          }
          if (raw && typeof raw === "object") {
            const candidate =
              (typeof raw.preview === "string" && raw.preview) ||
              (typeof raw.canvas === "string" && raw.canvas);
            if (candidate) {
              return candidate;
            }
          }
          // Only use default if placeholder is undefined (never set by user)
          return undefined;
        })()
      : null;
  const elementWidth = nonNullElement.size.width ?? 0;
  const elementHeight = nonNullElement.size.height ?? 0;
  const orderListItemsForMath =
    nonNullElement.type === "order-list"
      ? ((elementData.items as Array<OrderListItem>) || [])
      : [];
  const editingOrderListItem = editingMathOrderListItemId
    ? orderListItemsForMath.find(
        (item) => item.id === editingMathOrderListItemId
      )
    : null;

  const updateElementData = (dataChanges: Record<string, unknown>) => {
    if (isTableWithSelectedCellElement && parentTableId) {
      // We're updating a cell element inside a table
      // Get the table element
      const tableElement = elements.find((el) => el.id === parentTableId);
      if (tableElement) {
        const tableData = tableElement.data as Record<string, unknown>;
        const cellElements = (tableData.cellElements as Record<string, EditorElement>) || {};
        const selectedCellElementId = tableData.selectedCellElementId as string;

        // Update the cell element
        const updatedCellElement = {
          ...cellElements[selectedCellElementId],
          data: {
            ...cellElements[selectedCellElementId].data,
            ...dataChanges,
          },
        };

        // Update the table's cellElements
        onUpdate(parentTableId, {
          data: {
            ...tableData,
            cellElements: {
              ...cellElements,
              [selectedCellElementId]: updatedCellElement,
            },
          },
        });
      }
    } else {
      // Normal element update
      onUpdate(nonNullElement.id, {
        data: {
          ...elementData,
          ...dataChanges,
        },
      });
    }
  };

  // Wrapper functions for size and position updates that handle cell elements
  const updateElementSize = (size: {
    width: number;
    height?: number;
    minWidth?: number;
    minHeight?: number;
  }) => {
    if (isTableWithSelectedCellElement && parentTableId) {
      // For cell elements, update through the table's cellElements
      const tableElement = elements.find((el) => el.id === parentTableId);
      if (tableElement) {
        const tableData = tableElement.data as Record<string, unknown>;
        const cellElements = (tableData.cellElements as Record<string, EditorElement>) || {};
        const selectedCellElementId = tableData.selectedCellElementId as string;

        const updatedCellElement = {
          ...cellElements[selectedCellElementId],
          size: {
            ...cellElements[selectedCellElementId].size,
            ...size,
          },
        };

        onUpdate(parentTableId, {
          data: {
            ...tableData,
            cellElements: {
              ...cellElements,
              [selectedCellElementId]: updatedCellElement,
            },
          },
        });
      }
    } else {
      // Normal element
      onResize(nonNullElement.id, size);
    }
  };

  const updateElementPosition = (position: { x: number; y: number }) => {
    if (isTableWithSelectedCellElement && parentTableId) {
      // For cell elements, update through the table's cellElements
      const tableElement = elements.find((el) => el.id === parentTableId);
      if (tableElement) {
        const tableData = tableElement.data as Record<string, unknown>;
        const cellElements = (tableData.cellElements as Record<string, EditorElement>) || {};
        const selectedCellElementId = tableData.selectedCellElementId as string;

        const updatedCellElement = {
          ...cellElements[selectedCellElementId],
          position,
        };

        onUpdate(parentTableId, {
          data: {
            ...tableData,
            cellElements: {
              ...cellElements,
              [selectedCellElementId]: updatedCellElement,
            },
          },
        });
      }
    } else {
      // Normal element
      onMove(nonNullElement.id, position);
    }
  };

  const updateTextStyle = (styleChanges: Partial<TextStyle>) => {
    if (nonNullElement.type !== "text") {
      return;
    }
    const nextStyle = { ...resolveTextStyle(nonNullElement), ...styleChanges };
    updateElementData({ style: nextStyle });
  };

  // Helper functions for shortcuts
  const getElementShortcuts = (elementType: string): ToolbarShortcut[] => {
    if (!getShortcutsForElement) return [];
    if (elementType === "mathtype" || elementType === "mathlive") {
      return [
        ...getShortcutsForElement("mathtype"),
        ...getShortcutsForElement("mathlive"),
      ];
    }
    return getShortcutsForElement(elementType);
  };

  const isPropertyInShortcuts = (propertyPath: string): boolean => {
    if (!element) return false;
    return getElementShortcuts(element.type).some(
      (s) => s.propertyPath === propertyPath
    );
  };

  const handleRemoveShortcutByPath = (propertyPath: string) => {
    if (!element || !getShortcutsForElement || !onRemoveShortcut) return;
    const elementTypes =
      element.type === "mathtype" || element.type === "mathlive"
        ? ["mathtype", "mathlive"]
        : [element.type];
    for (const elementType of elementTypes) {
      const shortcuts = getShortcutsForElement(elementType);
      const shortcut = shortcuts.find((s) => s.propertyPath === propertyPath);
      if (shortcut) {
        onRemoveShortcut(elementType, shortcut.id);
        toast.success(`"${shortcut.label}" ${t("toast.shortcutRemoved")}`, {
          duration: 2000,
        });
        return;
      }
    }
  };

  const updateImageStyle = (styleChanges: Partial<ImageStyle>) => {
    if (nonNullElement.type !== "image") {
      return;
    }
    const nextStyle = { ...resolveImageStyle(nonNullElement), ...styleChanges };
    updateElementData({ style: nextStyle });
  };

  const updateAudioStyle = (styleChanges: Partial<AudioStyle>) => {
    if (nonNullElement.type !== "audio") {
      return;
    }
    const nextStyle = { ...resolveAudioStyle(nonNullElement), ...styleChanges };
    updateElementData({ style: nextStyle });
  };

  const updateVideoStyle = (styleChanges: Partial<VideoStyle>) => {
    if (nonNullElement.type !== "slide") {
      return;
    }
    const nextStyle = { ...resolveVideoStyle(nonNullElement), ...styleChanges };
    updateElementData({ style: nextStyle });
  };

  const updateMathTypeStyle = (styleChanges: Partial<MathTypeStyle>) => {
    if (!isMathTypeElement) {
      return;
    }
    const nextStyle = {
      ...resolveMathTypeStyle(nonNullElement),
      ...styleChanges,
    };
    updateElementData({ style: nextStyle });
  };

  // const updateTimerStyle = (styleChanges: Partial<TimerStyle>) => {
  //   if (nonNullElement.type !== "timer") {
  //     return;
  //   }
  //   const nextStyle = { ...resolveTimerStyle(nonNullElement), ...styleChanges };
  //   updateElementData({ style: nextStyle });
  // };

  // const updateSubmitButtonStyle = (
  //   styleChanges: Partial<SubmitButtonStyle>
  // ) => {
  //   if (nonNullElement.type !== "submit-button") {
  //     return;
  //   }
  //   const nextStyle = {
  //     ...resolveSubmitButtonStyle(nonNullElement),
  //     ...styleChanges,
  //   };
  //   updateElementData({ style: nextStyle });
  // };

  const updateChoiceAppearance = (
    appearanceChanges: Partial<ChoiceAppearanceExtended>
  ) => {
    if (nonNullElement.type !== "choice") {
      return;
    }
    const nextAppearance = {
      ...resolveChoiceAppearanceExtended(nonNullElement),
      ...appearanceChanges,
    };
    updateElementData({ appearance: nextAppearance });
  };

  const updateDragSourceAppearance = (
    appearanceChanges: Partial<DragSourceAppearance>
  ) => {
    if (nonNullElement.type !== "drag-source") {
      return;
    }
    const nextAppearance = {
      ...resolveDragSourceAppearance(nonNullElement),
      ...appearanceChanges,
    };

    // Also sync showLabel, clonable, showNumbering to data level for proper serialization
    // Convert boolean values to string "0"/"1" format for JSON compatibility
    // Note: We need to convert in the appearance object itself for proper serialization
    const appearanceForJson: any = { ...nextAppearance };

    // Convert boolean values in appearance to string "0"/"1" format
    if (appearanceForJson.showLabel !== undefined) {
      appearanceForJson.showLabel = appearanceForJson.showLabel ? "1" : "0";
    }
    if (appearanceForJson.clonable !== undefined) {
      appearanceForJson.clonable = appearanceForJson.clonable ? "1" : "0";
    }
    if (appearanceForJson.showNumbering !== undefined) {
      appearanceForJson.showNumbering = appearanceForJson.showNumbering
        ? "1"
        : "0";
    }
    if (appearanceForJson.useImage !== undefined) {
      appearanceForJson.useImage = appearanceForJson.useImage ? "1" : "0";
    }

    const dataUpdates: Record<string, any> = { appearance: appearanceForJson };

    // Also sync showLabel and clonable to data level (top level) for backward compatibility
    if ("showLabel" in appearanceChanges) {
      dataUpdates.showLabel = nextAppearance.showLabel ? "1" : "0";
    }

    if ("clonable" in appearanceChanges) {
      dataUpdates.clonable = nextAppearance.clonable ? "1" : "0";
    }

    updateElementData(dataUpdates);
  };

  const updateDropTargetAppearance = (
    appearanceChanges: Partial<DropTargetAppearance>
  ) => {
    if (nonNullElement.type !== "drop-target") {
      return;
    }
    const nextAppearance = {
      ...resolveDropTargetAppearance(nonNullElement),
      ...appearanceChanges,
    };

    // Convert boolean values in appearance to string "0"/"1" format for JSON compatibility
    // Note: We need to convert in the appearance object itself for proper serialization
    const appearanceForJson: any = { ...nextAppearance };
    if (
      "useBackgroundImage" in appearanceChanges ||
      appearanceForJson.useBackgroundImage !== undefined
    ) {
      appearanceForJson.useBackgroundImage =
        appearanceForJson.useBackgroundImage ? "1" : "0";
    }

    // Note: DropTargetAppearance doesn't have showLabel property
    // showLabel is handled at data level for drop-targets
    updateElementData({ appearance: appearanceForJson });
  };

  const updateDropArea = (changes: Partial<DropAreaConfig>) => {
    if (nonNullElement.type !== "drop-target") {
      return;
    }
    const currentConfig = {
      ...DEFAULT_DROP_AREA,
      ...(elementData.dropArea as DropAreaConfig | undefined),
    };
    const nextConfig: any = {
      ...currentConfig,
      ...changes,
    };

    // Convert boolean values to string "0"/"1" format for JSON compatibility
    if ("enabled" in changes) {
      nextConfig.enabled = changes.enabled ? "1" : "0";
    }
    if ("showOutline" in changes) {
      nextConfig.showOutline = changes.showOutline ? "1" : "0";
    }
    if ("highlightEnabled" in changes) {
      nextConfig.highlightEnabled = changes.highlightEnabled ? "1" : "0";
    }

    updateElementData({ dropArea: nextConfig });
  };

  const updateDropTargetPlaceholder = (placeholderText: string) => {
    if (nonNullElement.type !== "drop-target") {
      return;
    }
    updateElementData({ placeholder: placeholderText });
  };

  const updateMatchingPointAppearance = (
    appearanceChanges: Partial<MatchingPointAppearance>
  ) => {
    if (nonNullElement.type !== "matching-point") {
      return;
    }
    const nextAppearance = {
      ...resolveMatchingPointAppearance(nonNullElement),
      ...appearanceChanges,
    };

    // pointSize değiştiğinde element boyutunu da güncelle
    const updates: Partial<EditorElement> = {
      data: {
        ...elementData,
        appearance: nextAppearance,
      },
    };
    if (appearanceChanges.pointSize !== undefined) {
      updates.size = {
        width: appearanceChanges.pointSize,
        height: appearanceChanges.pointSize,
      };
    }

    onUpdate(nonNullElement.id, updates);
  };

  // Bu eşleştirme bloğunun STİL ayarlarını (bağlantı çizgisi, nokta ve içerik
  // stilleri; içerik tipi dahil) sorudaki tüm eşleştirme bloklarına uygular.
  // Her bloğun kendi içerik metni/resmi korunur.
  const applyMatchingStyleToAllBlocks = () => {
    if (nonNullElement.type !== "matching-point") {
      return;
    }
    const source = resolveMatchingPointAppearance(nonNullElement);
    const stylePatch: Partial<MatchingPointAppearance> = { ...source };
    // Per-block content values are preserved (not copied).
    delete stylePatch.contentText;
    delete stylePatch.contentImageUrl;

    elements
      .filter((el) => el.type === "matching-point")
      .forEach((el) => {
        const nextAppearance = {
          ...resolveMatchingPointAppearance(el),
          ...stylePatch,
        };
        const updates: Partial<EditorElement> = {
          data: {
            ...(el.data as Record<string, unknown>),
            appearance: nextAppearance,
          },
        };
        if (stylePatch.pointSize !== undefined) {
          updates.size = {
            width: stylePatch.pointSize,
            height: stylePatch.pointSize,
          };
        }
        onUpdate(el.id, updates);
      });
  };

  const updateFillInTheBlanksAppearance = (
    appearanceChanges: Partial<FillInTheBlanksAppearance>
  ) => {
    if (nonNullElement.type !== "fill-in-the-blanks") {
      return;
    }
    const nextAppearance = {
      ...resolveFillInTheBlanksAppearance(nonNullElement),
      ...appearanceChanges,
    };
    updateElementData({ appearance: nextAppearance });
  };

  const updatePaintAreaAppearance = (
    appearanceChanges: Partial<PaintAreaAppearance>
  ) => {
    if (nonNullElement.type !== "paint-area") {
      return;
    }
    const nextAppearance = {
      ...resolvePaintAreaAppearance(nonNullElement),
      ...appearanceChanges,
    };
    updateElementData({ appearance: nextAppearance });
  };

  const updatePaintSwatchAppearance = (
    appearanceChanges: Partial<PaintSwatchAppearance>
  ) => {
    if (nonNullElement.type !== "paint-swatch") {
      return;
    }
    const nextAppearance = {
      ...resolvePaintSwatchAppearance(nonNullElement),
      ...appearanceChanges,
    };
    updateElementData({ appearance: nextAppearance });
  };

  const updatePushToTalkAppearance = (
    appearanceChanges: Partial<PushToTalkAppearance>
  ) => {
    if (nonNullElement.type !== "push-to-talk") {
      return;
    }
    const nextAppearance = {
      ...resolvePushToTalkAppearance(nonNullElement),
      ...appearanceChanges,
    };
    updateElementData({ appearance: nextAppearance });
  };

  const updateTableAppearance = (
    appearanceChanges: Partial<TableAppearance>
  ) => {
    if (nonNullElement.type !== "table") {
      return;
    }
    const nextAppearance = {
      ...resolveTableAppearance(nonNullElement),
      ...appearanceChanges,
    };
    updateElementData({ appearance: nextAppearance });
  };

  const updateTableDimensions = (rows: number, columns: number) => {
    if (nonNullElement.type !== "table") {
      return;
    }
    const currentCells = tableCells;
    const newCells: TableCell[] = [];

    // Mevcut hücreleri koru, yenilerini ekle
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        const existingCell = currentCells.find(
          (cell) => cell.row === row && cell.col === col
        );
        if (existingCell) {
          newCells.push(existingCell);
        } else {
          newCells.push({
            id: `cell-${row}-${col}`,
            row,
            col,
            elements: [],
          });
        }
      }
    }

    const rowHeights = Array(rows).fill(100);
    const columnWidths = Array(columns).fill(Math.floor(nonNullElement.size.width / columns));

    updateElementData({
      rows,
      columns,
      cells: newCells,
      rowHeights,
      columnWidths,
    });
  };

  const updateTableCell = (cellId: string, changes: Partial<TableCell>) => {
    if (nonNullElement.type !== "table") {
      return;
    }
    const updatedCells = tableCells.map((cell) =>
      cell.id === cellId ? { ...cell, ...changes } : cell
    );
    updateElementData({ cells: updatedCells });
  };

  const selectTableCell = (cellId: string | null) => {
    if (nonNullElement.type !== "table") {
      return;
    }
    updateElementData({ selectedCellId: cellId });
    // Auto-open the selected cell accordion when a cell is selected
    if (cellId) {
      setOpenSection("selected-cell");
    }
  };

  const selectedTableCell =
    nonNullElement.type === "table"
      ? tableCells.find(
          (cell) => cell.id === (elementData.selectedCellId as string | undefined)
        )
      : null;

  // Multi-cell selection (Excel-style). Falls back to the legacy single id.
  const selectedCellIds: string[] = (() => {
    if (nonNullElement.type !== "table") return [];
    const arr = elementData.selectedCellIds;
    if (
      Array.isArray(arr) &&
      arr.every((x): x is string => typeof x === "string")
    ) {
      return arr;
    }
    const single = elementData.selectedCellId;
    return typeof single === "string" ? [single] : [];
  })();

  const selectedTableCells: TableCell[] = (() => {
    if (nonNullElement.type !== "table") return [];
    const idSet = new Set(selectedCellIds);
    return tableCells.filter((c) => idSet.has(c.id));
  })();

  /** Apply the same partial change to every selected cell.
   *  For `cellStyle`, fields are merged on top of each cell's existing style;
   *  passing `cellStyle: null` clears the per-cell style. */
  const updateSelectedCells = (changes: Partial<TableCell>) => {
    if (nonNullElement.type !== "table" || selectedTableCells.length === 0) {
      return;
    }
    const idSet = new Set(selectedTableCells.map((c) => c.id));
    const updatedCells = tableCells.map((cell) => {
      if (!idSet.has(cell.id)) return cell;
      const next: TableCell = { ...cell, ...changes };
      if (changes.cellStyle !== undefined) {
        next.cellStyle =
          changes.cellStyle === null
            ? undefined
            : { ...cell.cellStyle, ...changes.cellStyle };
      }
      return next;
    });
    updateElementData({ cells: updatedCells });
  };

  /** Common cellStyle value across selected cells; undefined if they differ. */
  function commonCellStyle<
    K extends keyof NonNullable<TableCell["cellStyle"]>
  >(key: K): NonNullable<TableCell["cellStyle"]>[K] | undefined {
    if (!selectedTableCells.length) return undefined;
    const first = selectedTableCells[0].cellStyle?.[key];
    for (let i = 1; i < selectedTableCells.length; i++) {
      if (selectedTableCells[i].cellStyle?.[key] !== first) return undefined;
    }
    return first;
  }

  /** Create an element from a template and drop it into the primary selected
   *  cell, sized to fit. Enables the table's locked mode so the nested element
   *  becomes interactive, and selects it so the inspector shows its props. */
  const addElementToCell = (templateKey: ElementTemplateKey) => {
    if (nonNullElement.type !== "table") return;
    const cell = selectedTableCells[0];
    if (!cell) return;
    const created = createElementFromTemplate(templateKey);
    if (!created) return;

    const columns = (elementData.columns as number) || 1;
    const rows = (elementData.rows as number) || 1;
    const appearance = resolveTableAppearance(nonNullElement);
    const cellPadding = appearance.cellPadding || 0;
    const cellWidth = nonNullElement.size.width / columns - cellPadding * 2;
    const cellHeight =
      (nonNullElement.size.height ?? 300) / rows - cellPadding * 2;

    const elementWidth = created.size.width ?? 100;
    const elementHeight = created.size.height ?? 50;
    let newWidth = elementWidth;
    let newHeight = elementHeight;
    if (elementWidth > cellWidth || elementHeight > cellHeight) {
      const scale =
        Math.min(cellWidth / elementWidth, cellHeight / elementHeight, 1) * 0.9;
      newWidth = Math.max(20, Math.floor(elementWidth * scale));
      newHeight = Math.max(20, Math.floor(elementHeight * scale));
    }

    const positioned: EditorElement = {
      ...created,
      size: { ...created.size, width: newWidth, height: newHeight },
      position: {
        x: Math.max(0, (cellWidth - newWidth) / 2),
        y: Math.max(0, (cellHeight - newHeight) / 2),
      },
    };

    const cellElements =
      (elementData.cellElements as Record<string, EditorElement>) || {};
    const newCellElements = { ...cellElements, [positioned.id]: positioned };
    const updatedCells = tableCells.map((c) =>
      c.id === cell.id
        ? { ...c, elements: [...c.elements, positioned.id] }
        : c
    );

    updateElementData({
      cells: updatedCells,
      cellElements: newCellElements,
      locked: true,
      selectedCellElementId: positioned.id,
    });
  };

  const updateChoiceOptions = (options: ChoiceOption[]) => {
    if (nonNullElement.type !== "choice") {
      return;
    }
    updateElementData({ options });
  };

  const addChoiceOption = () => {
    if (nonNullElement.type !== "choice") {
      return;
    }
    const currentOptions = (elementData.options as ChoiceOption[]) || [];
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const letter = letters[currentOptions.length % 26];
    const newOption: ChoiceOption = {
      id: `opt-${Date.now()}`,
      value: `option-${letter.toLowerCase()}`,
      label: `${t("props.optionPrefix")} ${letter}`,
      contentType: "text",
    };
    updateChoiceOptions([...currentOptions, newOption]);
  };

  const removeChoiceOption = (optionId: string) => {
    if (nonNullElement.type !== "choice") {
      return;
    }
    const currentOptions = (elementData.options as ChoiceOption[]) || [];
    updateChoiceOptions(currentOptions.filter((opt) => opt.id !== optionId));
  };

  const updateChoiceOption = (
    optionId: string,
    changes: Partial<ChoiceOption>
  ) => {
    if (nonNullElement.type !== "choice") {
      return;
    }
    const currentOptions = (elementData.options as ChoiceOption[]) || [];

    // If contentType is changing, clear irrelevant values
    const updatedChanges = { ...changes };
    if ("contentType" in changes) {
      const newContentType = changes.contentType as "text" | "image";
      const currentOption = currentOptions.find((opt) => opt.id === optionId);

      if (currentOption) {
        if (newContentType === "text") {
          // Clear image-related fields when switching to text
          updatedChanges.imageUrl = "";
          updatedChanges.imageAlt = "";
          updatedChanges.imageWidth = undefined;
          updatedChanges.imageHeight = undefined;
          updatedChanges.imageBorderRadius = undefined;
          updatedChanges.imageObjectFit = undefined;
        } else if (newContentType === "image") {
          // Clear text-related fields when switching to image
          updatedChanges.label = "";
        }
      }
    }

    updateChoiceOptions(
      currentOptions.map((opt) =>
        opt.id === optionId ? { ...opt, ...updatedChanges } : opt
      )
    );
  };

  const renderColorControl = (
    label: string,
    value: string,
    onChange: (next: string) => void,
    options?: { allowTransparent?: boolean }
  ) => (
    <div className="qe-color-control">
      <span className="qe-color-control__label">{label}</span>
      <div className="qe-color-control__row">
        <StandardColorPicker
          value={value}
          onChange={onChange}
          allowTransparent={options?.allowTransparent ?? true}
          title={label}
        />
      </div>
      {value === "transparent" ? (
        <span className="qe-form__help">{t("props.transparent")}</span>
      ) : null}
    </div>
  );

  const updatePosition =
    (axis: "x" | "y") => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(event.target.value);
      const safeValue = Number.isNaN(value)
        ? nonNullElement.position[axis]
        : value;
      const nextPosition = { ...nonNullElement.position, [axis]: safeValue };
      updateElementPosition(nextPosition);
    };

  const updateZIndex = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    onUpdate(nonNullElement.id, {
      zIndex: Number.isNaN(value) ? nonNullElement.zIndex : value,
    });
  };

  const updateRotation = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    onUpdate(nonNullElement.id, {
      rotation: Number.isNaN(value) ? nonNullElement.rotation : value,
    });
  };

  return (
    <>
      <form className="qe-form">
        {/* Cell Element Indicator */}
        {isTableWithSelectedCellElement && (
          <div
            style={{
              padding: "12px",
              marginBottom: "12px",
              backgroundColor: "#f0f9ff",
              border: "1px solid #bfdbfe",
              borderRadius: "6px",
              fontSize: "13px",
              color: "#1e40af",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: "4px" }}>
              {t("props.cellElementIndicatorTitle")}
            </div>
            <div style={{ fontSize: "12px", opacity: 0.8 }}>
              {t("props.cellElementIndicatorDesc")}
            </div>
          </div>
        )}
        {/* General Section */}
        <AccordionSection
          title={t("props.general")}
          isOpen={openSection === "general"}
          onToggle={() =>
            setOpenSection(openSection === "general" ? null : "general")
          }
        >
          <label className="qe-form__field">
            <span className="qe-form__label">{t("props.elementId")}</span>
            <input
              className="qe-input"
              type="text"
              value={nonNullElement.id}
              readOnly
            />
          </label>

          <label className="qe-form__field">
            <span className="qe-form__label">{t("props.type")}</span>
            <input
              className="qe-input"
              type="text"
              value={nonNullElement.type}
              readOnly
            />
          </label>

          <div className="qe-form__field">
            <span className="qe-form__label">{t("props.positionPx")}</span>
            <div className="qe-inline-group">
              <label className="qe-inline-input">
                <span className="qe-inline-input__label">X</span>
                <input
                  className="qe-input"
                  type="number"
                  value={nonNullElement.position.x}
                  onChange={updatePosition("x")}
                />
              </label>
              <label className="qe-inline-input">
                <span className="qe-inline-input__label">Y</span>
                <input
                  className="qe-input"
                  type="number"
                  value={nonNullElement.position.y}
                  onChange={updatePosition("y")}
                />
              </label>
            </div>
          </div>

          <div className="qe-form__field">
            <span className="qe-form__label">{t("props.borderStyle")}</span>
            <div className="qe-inline-group">
              <label className="qe-inline-input">
                <span className="qe-inline-input__label">Stil</span>
                <select
                  className="qe-input"
                  value={fillInTheBlanksAppearance?.inputBorderStyle || "full"}
                  onChange={(e) => {
                    updateFillInTheBlanksAppearance({
                      inputBorderStyle: e.target.value as any,
                    });
                  }}
                >
                  <option value="full">{t("props.borderStyleFull")}</option>
                  <option value="bottom">{t("props.borderStyleBottom")}</option>
                </select>
              </label>
            </div>
          </div>

          <label className="qe-form__field">
            <span className="qe-form__label">{t("props.zIndex")}</span>
            <input
              className="qe-input"
              type="number"
              value={nonNullElement.zIndex}
              onChange={updateZIndex}
            />
          </label>

          <label className="qe-form__field">
            <span className="qe-form__label">{t("props.rotation")}</span>
            <input
              className="qe-input"
              type="number"
              value={nonNullElement.rotation ?? 0}
              onChange={updateRotation}
            />
          </label>
        </AccordionSection>

        <AccordionSection
          title={t("props.appearanceTiming")}
          isOpen={openSection === "appear-timing"}
          onToggle={() =>
            setOpenSection(
              openSection === "appear-timing" ? null : "appear-timing"
            )
          }
        >
          <label className="qe-form__field">
            <span className="qe-form__label">
              {t("props.appearOrder")}
            </span>
            <input
              className="qe-input"
              type="number"
              placeholder={t("props.appearOrderPlaceholder")}
              value={
                typeof nonNullElement.appearOrder === "number"
                  ? nonNullElement.appearOrder
                  : ""
              }
              onChange={(e) => {
                const raw = e.target.value.trim();
                onUpdate(nonNullElement.id, {
                  appearOrder: raw === "" ? undefined : Number(raw),
                });
              }}
            />
          </label>

          <label className="qe-form__field">
            <span className="qe-form__label">
              {t("props.appearDelaySeconds")}
            </span>
            <input
              className="qe-input"
              type="number"
              min={0}
              step={0.5}
              placeholder="0"
              value={
                typeof nonNullElement.appearDelayMs === "number"
                  ? nonNullElement.appearDelayMs / 1000
                  : ""
              }
              onChange={(e) => {
                const raw = e.target.value.trim();
                if (raw === "") {
                  onUpdate(nonNullElement.id, { appearDelayMs: undefined });
                  return;
                }
                const seconds = Math.max(0, Number(raw));
                if (!Number.isFinite(seconds)) return;
                const snapped = Math.round(seconds * 2) / 2;
                onUpdate(nonNullElement.id, {
                  appearDelayMs: Math.round(snapped * 1000),
                });
              }}
            />
          </label>

          <label className="qe-form__field">
            <span className="qe-form__label">
              {t("props.minDisplaySeconds")}
            </span>
            <input
              className="qe-input"
              type="number"
              min={0}
              step={0.5}
              placeholder="4"
              value={
                typeof nonNullElement.minDisplayMs === "number"
                  ? nonNullElement.minDisplayMs / 1000
                  : ""
              }
              onChange={(e) => {
                const raw = e.target.value.trim();
                if (raw === "") {
                  onUpdate(nonNullElement.id, { minDisplayMs: undefined });
                  return;
                }
                const seconds = Math.max(0, Number(raw));
                if (!Number.isFinite(seconds)) return;
                const snapped = Math.round(seconds * 2) / 2;
                onUpdate(nonNullElement.id, {
                  minDisplayMs: Math.round(snapped * 1000),
                });
              }}
            />
          </label>
        </AccordionSection>

        {/* Text Settings Section (was Text Style) */}
        {nonNullElement.type === "text" && textStyle ? (
          <div
            className={`qe-properties-section ${
              openSection === "text-settings"
                ? "qe-properties-section--active"
                : ""
            }`}
          >
            <div
              className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                openSection === "text-settings"
                  ? "qe-properties-section__title--open"
                  : "qe-properties-section__title--closed"
              }`}
              onClick={() =>
                setOpenSection(
                  openSection === "text-settings" ? null : "text-settings"
                )
              }
            >
              <span>{t("props.textSettings")}</span>
              <svg
                className={`qe-properties-section__icon ${
                  openSection === "text-settings"
                    ? "qe-properties-section__icon--open"
                    : ""
                }`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <AccordionContent isOpen={openSection === "text-settings"}>
              <div
                className="qe-properties-section__content"
                style={{ padding: 0 }}
              >
                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.textContent")}</span>
                  <textarea
                    className="qe-input"
                    rows={4}
                    value={(elementData.content as string) || ""}
                    onChange={(event) => {
                      const nextContent = event.target.value;
                      updateElementData({
                        content: nextContent,
                        htmlContent: plainTextToHtml(nextContent),
                      });
                    }}
                  />
                </div>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "text",
                    propertyPath: "data.style.fontFamily",
                    label: t("props.fontFamily"),
                    controlType: "select",
                    options: FONT_OPTIONS,
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts("data.style.fontFamily")}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.fontFamily")
                  }
                >
                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.fontFamily")}</span>
                    <select
                      className="qe-input"
                      value={normalizeFontFamilyValue(textStyle.fontFamily)}
                      onChange={(event) =>
                        updateTextStyle({ fontFamily: event.target.value })
                      }
                    >
                      {FONT_OPTIONS.map((font) => (
                        <option key={font.value} value={font.value}>
                          {font.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "text",
                    propertyPath: "data.style.fontSize",
                    label: t("props.fontSize"),
                    controlType: "number",
                    step: 1,
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts("data.style.fontSize")}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.fontSize")
                  }
                >
                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.fontSize")}</span>
                    <input
                      className="qe-input"
                      type="number"
                      {...createNumberInputHandlers(
                        textStyle.fontSize,
                        null,
                        null,
                        (value) => updateTextStyle({ fontSize: value })
                      )}
                    />
                  </div>
                </ShortcutableControl>

                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.style")}</span>
                  <div className="qe-style-toggle-group">
                    <button
                      type="button"
                      className={`qe-style-toggle ${
                        textStyle.fontWeight >= 600
                          ? "qe-style-toggle--active"
                          : ""
                      }`}
                      onClick={() =>
                        updateTextStyle({
                          fontWeight: textStyle.fontWeight >= 600 ? 400 : 700,
                        })
                      }
                    >
                      {t("props.bold")}
                    </button>
                    <button
                      type="button"
                      className={`qe-style-toggle ${
                        textStyle.italic ? "qe-style-toggle--active" : ""
                      }`}
                      onClick={() =>
                        updateTextStyle({ italic: !textStyle.italic })
                      }
                    >
                      {t("props.italic")}
                    </button>
                    <button
                      type="button"
                      className={`qe-style-toggle ${
                        textStyle.underline ? "qe-style-toggle--active" : ""
                      }`}
                      onClick={() =>
                        updateTextStyle({ underline: !textStyle.underline })
                      }
                    >
                      {t("props.underline")}
                    </button>
                  </div>
                </div>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "text",
                    propertyPath: "data.style.color",
                    label: t("props.textColor"),
                    controlType: "color",
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts("data.style.color")}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.color")
                  }
                >
                  {renderColorControl(
                    t("props.color"),
                    textStyle.color,
                    (nextColor) => updateTextStyle({ color: nextColor }),
                    { allowTransparent: false }
                  )}
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "text",
                    propertyPath: "data.style.backgroundColor",
                    label: t("props.backgroundColor"),
                    controlType: "color",
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.style.backgroundColor"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.backgroundColor")
                  }
                >
                  {renderColorControl(
                    t("props.background"),
                    textStyle.backgroundColor,
                    (nextColor) =>
                      updateTextStyle({ backgroundColor: nextColor })
                  )}
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "text",
                    propertyPath: "data.style.textAlign",
                    label: t("props.textAlign"),
                    controlType: "segmented",
                    options: [
                      { value: "left", label: t("props.left") },
                      { value: "center", label: t("props.center") },
                      { value: "right", label: t("props.right") },
                      { value: "justify", label: t("props.justify") },
                    ],
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts("data.style.textAlign")}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.textAlign")
                  }
                >
                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.alignment")}</span>
                    <div className="qe-style-toggle-group">
                      {(["left", "center", "right", "justify"] as const).map(
                        (align) => (
                          <button
                            key={align}
                            type="button"
                            className={`qe-style-toggle ${
                              textStyle.textAlign === align
                                ? "qe-style-toggle--active"
                                : ""
                            }`}
                            onClick={() =>
                              updateTextStyle({ textAlign: align })
                            }
                          >
                            {align === "left"
                              ? t("props.left")
                              : align === "center"
                              ? t("props.center")
                              : align === "right"
                              ? t("props.right")
                              : t("props.justify")}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "text",
                    propertyPath: "data.style.lineHeight",
                    label: t("props.lineHeight"),
                    controlType: "number",
                    min: 0.5,
                    max: 3,
                    step: 0.1,
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts("data.style.lineHeight")}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.lineHeight")
                  }
                >
                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.lineHeight")}</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0.5}
                      max={3}
                      step={0.1}
                      value={textStyle.lineHeight}
                      onChange={(event) =>
                        updateTextStyle({
                          lineHeight: Math.max(
                            0.5,
                            Number(event.target.value) || 1
                          ),
                        })
                      }
                    />
                  </div>
                </ShortcutableControl>

                <div className="qe-form__field">
                  <div className="qe-inline-group">
                    <span className="qe-form__label">{t("props.padding")}</span>
                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "text",
                        propertyPath: "data.style.paddingX",
                        label: t("props.paddingX"),
                        controlType: "number",
                        min: 0,
                        max: 100,
                        step: 1,
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.style.paddingX"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath("data.style.paddingX")
                      }
                    >
                      <label className="qe-inline-input">
                        <span className="qe-inline-input__label">X</span>
                        <input
                          className="qe-input"
                          type="number"
                          min={0}
                          value={textStyle.paddingX}
                          onChange={(event) =>
                            updateTextStyle({
                              paddingX: Math.max(
                                0,
                                Number(event.target.value) || 0
                              ),
                            })
                          }
                        />
                      </label>
                    </ShortcutableControl>
                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "text",
                        propertyPath: "data.style.paddingY",
                        label: t("props.paddingY"),
                        controlType: "number",
                        min: 0,
                        max: 100,
                        step: 1,
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.style.paddingY"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath("data.style.paddingY")
                      }
                    >
                      <label className="qe-inline-input">
                        <span className="qe-inline-input__label">Y</span>
                        <input
                          className="qe-input"
                          type="number"
                          min={0}
                          value={textStyle.paddingY}
                          onChange={(event) =>
                            updateTextStyle({
                              paddingY: Math.max(
                                0,
                                Number(event.target.value) || 0
                              ),
                            })
                          }
                        />
                      </label>
                    </ShortcutableControl>
                  </div>
                </div>

                <div className="qe-form__field">
                  <div className="qe-inline-group">
                    <span className="qe-form__label">{t("props.border")}</span>
                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "text",
                        propertyPath: "data.style.borderWidth",
                        label: t("props.borderWidth"),
                        controlType: "number",
                        min: 0,
                        max: 20,
                        step: 1,
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.style.borderWidth"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath("data.style.borderWidth")
                      }
                    >
                      <label className="qe-inline-input">
                        <span className="qe-inline-input__label">{t("props.thickness")}</span>
                        <input
                          className="qe-input"
                          type="number"
                          min={0}
                          value={textStyle.borderWidth}
                          onChange={(event) =>
                            updateTextStyle({
                              borderWidth: Math.max(
                                0,
                                Number(event.target.value) || 0
                              ),
                            })
                          }
                        />
                      </label>
                    </ShortcutableControl>
                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "text",
                        propertyPath: "data.style.borderColor",
                        label: t("props.borderColor"),
                        controlType: "color",
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.style.borderColor"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath("data.style.borderColor")
                      }
                    >
                      <label className="qe-inline-input">
                        <span className="qe-inline-input__label">Renk</span>
                        <input
                          className="qe-input qe-input--color"
                          type="color"
                          value={textStyle.borderColor}
                          onChange={(event) =>
                            updateTextStyle({ borderColor: event.target.value })
                          }
                        />
                      </label>
                    </ShortcutableControl>
                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "text",
                        propertyPath: "data.style.borderRadius",
                        label: t("props.borderRadius"),
                        controlType: "number",
                        min: 0,
                        max: 100,
                        step: 1,
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.style.borderRadius"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath("data.style.borderRadius")
                      }
                    >
                      <label className="qe-inline-input">
                        <span className="qe-inline-input__label">{t("props.corner")}</span>
                        <input
                          className="qe-input"
                          type="number"
                          min={0}
                          value={textStyle.borderRadius}
                          onChange={(event) =>
                            updateTextStyle({
                              borderRadius: Math.max(
                                0,
                                Number(event.target.value) || 0
                              ),
                            })
                          }
                        />
                      </label>
                    </ShortcutableControl>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </div>
        ) : null}

        {/* Fill In The Blanks Settings Section */}
        {nonNullElement.type === "fill-in-the-blanks" &&
        fillInTheBlanksAppearance ? (
          <div
            className={`qe-properties-section ${
              openSection === "fill-in-the-blanks-settings"
                ? "qe-properties-section--active"
                : ""
            }`}
          >
            <div
              className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                openSection === "fill-in-the-blanks-settings"
                  ? "qe-properties-section__title--open"
                  : "qe-properties-section__title--closed"
              }`}
              onClick={() =>
                setOpenSection(
                  openSection === "fill-in-the-blanks-settings"
                    ? null
                    : "fill-in-the-blanks-settings"
                )
              }
            >
              <span>{t("props.fillInTheBlanksSettings")}</span>
              <svg
                className={`qe-properties-section__icon ${
                  openSection === "fill-in-the-blanks-settings"
                    ? "qe-properties-section__icon--open"
                    : ""
                }`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <AccordionContent
              isOpen={openSection === "fill-in-the-blanks-settings"}
            >
              <div
                className="qe-properties-section__content"
                style={{ padding: 0 }}
              >
                {/* 1. İçerik ve Metin - Rebuild Trigger */}

                <div className="qe-form__field">
                  <span className="qe-form__label">
                    {t("props.fillInTheBlanksContent")}
                  </span>
                  <textarea
                    className="qe-input"
                    rows={6}
                    value={(elementData.content as string) || ""}
                    onChange={(event) => {
                      const newContent = event.target.value;
                      // Parse blanks from content
                      const matches = newContent.match(/\[(.*?)\]/g) || [];
                      const currentBlanks =
                        (elementData.blanks as Blank[]) || [];

                      const newBlanks: Blank[] = matches.map((match, index) => {
                        const value = match.slice(1, -1); // Remove brackets
                        // Match by position/index to preserve ID and appearance even if value changes
                        const existing = currentBlanks[index];
                        return {
                          id:
                            existing?.id ||
                            `blank-${Date.now()}-${Math.random()
                              .toString(36)
                              .substr(2, 9)}`,
                          value,
                          appearance: existing?.appearance, // Preserve appearance by position
                        };
                      });

                      updateElementData({
                        content: newContent,
                        blanks: newBlanks,
                      });
                    }}
                    placeholder={t("props.fillInTheBlanksPlaceholder")}
                  />
                </div>

                <div className="qe-form__field">
                  <label className="qe-checkbox-label">
                    <input
                      type="checkbox"
                      checked={fillInTheBlanksAppearance.hideAnswers ?? false}
                      onChange={() =>
                        updateFillInTheBlanksAppearance({
                          hideAnswers: !(
                            fillInTheBlanksAppearance.hideAnswers ?? false
                          ),
                        })
                      }
                    />
                    <span>{t("props.hideCorrectAnswers")}</span>
                  </label>
                  <div className="qe-form__field">
                    <div className="qe-form__help-text">
                      {t("props.hideCorrectAnswersHelp")}
                    </div>
                  </div>
                </div>

                {/* Blank Choices Editor */}
                {(elementData.blanks as Blank[])?.length > 0 && (
                  <div className="qe-form__field">
                    <span className="qe-form__label">
                      {t("props.blankChoicesDropdown")}
                    </span>
                    <div className="qe-form__help-text" style={{ marginBottom: "12px" }}>
                      {t("props.blankChoicesHelp")}
                    </div>
                    {(elementData.blanks as Blank[]).map((blank, index) => {
                      const blankChoices = blank.choices || [];
                      return (
                        <div
                          key={blank.id}
                          style={{
                            marginBottom: "16px",
                            padding: "12px",
                            border: "1px solid var(--qe-border-subtle, #E5E7EB)",
                            borderRadius: "8px",
                            backgroundColor: "var(--qe-bg-surface, #F9FAFB)",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <span style={{ fontWeight: 500, fontSize: "14px" }}>
                              {t("props.blankLabel").replace("{n}", String(index + 1)).replace("{value}", blank.value)}
                            </span>
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                fontSize: "13px",
                                cursor: "pointer",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={blankChoices.length > 0}
                                onChange={(e) => {
                                  const newBlanks = [...(elementData.blanks as Blank[])];
                                  if (e.target.checked && blankChoices.length === 0) {
                                    // Enable dropdown mode - add correct answer as first choice
                                    newBlanks[index] = {
                                      ...blank,
                                      choices: [blank.value],
                                    };
                                  } else if (!e.target.checked) {
                                    // Disable dropdown mode - remove choices
                                    newBlanks[index] = {
                                      ...blank,
                                      choices: undefined,
                                    };
                                  } else {
                                    // Just keep current state
                                    newBlanks[index] = blank;
                                  }
                                  updateElementData({ blanks: newBlanks });
                                }}
                              />
                              <span>{t("props.dropdownActive")}</span>
                            </label>
                          </div>
                          {blankChoices.length > 0 && (
                            <div>
                              <div style={{ marginBottom: "8px" }}>
                                <input
                                  type="text"
                                  className="qe-input"
                                  placeholder={t("props.addNewChoice")}
                                  style={{ marginBottom: "8px" }}
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                      const newChoice = e.currentTarget.value.trim();
                                      if (newChoice) {
                                        const newBlanks = [...(elementData.blanks as Blank[])];
                                        newBlanks[index] = {
                                          ...blank,
                                          choices: [...blankChoices, newChoice],
                                        };
                                        updateElementData({ blanks: newBlanks });
                                        e.currentTarget.value = "";
                                      }
                                    }
                                  }}
                                />
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "8px",
                                  }}
                                >
                                  {blankChoices.map((choice, choiceIndex) => (
                                    <span
                                      key={choiceIndex}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        padding: "4px 10px",
                                        backgroundColor: choice === blank.value
                                          ? "#d1fae5"
                                          : "#e5e7eb",
                                        borderRadius: "16px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {choice}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newBlanks = [...(elementData.blanks as Blank[])];
                                          const newChoices = blankChoices.filter(
                                            (_, i) => i !== choiceIndex
                                          );
                                          newBlanks[index] = {
                                            ...blank,
                                            choices: newChoices.length > 0
                                              ? newChoices
                                              : undefined,
                                          };
                                          updateElementData({ blanks: newBlanks });
                                        }}
                                        style={{
                                          background: "none",
                                          border: "none",
                                          color: "#6b7280",
                                          cursor: "pointer",
                                          padding: "0",
                                          fontSize: "16px",
                                          lineHeight: "1",
                                        }}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="qe-form__field">
                  <div className="qe-inline-group">
                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "fill-in-the-blanks",
                        propertyPath: "data.appearance.fontFamily",
                        label: t("props.fontFamily"),
                        controlType: "select",
                        options: FONT_OPTIONS,
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.appearance.fontFamily"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath("data.appearance.fontFamily")
                      }
                    >
                      <label className="qe-inline-input" style={{ flex: 2 }}>
                        <span className="qe-inline-input__label">
                          {t("props.fontFamily")}
                        </span>
                        <select
                          className="qe-input"
                          value={normalizeFontFamilyValue(
                            fillInTheBlanksAppearance.fontFamily || ""
                          )}
                          onChange={(e) =>
                            updateFillInTheBlanksAppearance({
                              fontFamily: e.target.value,
                            })
                          }
                        >
                          <option value="">{t("props.default")}</option>
                          {FONT_OPTIONS.map((font) => (
                            <option key={font.value} value={font.value}>
                              {font.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </ShortcutableControl>

                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "fill-in-the-blanks",
                        propertyPath: "data.appearance.fontSize",
                        label: t("props.fontSize"),
                        controlType: "number",
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.appearance.fontSize"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath("data.appearance.fontSize")
                      }
                    >
                      <label className="qe-inline-input" style={{ flex: 1 }}>
                        <span className="qe-inline-input__label">
                          Boyut
                        </span>
                        <input
                          className="qe-input"
                          type="number"
                          value={fillInTheBlanksAppearance.fontSize}
                          onChange={(e) =>
                            updateFillInTheBlanksAppearance({
                              fontSize: Number(e.target.value),
                            })
                          }
                        />
                      </label>
                    </ShortcutableControl>
                  </div>
                </div>

                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.textColor")}</span>
                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "fill-in-the-blanks",
                      propertyPath: "data.appearance.color",
                      label: t("props.textColor"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.color"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.color")
                    }
                  >
                    {renderColorControl(
                      t("props.color"),
                      fillInTheBlanksAppearance.color,
                      (nextColor) =>
                        updateFillInTheBlanksAppearance({ color: nextColor }),
                      { allowTransparent: false }
                    )}
                  </ShortcutableControl>
                </div>

                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.style")}</span>
                  <div className="qe-style-toggle-group">
                    <button
                      type="button"
                      className={`qe-style-toggle ${
                        fillInTheBlanksAppearance.fontWeight >= 600
                          ? "qe-style-toggle--active"
                          : ""
                      }`}
                      onClick={() =>
                        updateFillInTheBlanksAppearance({
                          fontWeight:
                            fillInTheBlanksAppearance.fontWeight >= 600
                              ? 400
                              : 700,
                        })
                      }
                    >
                      {t("props.bold")}
                    </button>
                    <button
                      type="button"
                      className={`qe-style-toggle ${
                        fillInTheBlanksAppearance.italic
                          ? "qe-style-toggle--active"
                          : ""
                      }`}
                      onClick={() =>
                        updateFillInTheBlanksAppearance({
                          italic: !fillInTheBlanksAppearance.italic,
                        })
                      }
                    >
                      {t("props.italic")}
                    </button>
                    <button
                      type="button"
                      className={`qe-style-toggle ${
                        fillInTheBlanksAppearance.underline
                          ? "qe-style-toggle--active"
                          : ""
                      }`}
                      onClick={() =>
                        updateFillInTheBlanksAppearance({
                          underline: !fillInTheBlanksAppearance.underline,
                        })
                      }
                    >
                      {t("props.underline")}
                    </button>
                  </div>
                </div>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "fill-in-the-blanks",
                    propertyPath: "data.appearance.textAlign",
                    label: t("props.alignment"),
                    controlType: "segmented",
                    options: [
                      { value: "left", label: t("props.left") },
                      { value: "center", label: t("props.center") },
                      { value: "right", label: t("props.right") },
                      { value: "justify", label: t("props.justify") },
                    ],
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.appearance.textAlign"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.appearance.textAlign")
                  }
                >
                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.alignment")}</span>
                    <div className="qe-style-toggle-group">
                      {(["left", "center", "right", "justify"] as const).map(
                        (align) => (
                          <button
                            key={align}
                            type="button"
                            className={`qe-style-toggle ${
                              fillInTheBlanksAppearance.textAlign === align
                                ? "qe-style-toggle--active"
                                : ""
                            }`}
                            onClick={() =>
                              updateFillInTheBlanksAppearance({
                                textAlign: align,
                              })
                            }
                          >
                            {align === "left"
                              ? t("props.left")
                              : align === "center"
                              ? t("props.center")
                              : align === "right"
                              ? t("props.right")
                              : t("props.justify")}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "fill-in-the-blanks",
                    propertyPath: "data.appearance.lineHeight",
                    label: t("props.lineHeight"),
                    controlType: "number",
                    min: 0.5,
                    max: 3,
                    step: 0.1,
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.appearance.lineHeight"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.appearance.lineHeight")
                  }
                >
                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.lineHeight")}</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0.5}
                      max={3}
                      step={0.1}
                      value={fillInTheBlanksAppearance.lineHeight}
                      onChange={(event) =>
                        updateFillInTheBlanksAppearance({
                          lineHeight: Math.max(
                            0.5,
                            Number(event.target.value) || 1
                          ),
                        })
                      }
                    />
                  </div>
                </ShortcutableControl>







                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.backgroundColor")}</span>
                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "fill-in-the-blanks",
                      propertyPath: "data.appearance.backgroundColor",
                      label: t("props.backgroundColor"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.backgroundColor"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.backgroundColor"
                      )
                    }
                  >
                    {renderColorControl(
                      t("props.background"),
                      fillInTheBlanksAppearance.backgroundColor,
                      (nextColor) =>
                        updateFillInTheBlanksAppearance({
                          backgroundColor: nextColor,
                        }),
                      { allowTransparent: true }
                    )}
                  </ShortcutableControl>
                </div>

                <div className="qe-form__field">
                  <div className="qe-inline-group">
                    <span className="qe-form__label">{t("props.padding")}</span>
                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "fill-in-the-blanks",
                        propertyPath: "data.appearance.paddingX",
                        label: t("props.horizontalSpacing"),
                        controlType: "number",
                        min: 0,
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.appearance.paddingX"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath("data.appearance.paddingX")
                      }
                    >
                      <label className="qe-inline-input">
                        <span className="qe-inline-input__label">X</span>
                        <input
                          className="qe-input"
                          type="number"
                          min={0}
                          value={fillInTheBlanksAppearance.paddingX}
                          onChange={(event) =>
                            updateFillInTheBlanksAppearance({
                              paddingX: Math.max(
                                0,
                                Number(event.target.value) || 0
                              ),
                            })
                          }
                        />
                      </label>
                    </ShortcutableControl>
                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "fill-in-the-blanks",
                        propertyPath: "data.appearance.paddingY",
                        label: t("props.verticalSpacing"),
                        controlType: "number",
                        min: 0,
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.appearance.paddingY"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath("data.appearance.paddingY")
                      }
                    >
                      <label className="qe-inline-input">
                        <span className="qe-inline-input__label">Y</span>
                        <input
                          className="qe-input"
                          type="number"
                          min={0}
                          value={fillInTheBlanksAppearance.paddingY}
                          onChange={(event) =>
                            updateFillInTheBlanksAppearance({
                              paddingY: Math.max(
                                0,
                                Number(event.target.value) || 0
                              ),
                            })
                          }
                        />
                      </label>
                    </ShortcutableControl>
                  </div>
                </div>

                <div className="qe-form__field">
                  <div className="qe-inline-group">
                    <span className="qe-form__label">{t("props.border")}</span>
                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "fill-in-the-blanks",
                        propertyPath: "data.appearance.borderWidth",
                        label: t("props.borderWidth"),
                        controlType: "number",
                        min: 0,
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.appearance.borderWidth"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath(
                          "data.appearance.borderWidth"
                        )
                      }
                    >
                      <label className="qe-inline-input">
                        <span className="qe-inline-input__label">{t("props.thickness")}</span>
                        <input
                          className="qe-input"
                          type="number"
                          min={0}
                          value={fillInTheBlanksAppearance.borderWidth}
                          onChange={(event) =>
                            updateFillInTheBlanksAppearance({
                              borderWidth: Math.max(
                                0,
                                Number(event.target.value) || 0
                              ),
                            })
                          }
                        />
                      </label>
                    </ShortcutableControl>
                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "fill-in-the-blanks",
                        propertyPath: "data.appearance.borderColor",
                        label: t("props.borderColor"),
                        controlType: "color",
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.appearance.borderColor"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath(
                          "data.appearance.borderColor"
                        )
                      }
                    >
                      <label className="qe-inline-input">
                        <span className="qe-inline-input__label">Renk</span>
                        <input
                          className="qe-input qe-input--color"
                          type="color"
                          value={fillInTheBlanksAppearance.borderColor}
                          onChange={(event) =>
                            updateFillInTheBlanksAppearance({
                              borderColor: event.target.value,
                            })
                          }
                        />
                      </label>
                    </ShortcutableControl>
                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "fill-in-the-blanks",
                        propertyPath: "data.appearance.borderRadius",
                        label: t("props.borderRadius"),
                        controlType: "number",
                        min: 0,
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.appearance.borderRadius"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath(
                          "data.appearance.borderRadius"
                        )
                      }
                    >
                      <label className="qe-inline-input">
                        <span className="qe-inline-input__label">{t("props.corner")}</span>
                        <input
                          className="qe-input"
                          type="number"
                          min={0}
                          value={fillInTheBlanksAppearance.borderRadius}
                          onChange={(event) =>
                            updateFillInTheBlanksAppearance({
                              borderRadius: Math.max(
                                0,
                                Number(event.target.value) || 0
                              ),
                            })
                          }
                        />
                      </label>
                    </ShortcutableControl>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </div>
        ) : null}

        {/* Individual Blank Styling Sections */}
        {nonNullElement.type === "fill-in-the-blanks" &&
        fillInTheBlanksAppearance &&
        (elementData.blanks as Blank[])?.length > 0 ? (
          <>
            {(elementData.blanks as Blank[]).map((blank, blankIndex) => {
              const blankAppearance = blank.appearance || {};
              const updateBlankAppearance = (
                changes: Partial<BlankInputAppearance>
              ) => {
                const currentBlanks = (elementData.blanks as Blank[]) || [];
                const updatedBlanks = currentBlanks.map((b) =>
                  b.id === blank.id
                    ? { ...b, appearance: { ...blankAppearance, ...changes } }
                    : b
                );
                updateElementData({ blanks: updatedBlanks });
              };

              const applyAppearanceToAllBlanks = () => {
                const currentBlanks = (elementData.blanks as Blank[]) || [];
                const sourceAppearance = blank.appearance || {};
                const updatedBlanks = currentBlanks.map((b) => ({
                  ...b,
                  appearance:
                    b.id === blank.id
                      ? b.appearance || sourceAppearance
                      : { ...sourceAppearance },
                }));
                updateElementData({ blanks: updatedBlanks });
              };

              return (
                <div
                  key={blank.id}
                  className={`qe-properties-section ${
                    openSection === `blank-${blank.id}`
                      ? "qe-properties-section--active"
                      : ""
                  }`}
                >
                  <div
                    className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                      openSection === `blank-${blank.id}`
                        ? "qe-properties-section__title--open"
                        : "qe-properties-section__title--closed"
                    }`}
                    onClick={() =>
                      setOpenSection(
                        openSection === `blank-${blank.id}`
                          ? null
                          : `blank-${blank.id}`
                      )
                    }
                  >
                    <span>
                      {`${t("props.fillInTheBlanksSettings")} ${blankIndex + 1} (${blank.value || t("props.empty")})`}
                    </span>
                    <svg
                      className={`qe-properties-section__icon ${
                        openSection === `blank-${blank.id}`
                          ? "qe-properties-section__icon--open"
                          : ""
                      }`}
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                    >
                      <path
                        d="M3 4.5L6 7.5L9 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <AccordionContent
                    isOpen={openSection === `blank-${blank.id}`}
                  >
                    <div
                      className="qe-properties-section__content"
                      style={{ padding: 0 }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-start",
                          padding: "4px 0",
                        }}
                      >
                        <button
                          type="button"
                          className="qe-button qe-button--ghost"
                          style={{ fontSize: "12px", padding: "6px 10px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            applyAppearanceToAllBlanks();
                          }}
                        >
                          {t("props.applyToAllBlanks")}
                        </button>
                      </div>
                      <div className="qe-form__field">
                        <div className="qe-inline-group">
                          <span className="qe-form__label">{t("props.borderStyle")}</span>
                          <label className="qe-inline-input">
                            <span className="qe-inline-input__label">Stil</span>
                            <select
                              className="qe-input"
                              value={blankAppearance.inputBorderStyle ?? "full"}
                              onChange={(e) => {
                                updateBlankAppearance({
                                  inputBorderStyle: e.target.value as any,
                                });
                              }}
                            >
                              <option value="full">Tam Kenar</option>
                              <option value="bottom">Alt Kenar</option>
                            </select>
                          </label>
                        </div>
                      </div>

                      <div className="qe-form__field">
                        <span className="qe-form__label">Metin Hizalama</span>
                        <div className="qe-style-toggle-group">
                          {(["left", "center", "right"] as const).map(
                            (align) => (
                              <button
                                key={align}
                                type="button"
                                className={`qe-style-toggle ${
                                  blankAppearance.inputTextAlign === align
                                    ? "qe-style-toggle--active"
                                    : ""
                                }`}
                                onClick={() =>
                                  updateBlankAppearance({
                                    inputTextAlign: align,
                                  })
                                }
                              >
                                {align === "left"
                                  ? "Sol"
                                  : align === "center"
                                  ? "Orta"
                                  : t("props.right")}
                              </button>
                            )
                          )}
                        </div>
                      </div>

                      <div className="qe-form__field">
                        <span className="qe-form__label">{t("props.boxBackground")}</span>
                        {renderColorControl(
                          t("props.background"),
                          blankAppearance.inputBackgroundColor ??
                            fillInTheBlanksAppearance.inputBackgroundColor ??
                            "#ffffff",
                          (nextColor) =>
                            updateBlankAppearance({
                              inputBackgroundColor: nextColor,
                            }),
                          { allowTransparent: true }
                        )}
                      </div>

                      <div className="qe-form__field">
                        <span className="qe-form__label">{t("props.boxBorder")}</span>
                        {renderColorControl(
                          t("props.border"),
                          blankAppearance.inputBorderColor ??
                            fillInTheBlanksAppearance.inputBorderColor ??
                            "#cbd5e1",
                          (nextColor) =>
                            updateBlankAppearance({
                              inputBorderColor: nextColor,
                            }),
                          { allowTransparent: true }
                        )}
                      </div>

                      <div className="qe-form__field">
                        <span className="qe-form__label">Kutu Metin Rengi</span>
                        {renderColorControl(
                          t("props.text"),
                          blankAppearance.inputTextColor ??
                            fillInTheBlanksAppearance.inputTextColor ??
                            "#1f2430",
                          (nextColor) =>
                            updateBlankAppearance({
                              inputTextColor: nextColor,
                            }),
                          { allowTransparent: false }
                        )}
                      </div>

                      <div className="qe-form__field">
                        <span className="qe-form__label">
                          {t("props.blankDefaultWidth")}
                        </span>
                        <input
                          className="qe-input"
                          type="number"
                          min={0}
                          step={1}
                          value={
                            blankAppearance.inputDefaultWidth ??
                            fillInTheBlanksAppearance.inputDefaultWidth ??
                            8
                          }
                          onChange={(event) => {
                            // Allow free typing while focused
                            const value = Number(event.target.value);
                            updateBlankAppearance({
                              inputDefaultWidth: isNaN(value)
                                ? undefined
                                : value,
                            });
                          }}
                          onBlur={(event) => {
                            const value = Number(event.target.value);
                            // Enforce minimum 5 on blur
                            if (isNaN(value) || value < 5) {
                              updateBlankAppearance({
                                inputDefaultWidth: 5,
                              });
                            } else {
                              updateBlankAppearance({
                                inputDefaultWidth: value,
                              });
                            }
                          }}
                        />
                      </div>

                      <div className="qe-form__field">
                        <div className="qe-inline-group">
                          <span className="qe-form__label">{t("props.padding")}</span>
                          <label className="qe-inline-input">
                            <span className="qe-inline-input__label">X</span>
                            <input
                              className="qe-input"
                              type="number"
                              min={0}
                              value={
                                blankAppearance.inputPaddingX ??
                                fillInTheBlanksAppearance.inputPaddingX ??
                                8
                              }
                              onChange={(event) =>
                                updateBlankAppearance({
                                  inputPaddingX: Math.max(
                                    0,
                                    Number(event.target.value) || 0
                                  ),
                                })
                              }
                            />
                          </label>
                          <label className="qe-inline-input">
                            <span className="qe-inline-input__label">Y</span>
                            <input
                              className="qe-input"
                              type="number"
                              min={0}
                              value={
                                blankAppearance.inputPaddingY ??
                                fillInTheBlanksAppearance.inputPaddingY ??
                                4
                              }
                              onChange={(event) =>
                                updateBlankAppearance({
                                  inputPaddingY: Math.max(
                                    0,
                                    Number(event.target.value) || 0
                                  ),
                                })
                              }
                            />
                          </label>
                        </div>
                      </div>

                      <div className="qe-form__field">
                        <span className="qe-form__label">
                          {t("props.blankFontFamily")}
                        </span>
                        <select
                          className="qe-input"
                          value={normalizeFontFamilyValue(
                            blankAppearance.inputFontFamily ??
                              fillInTheBlanksAppearance.inputFontFamily ??
                              fillInTheBlanksAppearance.fontFamily
                          )}
                          onChange={(e) =>
                            updateBlankAppearance({
                              inputFontFamily: e.target.value,
                            })
                          }
                        >
                          {FONT_OPTIONS.map((font) => (
                            <option key={font.value} value={font.value}>
                              {font.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="qe-form__field">
                        <span className="qe-form__label">
                          {t("props.blankFontSize")}
                        </span>
                        <input
                          className="qe-input"
                          type="number"
                          min={0}
                          value={
                            blankAppearance.inputFontSize ??
                            fillInTheBlanksAppearance.inputFontSize ??
                            fillInTheBlanksAppearance.fontSize
                          }
                          onChange={(event) => {
                            // Allow free typing while focused
                            const value = Number(event.target.value);
                            updateBlankAppearance({
                              inputFontSize: isNaN(value) ? undefined : value,
                            });
                          }}
                          onBlur={(event) => {
                            const value = Number(event.target.value);
                            // Enforce bounds on blur
                            const normalized = isNaN(value)
                              ? fillInTheBlanksAppearance.fontSize
                              : Math.max(8, Math.min(72, value));
                            updateBlankAppearance({
                              inputFontSize: normalized,
                            });
                          }}
                        />
                      </div>

                      <div className="qe-form__field">
                        <span className="qe-form__label">
                          {t("props.blankFontStyles")}
                        </span>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px",
                          }}
                        >
                          <label
                            className="qe-checkbox-label"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={
                                (blankAppearance.inputFontWeight ??
                                  fillInTheBlanksAppearance.inputFontWeight ??
                                  fillInTheBlanksAppearance.fontWeight) >= 600
                              }
                              onChange={() =>
                                updateBlankAppearance({
                                  inputFontWeight:
                                    (blankAppearance.inputFontWeight ??
                                      fillInTheBlanksAppearance.inputFontWeight ??
                                      fillInTheBlanksAppearance.fontWeight) >=
                                    600
                                      ? 400
                                      : 700,
                                })
                              }
                            />
                            <span>{t("props.bold")}</span>
                          </label>
                          <label
                            className="qe-checkbox-label"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={
                                blankAppearance.inputItalic ??
                                fillInTheBlanksAppearance.inputItalic ??
                                false
                              }
                              onChange={() =>
                                updateBlankAppearance({
                                  inputItalic: !(
                                    blankAppearance.inputItalic ??
                                    fillInTheBlanksAppearance.inputItalic ??
                                    false
                                  ),
                                })
                              }
                            />
                            <span>{t("props.italic")}</span>
                          </label>
                          <label
                            className="qe-checkbox-label"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={
                                blankAppearance.inputUnderline ??
                                fillInTheBlanksAppearance.inputUnderline ??
                                false
                              }
                              onChange={() =>
                                updateBlankAppearance({
                                  inputUnderline: !(
                                    blankAppearance.inputUnderline ??
                                    fillInTheBlanksAppearance.inputUnderline ??
                                    false
                                  ),
                                })
                              }
                            />
                            <span>{t("props.underline")}</span>
                          </label>
                        </div>
                      </div>

                      <div className="qe-form__field">
                        <span className="qe-form__label">
                          {t("props.blankLetterSpacing")}
                        </span>
                        <input
                          className="qe-input"
                          type="number"
                          min={-2}
                          max={10}
                          step={0.1}
                          value={
                            blankAppearance.inputLetterSpacing ??
                            fillInTheBlanksAppearance.inputLetterSpacing ??
                            0
                          }
                          onChange={(event) =>
                            updateBlankAppearance({
                              inputLetterSpacing: Math.max(
                                -2,
                                Math.min(10, Number(event.target.value) || 0)
                              ),
                            })
                          }
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </div>
              );
            })}
          </>
        ) : null}

        {/* Image Settings Section */}
        {nonNullElement.type === "image" && imageStyle ? (
          <div
            className={`qe-properties-section ${
              openSection === "image-settings"
                ? "qe-properties-section--active"
                : ""
            }`}
          >
            <div
              className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                openSection === "image-settings"
                  ? "qe-properties-section__title--open"
                  : "qe-properties-section__title--closed"
              }`}
              onClick={() =>
                setOpenSection(
                  openSection === "image-settings" ? null : "image-settings"
                )
              }
            >
              <span>{t("props.imageSettings")}</span>
              <svg
                className={`qe-properties-section__icon ${
                  openSection === "image-settings"
                    ? "qe-properties-section__icon--open"
                    : ""
                }`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <AccordionContent isOpen={openSection === "image-settings"}>
              <div
                className="qe-properties-section__content"
                style={{ padding: 0 }}
              >
                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.image")}</span>
                  <button
                    type="button"
                    className={`qe-media-library-button ${
                      imageStyle.url ? "qe-media-library-button--has-value" : ""
                    }`}
                    onClick={() => {
                      setMediaLibraryContext({
                        type: "image",
                        onSelect: (url) => updateImageStyle({ url }),
                        currentUrl: imageStyle.url,
                      });
                      setIsMediaLibraryOpen(true);
                    }}
                    title={
                      imageStyle.url
                        ? t("props.imageClickToChange")
                        : t("props.imageSelectFromLibrary")
                    }
                  >
                    <svg
                      className="qe-media-library-button__icon"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M14 2H2C1.44772 2 1 2.44772 1 3V13C1 13.5523 1.44772 14 2 14H14C14.5523 14 15 13.5523 15 13V3C15 2.44772 14.5523 2 14 2Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5.5 7C6.32843 7 7 6.32843 7 5.5C7 4.67157 6.32843 4 5.5 4C4.67157 4 4 4.67157 4 5.5C4 6.32843 4.67157 7 5.5 7Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M15 10L11 6L2 15"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {imageStyle.url
                      ? t("props.imageSelected")
                      : t("props.selectFromMediaLibrary")}
                  </button>
                  {imageStyle.url && (
                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "12px",
                        color: "var(--qe-text-muted)",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setImagePreview({
                            url: imageStyle.url,
                            name: imageStyle.alt || "Resim",
                          })
                        }
                        style={{
                          color: "var(--qe-accent)",
                          textDecoration: "underline",
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          fontSize: "12px",
                          fontFamily: "inherit",
                        }}
                      >
                        {t("props.viewCurrentImage")}
                      </button>
                    </div>
                  )}
                </div>

                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.altText")}</span>
                  <input
                    className="qe-input"
                    type="text"
                    value={imageStyle.alt}
                    onChange={(event) =>
                      updateImageStyle({ alt: event.target.value })
                    }
                  />
                </div>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "image",
                    propertyPath: "data.style.objectFit",
                    label: t("props.objectFit"),
                    controlType: "select",
                    options: [
                      { value: "contain", label: t("props.objectFitContain") },
                      { value: "cover", label: t("props.objectFitCover") },
                      { value: "fill", label: t("props.objectFitFill") },
                      { value: "none", label: t("props.objectFitNone") },
                      { value: "scale-down", label: t("props.objectFitScaleDown") },
                    ],
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts("data.style.objectFit")}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.objectFit")
                  }
                >
                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.objectFit")}</span>
                    <select
                      className="qe-input"
                      value={imageStyle.objectFit}
                      onChange={(event) =>
                        updateImageStyle({
                          objectFit: event.target
                            .value as ImageStyle["objectFit"],
                        })
                      }
                    >
                      <option value="contain">{t("props.objectFitContain")}</option>
                      <option value="cover">{t("props.objectFitCover")}</option>
                      <option value="fill">{t("props.objectFitFill")}</option>
                      <option value="none">{t("props.objectFitNone")}</option>
                      <option value="scale-down">{t("props.objectFitScaleDown")}</option>
                    </select>
                  </div>
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "image",
                    propertyPath: "data.style.objectPosition",
                    label: t("props.imagePosition"),
                    controlType: "select",
                    options: [
                      { value: "center center", label: t("props.posCenter") },
                      { value: "top center", label: t("props.posTop") },
                      { value: "bottom center", label: t("props.posBottom") },
                      { value: "center left", label: t("props.left") },
                      { value: "center right", label: t("props.right") },
                      { value: "top left", label: t("props.posTopLeft") },
                      { value: "top right", label: t("props.posTopRight") },
                      { value: "bottom left", label: t("props.posBottomLeft") },
                      { value: "bottom right", label: t("props.posBottomRight") },
                    ],
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.style.objectPosition"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.objectPosition")
                  }
                >
                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.imagePosition")}</span>
                    <select
                      className="qe-input"
                      value={imageStyle.objectPosition}
                      onChange={(event) =>
                        updateImageStyle({ objectPosition: event.target.value })
                      }
                    >
                      <option value="center center">{t("props.posCenter")}</option>
                      <option value="top center">{t("props.posTop")}</option>
                      <option value="bottom center">{t("props.posBottom")}</option>
                      <option value="center left">{t("props.left")}</option>
                      <option value="center right">{t("props.right")}</option>
                      <option value="top left">{t("props.posTopLeft")}</option>
                      <option value="top right">{t("props.posTopRight")}</option>
                      <option value="bottom left">{t("props.posBottomLeft")}</option>
                      <option value="bottom right">{t("props.posBottomRight")}</option>
                    </select>
                  </div>
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "image",
                    propertyPath: "data.style.opacity",
                    label: t("props.opacity"),
                    controlType: "number",
                    min: 0,
                    max: 1,
                    step: 0.1,
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts("data.style.opacity")}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.opacity")
                  }
                >
                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.opacity")}</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      max={1}
                      step={0.1}
                      {...createNumberInputHandlers(
                        imageStyle.opacity,
                        0,
                        1,
                        (value) => updateImageStyle({ opacity: value })
                      )}
                      key={`image-opacity-${imageStyle.opacity}`}
                    />
                  </div>
                </ShortcutableControl>

                <div className="qe-form__field">
                  <div className="qe-inline-group">
                    <span className="qe-form__label">{t("props.border")}</span>
                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "image",
                        propertyPath: "data.style.borderWidth",
                        label: t("props.borderWidth"),
                        controlType: "number",
                        min: 0,
                        max: 20,
                        step: 1,
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.style.borderWidth"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath("data.style.borderWidth")
                      }
                    >
                      <label className="qe-inline-input">
                        <span className="qe-inline-input__label">{t("props.thickness")}</span>
                        <input
                          className="qe-input"
                          type="number"
                          min={0}
                          value={imageStyle.borderWidth}
                          onChange={(event) =>
                            updateImageStyle({
                              borderWidth: Math.max(
                                0,
                                Number(event.target.value) || 0
                              ),
                            })
                          }
                        />
                      </label>
                    </ShortcutableControl>
                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "image",
                        propertyPath: "data.style.borderColor",
                        label: t("props.borderColor"),
                        controlType: "color",
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.style.borderColor"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath("data.style.borderColor")
                      }
                    >
                      <label className="qe-inline-input">
                        <span className="qe-inline-input__label">Renk</span>
                        <input
                          className="qe-input qe-input--color"
                          type="color"
                          value={imageStyle.borderColor}
                          onChange={(event) =>
                            updateImageStyle({
                              borderColor: event.target.value,
                            })
                          }
                        />
                      </label>
                    </ShortcutableControl>
                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "image",
                        propertyPath: "data.style.borderRadius",
                        label: t("props.borderRadius"),
                        controlType: "number",
                        min: 0,
                        max: 100,
                        step: 1,
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.style.borderRadius"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath("data.style.borderRadius")
                      }
                    >
                      <label className="qe-inline-input">
                        <span className="qe-inline-input__label">{t("props.corner")}</span>
                        <input
                          className="qe-input"
                          type="number"
                          min={0}
                          value={imageStyle.borderRadius}
                          onChange={(event) =>
                            updateImageStyle({
                              borderRadius: Math.max(
                                0,
                                Number(event.target.value) || 0
                              ),
                            })
                          }
                        />
                      </label>
                    </ShortcutableControl>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </div>
        ) : null}

        {/* Timer Settings Section */}
        {/* {nonNullElement.type === "timer" && timerStyle ? (
        <div
          className={`qe-properties-section ${
            openSection === "timer-settings"
              ? "qe-properties-section--active"
              : ""
          }`}
        >
          <div
            className={`qe-properties-section__title qe-properties-section__title--collapsible ${
              openSection === "timer-settings"
                ? "qe-properties-section__title--open"
                : "qe-properties-section__title--closed"
            }`}
            onClick={() =>
              setOpenSection(
                openSection === "timer-settings" ? null : "timer-settings"
              )
            }
          >
            <span>{t("props.timerSettings")}</span>
            <svg
              className={`qe-properties-section__icon ${
                openSection === "timer-settings"
                  ? "qe-properties-section__icon--open"
                  : ""
              }`}
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <AccordionContent isOpen={openSection === "timer-settings"}>
            <div
              className="qe-properties-section__content"
              style={{ padding: 0 }}
            >
              <div className="qe-form__field">
                <span className="qe-form__label">{t("props.durationSeconds")}</span>
                <input
                  className="qe-input"
                  type="number"
                  min={1}
                  {...createNumberInputHandlers(
                    timerStyle.duration,
                    1,
                    null,
                    (value) => updateTimerStyle({ duration: value })
                  )}
                />
              </div>

              <div className="qe-form__field">
                <span className="qe-form__label">{t("props.displayFormat")}</span>
                <select
                  className="qe-input"
                  value={timerStyle.format}
                  onChange={(event) =>
                    updateTimerStyle({
                      format: event.target.value as TimerStyle["format"],
                    })
                  }
                >
                  <option value="mm:ss">Dakika:Saniye (mm:ss)</option>
                  <option value="ss">Saniye (ss)</option>
                </select>
              </div>

              <div className="qe-form__field">
                <label className="qe-toggle">
                  <input
                    type="checkbox"
                    checked={timerStyle.allowContinueAfterExpiry}
                    onChange={(event) =>
                      updateTimerStyle({
                        allowContinueAfterExpiry: event.target.checked,
                      })
                    }
                  />
                  <span>{t("props.continueAfterExpiry")}</span>
                </label>
              </div>

              <div className="qe-form__field">
                <label className="qe-toggle">
                  <input
                    type="checkbox"
                    checked={timerStyle.autoSubmitOnExpiry}
                    onChange={(event) =>
                      updateTimerStyle({
                        autoSubmitOnExpiry: event.target.checked,
                      })
                    }
                  />
                  <span>{t("props.autoSubmitOnExpiry")}</span>
                </label>
              </div>

              <div className="qe-form__field">
                <span className="qe-form__label">Font Ailesi</span>
                <select
                  className="qe-input"
                  value={normalizeFontFamilyValue(timerStyle.fontFamily)}
                  onChange={(event) =>
                    updateTimerStyle({ fontFamily: event.target.value })
                  }
                >
                  {FONT_OPTIONS.map((font) => (
                    <option key={font.value} value={font.value}>
                      {font.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="qe-form__field">
                <span className="qe-form__label">{t("props.fontSize")}</span>
                <input
                  className="qe-input"
                  type="number"
                  {...createNumberInputHandlers(
                    timerStyle.fontSize,
                    null,
                    null,
                    (value) => updateTimerStyle({ fontSize: value })
                  )}
                />
              </div>

              <div className="qe-form__field">
                <span className="qe-form__label">{t("props.thickness")}</span>
                <select
                  className="qe-input"
                  value={timerStyle.fontWeight}
                  onChange={(event) =>
                    updateTimerStyle({ fontWeight: Number(event.target.value) })
                  }
                >
                  <option value="400">Normal</option>
                  <option value="600">{t("props.fontWeightMedium")}</option>
                  <option value="700">{t("props.fontWeightBold")}</option>
                  <option value="800">{t("props.fontWeightExtraBold")}</option>
                </select>
              </div>

              <ShortcutableControl
                shortcutConfig={{
                  elementType: 'timer',
                  propertyPath: 'data.style.color',
                  label: 'Renk',
                  controlType: 'color',
                }}
                onAddShortcut={onAddShortcut}
                isInShortcuts={isPropertyInShortcuts('data.style.color')}
                onRemoveShortcut={() => handleRemoveShortcutByPath('data.style.color')}
              >
                {renderColorControl(
                  t("props.color"),
                  timerStyle.color,
                  (nextColor) => updateTimerStyle({ color: nextColor }),
                  { allowTransparent: false }
                )}
              </ShortcutableControl>

              <ShortcutableControl
                shortcutConfig={{
                  elementType: 'timer',
                  propertyPath: 'data.style.backgroundColor',
                  label: 'Arkaplan Rengi',
                  controlType: 'color',
                }}
                onAddShortcut={onAddShortcut}
                isInShortcuts={isPropertyInShortcuts('data.style.backgroundColor')}
                onRemoveShortcut={() => handleRemoveShortcutByPath('data.style.backgroundColor')}
              >
                {renderColorControl(
                  t("props.background"),
                  timerStyle.backgroundColor,
                  (nextColor) => updateTimerStyle({ backgroundColor: nextColor })
                )}
              </ShortcutableControl>

              <div className="qe-form__field">
                <span className="qe-form__label">{t("props.alignment")}</span>
                <div className="qe-style-toggle-group">
                  {(["left", "center", "right"] as const).map((align) => (
                    <button
                      key={align}
                      type="button"
                      className={`qe-style-toggle ${
                        timerStyle.textAlign === align
                          ? "qe-style-toggle--active"
                          : ""
                      }`}
                      onClick={() => updateTimerStyle({ textAlign: align })}
                    >
                      {align === "left"
                        ? "Sol"
                        : align === "center"
                        ? "Orta"
                        : t("props.right")}
                    </button>
                  ))}
                </div>
              </div>

              <div className="qe-form__field">
                <span className="qe-form__label">{t("props.padding")}</span>
                <div className="qe-inline-group">
                  <label className="qe-inline-input">
                    <span className="qe-inline-input__label">X</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      value={timerStyle.paddingX}
                      onChange={(event) =>
                        updateTimerStyle({
                          paddingX: Math.max(
                            0,
                            Number(event.target.value) || 0
                          ),
                        })
                      }
                    />
                  </label>
                  <label className="qe-inline-input">
                    <span className="qe-inline-input__label">Y</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      value={timerStyle.paddingY}
                      onChange={(event) =>
                        updateTimerStyle({
                          paddingY: Math.max(
                            0,
                            Number(event.target.value) || 0
                          ),
                        })
                      }
                    />
                  </label>
                </div>
              </div>

              <div className="qe-form__field">
                <span className="qe-form__label">{t("props.border")}</span>
                <div className="qe-inline-group">
                  <label className="qe-inline-input">
                    <span className="qe-inline-input__label">{t("props.thickness")}</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      value={timerStyle.borderWidth}
                      onChange={(event) =>
                        updateTimerStyle({
                          borderWidth: Math.max(
                            0,
                            Number(event.target.value) || 0
                          ),
                        })
                      }
                    />
                  </label>
                  <label className="qe-inline-input">
                    <span className="qe-inline-input__label">Renk</span>
                    <input
                      className="qe-input qe-input--color"
                      type="color"
                      value={timerStyle.borderColor}
                      onChange={(event) =>
                        updateTimerStyle({ borderColor: event.target.value })
                      }
                    />
                  </label>
                  <label className="qe-inline-input">
                    <span className="qe-inline-input__label">{t("props.corner")}</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      value={timerStyle.borderRadius}
                      onChange={(event) =>
                        updateTimerStyle({
                          borderRadius: Math.max(
                            0,
                            Number(event.target.value) || 0
                          ),
                        })
                      }
                    />
                  </label>
                </div>
              </div>
            </div>
          </AccordionContent>
        </div>
      ) : null} */}

        {/* Submit Button Settings Section */}
        {/* {nonNullElement.type === "submit-button" && submitButtonStyle ? (
        <div
          className={`qe-properties-section ${
            openSection === "submit-button-settings"
              ? "qe-properties-section--active"
              : ""
          }`}
        >
          <div
            className={`qe-properties-section__title qe-properties-section__title--collapsible ${
              openSection === "submit-button-settings"
                ? "qe-properties-section__title--open"
                : "qe-properties-section__title--closed"
            }`}
            onClick={() =>
              setOpenSection(
                openSection === "submit-button-settings"
                  ? null
                  : "submit-button-settings"
              )
            }
          >
            <span>{t("props.buttonSettings")}</span>
            <svg
              className={`qe-properties-section__icon ${
                openSection === "submit-button-settings"
                  ? "qe-properties-section__icon--open"
                  : ""
              }`}
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <AccordionContent isOpen={openSection === "submit-button-settings"}>
            <div
              className="qe-properties-section__content"
              style={{ padding: 0 }}
            >
              <div className="qe-form__field">
                <span className="qe-form__label">Buton Metni</span>
                <input
                  className="qe-input"
                  type="text"
                  value={submitButtonStyle.text}
                  onChange={(event) =>
                    updateSubmitButtonStyle({ text: event.target.value })
                  }
                />
              </div>

              <div className="qe-form__field">
                <span className="qe-form__label">Font Ailesi</span>
                <select
                  className="qe-input"
                  value={normalizeFontFamilyValue(submitButtonStyle.fontFamily)}
                  onChange={(event) =>
                    updateSubmitButtonStyle({ fontFamily: event.target.value })
                  }
                >
                  {FONT_OPTIONS.map((font) => (
                    <option key={font.value} value={font.value}>
                      {font.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="qe-form__field">
                <span className="qe-form__label">{t("props.fontSize")}</span>
                <input
                  className="qe-input"
                  type="number"
                  {...createNumberInputHandlers(
                    submitButtonStyle.fontSize,
                    null,
                    null,
                    (value) => updateSubmitButtonStyle({ fontSize: value })
                  )}
                />
              </div>

              <div className="qe-form__field">
                <span className="qe-form__label">{t("props.thickness")}</span>
                <select
                  className="qe-input"
                  value={submitButtonStyle.fontWeight}
                  onChange={(event) =>
                    updateSubmitButtonStyle({
                      fontWeight: Number(event.target.value),
                    })
                  }
                >
                  <option value="400">Normal</option>
                  <option value="600">{t("props.fontWeightMedium")}</option>
                  <option value="700">{t("props.fontWeightBold")}</option>
                  <option value="800">{t("props.fontWeightExtraBold")}</option>
                </select>
              </div>

              <ShortcutableControl
                shortcutConfig={{
                  elementType: 'submit-button',
                  propertyPath: 'data.style.textColor',
                  label: t("props.textColor"),
                  controlType: 'color',
                }}
                onAddShortcut={onAddShortcut}
                isInShortcuts={isPropertyInShortcuts('data.style.textColor')}
                onRemoveShortcut={() => handleRemoveShortcutByPath('data.style.textColor')}
              >
                {renderColorControl(
                  t("props.textColor"),
                  submitButtonStyle.textColor,
                  (nextColor) =>
                    updateSubmitButtonStyle({ textColor: nextColor }),
                  { allowTransparent: false }
                )}
              </ShortcutableControl>

              <ShortcutableControl
                shortcutConfig={{
                  elementType: 'submit-button',
                  propertyPath: 'data.style.backgroundColor',
                  label: 'Arkaplan Rengi',
                  controlType: 'color',
                }}
                onAddShortcut={onAddShortcut}
                isInShortcuts={isPropertyInShortcuts('data.style.backgroundColor')}
                onRemoveShortcut={() => handleRemoveShortcutByPath('data.style.backgroundColor')}
              >
                {renderColorControl(
                  t("props.background"),
                  submitButtonStyle.backgroundColor,
                  (nextColor) =>
                    updateSubmitButtonStyle({ backgroundColor: nextColor }),
                  { allowTransparent: false }
                )}
              </ShortcutableControl>

              <div className="qe-form__field">
                <span className="qe-form__label">{t("props.padding")}</span>
                <div className="qe-inline-group">
                  <label className="qe-inline-input">
                    <span className="qe-inline-input__label">X</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      value={submitButtonStyle.paddingX}
                      onChange={(event) =>
                        updateSubmitButtonStyle({
                          paddingX: Math.max(
                            0,
                            Number(event.target.value) || 0
                          ),
                        })
                      }
                    />
                  </label>
                  <label className="qe-inline-input">
                    <span className="qe-inline-input__label">Y</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      value={submitButtonStyle.paddingY}
                      onChange={(event) =>
                        updateSubmitButtonStyle({
                          paddingY: Math.max(
                            0,
                            Number(event.target.value) || 0
                          ),
                        })
                      }
                    />
                  </label>
                </div>
              </div>

              <div className="qe-form__field">
                <span className="qe-form__label">{t("props.border")}</span>
                <div className="qe-inline-group">
                  <label className="qe-inline-input">
                    <span className="qe-inline-input__label">{t("props.thickness")}</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      value={submitButtonStyle.borderWidth}
                      onChange={(event) =>
                        updateSubmitButtonStyle({
                          borderWidth: Math.max(
                            0,
                            Number(event.target.value) || 0
                          ),
                        })
                      }
                    />
                  </label>
                  <label className="qe-inline-input">
                    <span className="qe-inline-input__label">Renk</span>
                    <input
                      className="qe-input qe-input--color"
                      type="color"
                      value={submitButtonStyle.borderColor}
                      onChange={(event) =>
                        updateSubmitButtonStyle({
                          borderColor: event.target.value,
                        })
                      }
                    />
                  </label>
                  <label className="qe-inline-input">
                    <span className="qe-inline-input__label">{t("props.corner")}</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      value={submitButtonStyle.borderRadius}
                      onChange={(event) =>
                        updateSubmitButtonStyle({
                          borderRadius: Math.max(
                            0,
                            Number(event.target.value) || 0
                          ),
                        })
                      }
                    />
                  </label>
                </div>
              </div>
            </div>
          </AccordionContent>
        </div>
      ) : null} */}

        {/* Choice Element */}
        {nonNullElement.type === "choice" && choiceAppearance ? (
          <>
            {/* Seçenekler */}
            <div
              className={`qe-properties-section qe-properties-section--choice-options ${
                openSection === "choice-options"
                  ? "qe-properties-section--active"
                  : ""
              }`}
            >
              <div
                className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                  openSection === "choice-options"
                    ? "qe-properties-section__title--open"
                    : "qe-properties-section__title--closed"
                }`}
                onClick={() =>
                  setOpenSection(
                    openSection === "choice-options" ? null : "choice-options"
                  )
                }
              >
                <span>{t("props.options")}</span>
                <svg
                  className={`qe-properties-section__icon ${
                    openSection === "choice-options"
                      ? "qe-properties-section__icon--open"
                      : ""
                  }`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <AccordionContent
                isOpen={openSection === "choice-options"}
                isChoiceOptions={true}
              >
                <div
                  className="qe-properties-section__content"
                  style={{ padding: 0 }}
                >
                  <div className="qe-form__field">
                    <label className="qe-checkbox-label">
                      <input
                        type="checkbox"
                        className="qe-checkbox"
                        checked={(elementData.multiple as boolean) || false}
                        onChange={(e) =>
                          updateElementData({ multiple: e.target.checked })
                        }
                      />
                      <span>{t("props.multipleSelection")}</span>
                    </label>
                  </div>

                  {((elementData.options as ChoiceOption[]) || []).map(
                    (option, index) => {
                      const rawContentType = option.contentType || "text";
                      const contentType =
                        rawContentType === "mathlive"
                          ? "mathtype"
                          : rawContentType;
                      const optionTitle =
                        option.label?.trim() ||
                        option.imageAlt?.trim() ||
                        (contentType === "image"
                          ? `${t("props.contentTypeImage")} ${t("props.option")} ${index + 1}`
                          : contentType === "mathtype"
                          ? `${t("props.contentTypeMath")} ${t("props.option")} ${index + 1}`
                          : `${t("props.options")} ${index + 1}`);
                      const imageObjectFitValue =
                        option.imageObjectFit ||
                        choiceAppearance?.optionImageObjectFit ||
                        "cover";

                      return (
                        <div key={option.id} className="qe-choice-option">
                          <div className="qe-choice-option__header">
                            <strong className="qe-choice-option__title">
                              {optionTitle}
                            </strong>
                            <button
                              type="button"
                              className="qe-choice-option__remove"
                              onClick={() => removeChoiceOption(option.id)}
                              aria-label={t("props.deleteOption")}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                              >
                                <path
                                  d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          </div>

                          <div className="qe-form__field">
                            <span className="qe-form__label">{t("props.contentType")}</span>
                            <select
                              className="qe-input"
                              value={contentType}
                              onChange={(event) => {
                                const newContentType = event.target.value as
                                  | "text"
                                  | "image"
                                  | "mathtype"
                                  | "mathlive";
                                updateChoiceOption(option.id, {
                                  contentType:
                                    newContentType === "mathlive"
                                      ? "mathtype"
                                      : newContentType,
                                });
                                // Matematik seçildiğinde otomatik editörü aç
                                if (
                                  newContentType === "mathtype" ||
                                  newContentType === "mathlive"
                                ) {
                                  setEditingMathOptionId(option.id);
                                  setEditingMathOrderListItemId(null);
                                  setIsMathEditorOpen(true);
                                }
                              }}
                            >
                              <option value="text">{t("props.contentTypeText")}</option>
                              <option value="image">{t("props.contentTypeImage")}</option>
                              <option value="mathtype">
                                Matematik (MathType)
                              </option>
                            </select>
                          </div>

                          {contentType === "text" && (
                            <div
                              className="qe-form__field"
                              style={{ marginBottom: 0 }}
                            >
                              <span className="qe-form__label">{t("props.label")}</span>
                              <input
                                className="qe-input"
                                type="text"
                                value={option.label || ""}
                                placeholder={t("props.optionTextPlaceholder")}
                                onChange={(event) =>
                                  updateChoiceOption(option.id, {
                                    label: event.target.value,
                                  })
                                }
                              />
                            </div>
                          )}

                          {contentType === "image" && (
                            <>
                              <div className="qe-form__field">
                                <span className="qe-form__label">{t("props.visual")}</span>
                                <button
                                  type="button"
                                  className={`qe-media-library-button ${
                                    option.imageUrl
                                      ? "qe-media-library-button--has-value"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    setMediaLibraryContext({
                                      type: "choice-option",
                                      onSelect: (url) =>
                                        updateChoiceOption(option.id, {
                                          imageUrl: url,
                                        }),
                                      currentUrl: option.imageUrl,
                                    });
                                    setIsMediaLibraryOpen(true);
                                  }}
                                >
                                  <svg
                                    className="qe-media-library-button__icon"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M14 2H2C1.44772 2 1 2.44772 1 3V13C1 13.5523 1.44772 14 2 14H14C14.5523 14 15 13.5523 15 13V3C15 2.44772 14.5523 2 14 2Z"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M5.5 7C6.32843 7 7 6.32843 7 5.5C7 4.67157 6.32843 4 5.5 4C4.67157 4 4 4.67157 4 5.5C4 6.32843 4.67157 7 5.5 7Z"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M15 10L11 6L2 15"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  {option.imageUrl
                                    ? t("props.imageSelected")
                                    : t("props.selectFromMediaLibrary")}
                                </button>
                                {option.imageUrl && (
                                  <div
                                    style={{
                                      marginTop: "8px",
                                      fontSize: "12px",
                                      color: "var(--qe-text-muted)",
                                    }}
                                  >
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setImagePreview({
                                          url: option.imageUrl!,
                                          name: option.label || "Resim",
                                        })
                                      }
                                      style={{
                                        color: "var(--qe-accent)",
                                        textDecoration: "underline",
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                        cursor: "pointer",
                                        fontSize: "12px",
                                        fontFamily: "inherit",
                                      }}
                                    >
                                      {t("props.viewCurrentImage")}
                                    </button>
                                  </div>
                                )}
                              </div>
                              <div className="qe-form__field">
                                <span className="qe-form__label">
                                  Boyut (px)
                                </span>
                                <div className="qe-inline-group">
                                  <label className="qe-inline-input">
                                    <span className="qe-inline-input__label">
                                      {t("props.width")}
                                    </span>
                                    <input
                                      className="qe-input"
                                      type="number"
                                      value={option.imageWidth ?? ""}
                                      placeholder="120"
                                      onChange={(event) => {
                                        const raw = event.target.value;
                                        updateChoiceOption(option.id, {
                                          imageWidth:
                                            raw === ""
                                              ? undefined
                                              : Number(raw) || 0,
                                        });
                                      }}
                                    />
                                  </label>
                                  <label className="qe-inline-input">
                                    <span className="qe-inline-input__label">
                                      {t("props.height")}
                                    </span>
                                    <input
                                      className="qe-input"
                                      type="number"
                                      value={option.imageHeight ?? ""}
                                      placeholder="120"
                                      onChange={(event) => {
                                        const raw = event.target.value;
                                        updateChoiceOption(option.id, {
                                          imageHeight:
                                            raw === ""
                                              ? undefined
                                              : Number(raw) || 0,
                                        });
                                      }}
                                    />
                                  </label>
                                </div>
                                <span className="qe-form__help">
                                  {t("props.autoSizeHelpShort")}
                                </span>
                              </div>
                              <div className="qe-form__field">
                                <span className="qe-form__label">
                                  {t("props.borderRadius")}
                                </span>
                                <input
                                  className="qe-input"
                                  type="number"
                                  min={0}
                                  placeholder={t("props.imagePlaceholder")}
                                  value={
                                    option.imageBorderRadius === undefined
                                      ? ""
                                      : String(option.imageBorderRadius)
                                  }
                                  onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    const val = e.target.value;
                                    if (val === "") {
                                      updateChoiceOption(option.id, {
                                        imageBorderRadius: undefined,
                                      });
                                    } else {
                                      const numValue = Number(val);
                                      if (!isNaN(numValue) && numValue >= 0) {
                                        updateChoiceOption(option.id, {
                                          imageBorderRadius: numValue,
                                        });
                                      }
                                    }
                                  }}
                                  onBlur={(
                                    e: React.FocusEvent<HTMLInputElement>
                                  ) => {
                                    const val = e.target.value.trim();
                                    if (val === "" || val === "0") {
                                      updateChoiceOption(option.id, {
                                        imageBorderRadius: undefined,
                                      });
                                      e.target.value = "";
                                    } else {
                                      const numValue = Number(val);
                                      if (isNaN(numValue) || numValue < 0) {
                                        updateChoiceOption(option.id, {
                                          imageBorderRadius: undefined,
                                        });
                                        e.target.value = "";
                                      } else {
                                        const clamped = Math.max(0, numValue);
                                        updateChoiceOption(option.id, {
                                          imageBorderRadius: clamped,
                                        });
                                        e.target.value = String(clamped);
                                      }
                                    }
                                  }}
                                />
                              </div>
                              <div className="qe-form__field">
                                <span className="qe-form__label">
                                  {t("props.imageScaling")}
                                </span>
                                <select
                                  className="qe-input"
                                  value={imageObjectFitValue}
                                  onChange={(event) =>
                                    updateChoiceOption(option.id, {
                                      imageObjectFit: event.target.value as
                                        | "cover"
                                        | "contain"
                                        | "fill"
                                        | "scale-down",
                                    })
                                  }
                                >
                                  <option value="cover">{t("props.objectFitCover")}</option>
                                  <option value="contain">{t("props.optionContain")}</option>
                                  <option value="fill">{t("props.objectFitFill")}</option>
                                  <option value="scale-down">{t("props.objectFitScaleDown")}</option>
                                </select>
                              </div>
                            </>
                          )}

                          {contentType === "mathtype" && (
                            <>
                              <div className="qe-form__field">
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: "8px",
                                  }}
                                >
                                  <span className="qe-form__label">
                                    {t("props.mathmlFormula")}
                                  </span>
                                  <button
                                    className="qe-button qe-button--primary"
                                    style={{
                                      padding: "4px 12px",
                                      fontSize: "12px",
                                      height: "auto",
                                    }}
                                    onClick={() => {
                                      setEditingMathOptionId(option.id);
                                      setEditingMathOrderListItemId(null);
                                      setIsMathEditorOpen(true);
                                    }}
                                    type="button"
                                  >
                                    {t("props.openEditor")}
                                  </button>
                                </div>
                                <textarea
                                  className="qe-input qe-textarea"
                                  value={option.mathml || option.latex || ""}
                                  placeholder="<math><mi>x</mi><mo>=</mo><mfrac>...</mfrac></math>"
                                  onChange={(event) =>
                                    updateChoiceOption(option.id, {
                                      mathml: event.target.value,
                                    })
                                  }
                                  rows={3}
                                />
                              </div>
                              <div className="qe-form__field">
                                <span className="qe-form__label">
                                  Font Boyutu
                                </span>
                                <input
                                  className="qe-input"
                                  type="number"
                                  {...createNumberInputHandlers(
                                    option.mathFontSize ?? 24,
                                    null,
                                    null,
                                    (value) =>
                                      updateChoiceOption(option.id, {
                                        mathFontSize: value,
                                      })
                                  )}
                                />
                              </div>
                              <div className="qe-form__field">
                                <span className="qe-form__label">
                                  Metin Rengi
                                </span>
                                <input
                                  className="qe-input"
                                  type="color"
                                  value={option.mathColor ?? "#000000"}
                                  onChange={(event) =>
                                    updateChoiceOption(option.id, {
                                      mathColor: event.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div className="qe-form__field">
                                <span className="qe-form__label">
                                  Arkaplan Rengi
                                </span>
                                <input
                                  className="qe-input"
                                  type="color"
                                  value={
                                    option.mathBackgroundColor ?? "transparent"
                                  }
                                  onChange={(event) =>
                                    updateChoiceOption(option.id, {
                                      mathBackgroundColor: event.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div className="qe-form__field">
                                <span className="qe-form__label">
                                  {t("props.contentAlignment")}
                                </span>
                                <select
                                  className="qe-input"
                                  value={option.mathAlign ?? "center"}
                                  onChange={(event) =>
                                    updateChoiceOption(option.id, {
                                      mathAlign: event.target.value as
                                        | "left"
                                        | "center"
                                        | "right",
                                    })
                                  }
                                >
                                  <option value="left">{t("props.left")}</option>
                                  <option value="center">{t("props.center")}</option>
                                  <option value="right">{t("props.right")}</option>
                                </select>
                              </div>
                              <div className="qe-form__field">
                                <span className="qe-form__label">
                                  {t("props.borderWidth")}
                                </span>
                                <input
                                  className="qe-input"
                                  type="number"
                                  min={0}
                                  value={option.mathBorderWidth ?? 0}
                                  onChange={(event) => {
                                    const val =
                                      event.target.value === ""
                                        ? 0
                                        : Number(event.target.value);
                                    updateChoiceOption(option.id, {
                                      mathBorderWidth: Math.max(0, val),
                                    });
                                  }}
                                />
                              </div>
                              <div className="qe-form__field">
                                <span className="qe-form__label">
                                  {t("props.borderColor")}
                                </span>
                                <input
                                  className="qe-input"
                                  type="color"
                                  value={option.mathBorderColor ?? "#cccccc"}
                                  onChange={(event) =>
                                    updateChoiceOption(option.id, {
                                      mathBorderColor: event.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div className="qe-form__field">
                                <span className="qe-form__label">
                                  {t("props.borderRadius")}
                                </span>
                                <input
                                  className="qe-input"
                                  type="number"
                                  min={0}
                                  value={option.mathBorderRadius ?? 4}
                                  onChange={(event) => {
                                    const val =
                                      event.target.value === ""
                                        ? 0
                                        : Number(event.target.value);
                                    updateChoiceOption(option.id, {
                                      mathBorderRadius: Math.max(0, val),
                                    });
                                  }}
                                />
                              </div>
                              <div className="qe-form__field">
                                <span className="qe-form__label">
                                  Padding (px)
                                </span>
                                <div className="qe-inline-group">
                                  <label className="qe-inline-input">
                                    <span className="qe-inline-input__label">
                                      Yatay
                                    </span>
                                    <input
                                      className="qe-input"
                                      type="number"
                                      min={0}
                                      value={option.mathPaddingX ?? 8}
                                      onChange={(event) => {
                                        const val =
                                          event.target.value === ""
                                            ? 0
                                            : Number(event.target.value);
                                        updateChoiceOption(option.id, {
                                          mathPaddingX: Math.max(0, val),
                                        });
                                      }}
                                    />
                                  </label>
                                  <label className="qe-inline-input">
                                    <span className="qe-inline-input__label">
                                      Dikey
                                    </span>
                                    <input
                                      className="qe-input"
                                      type="number"
                                      min={0}
                                      value={option.mathPaddingY ?? 4}
                                      onChange={(event) => {
                                        const val =
                                          event.target.value === ""
                                            ? 0
                                            : Number(event.target.value);
                                        updateChoiceOption(option.id, {
                                          mathPaddingY: Math.max(0, val),
                                        });
                                      }}
                                    />
                                  </label>
                                </div>
                              </div>
                            </>
                          )}

                          <details className="qe-choice-option__advanced">
                            <summary className="qe-choice-option__advanced-trigger">
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                style={{ marginRight: "6px" }}
                              >
                                <path
                                  d="M3 4.5L6 7.5L9 4.5"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <span>{t("props.advancedSettings")}</span>
                            </summary>

                            <div className="qe-choice-option__advanced-content">
                              <div className="qe-form__field">
                                <span className="qe-form__label">
                                  {t("props.valueId")}
                                </span>
                                <input
                                  className="qe-input"
                                  type="text"
                                  value={option.value}
                                  readOnly
                                />
                              </div>

                              <div className="qe-form__field">
                                <span className="qe-form__label">{t("props.iconUrl")}</span>
                                <input
                                  className="qe-input"
                                  type="text"
                                  value={option.customIconUrl || ""}
                                  placeholder="https://..."
                                  onChange={(e) =>
                                    updateChoiceOption(option.id, {
                                      customIconUrl: e.target.value,
                                    })
                                  }
                                />
                              </div>

                              <div className="qe-form__field">
                                <span className="qe-form__label">
                                  {t("props.iconUrlChecked")}
                                </span>
                                <input
                                  className="qe-input"
                                  type="text"
                                  value={option.customIconCheckedUrl || ""}
                                  placeholder="https://..."
                                  onChange={(e) =>
                                    updateChoiceOption(option.id, {
                                      customIconCheckedUrl: e.target.value,
                                    })
                                  }
                                />
                              </div>

                              <div className="qe-form__field">
                                <label className="qe-checkbox-label">
                                  <input
                                    type="checkbox"
                                    className="qe-checkbox"
                                    checked={option.disabled || false}
                                    onChange={(e) =>
                                      updateChoiceOption(option.id, {
                                        disabled: e.target.checked,
                                      })
                                    }
                                  />
                                  <span>{t("props.disabled")}</span>
                                </label>
                              </div>
                            </div>
                          </details>
                        </div>
                      );
                    }
                  )}

                  <div className="qe-form__field">
                    <button
                      type="button"
                      className="qe-button qe-button--secondary"
                      onClick={addChoiceOption}
                      style={{ width: "100%" }}
                    >
                      {t("props.addOption")}
                    </button>
                  </div>
                </div>
              </AccordionContent>
            </div>

            {/* Düzen */}
            <div
              className={`qe-properties-section ${
                openSection === "choice-layout"
                  ? "qe-properties-section--active"
                  : ""
              }`}
            >
              <div
                className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                  openSection === "choice-layout"
                    ? "qe-properties-section__title--open"
                    : "qe-properties-section__title--closed"
                }`}
                onClick={() =>
                  setOpenSection(
                    openSection === "choice-layout" ? null : "choice-layout"
                  )
                }
              >
                <span>{t("props.layout")}</span>
                <svg
                  className={`qe-properties-section__icon ${
                    openSection === "choice-layout"
                      ? "qe-properties-section__icon--open"
                      : ""
                  }`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <AccordionContent isOpen={openSection === "choice-layout"}>
                <div
                  className="qe-properties-section__content"
                  style={{ padding: 0 }}
                >
                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.layout",
                      label: t("props.layoutDirection"),
                      controlType: "select",
                      options: [
                        { value: "vertical", label: t("props.layoutVertical") },
                        { value: "horizontal", label: t("props.layoutHorizontal") },
                        { value: "grid", label: "Grid" },
                      ],
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.layout"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.layout")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.layoutDirection")}</span>
                      <select
                        className="qe-input"
                        value={choiceAppearance.layout}
                        onChange={(e) =>
                          updateChoiceAppearance({
                            layout: e.target.value as
                              | "vertical"
                              | "horizontal"
                              | "grid",
                          })
                        }
                      >
                        <option value="vertical">{t("props.layoutVertical")}</option>
                        <option value="horizontal">{t("props.layoutHorizontal")}</option>
                        <option value="grid">Grid</option>
                      </select>
                    </div>
                  </ShortcutableControl>

                  {choiceAppearance.layout === "grid" && (
                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "choice",
                        propertyPath: "data.appearance.gridColumns",
                        label: t("props.gridColumns"),
                        controlType: "number",
                        min: 1,
                        max: 6,
                        step: 1,
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.appearance.gridColumns"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath(
                          "data.appearance.gridColumns"
                        )
                      }
                    >
                      <div className="qe-form__field">
                        <span className="qe-form__label">{t("props.gridColumns")}</span>
                        <input
                          className="qe-input"
                          type="number"
                          min={1}
                          max={6}
                          {...createNumberInputHandlers(
                            choiceAppearance.gridColumns || 2,
                            1,
                            6,
                            (value) =>
                              updateChoiceAppearance({ gridColumns: value })
                          )}
                        />
                      </div>
                    </ShortcutableControl>
                  )}

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.optionSpacing",
                      label: t("props.optionSpacing"),
                      controlType: "number",
                      min: 0,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.optionSpacing"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.optionSpacing"
                      )
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.optionSpacing")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        {...createNumberInputHandlers(
                          choiceAppearance.optionSpacing,
                          0,
                          null,
                          (value) =>
                            updateChoiceAppearance({ optionSpacing: value })
                        )}
                      />
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.uniformOptionWidth",
                      label: t("props.uniformWidth"),
                      controlType: "toggle",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.uniformOptionWidth"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.uniformOptionWidth"
                      )
                    }
                  >
                    <div className="qe-form__field">
                      <label className="qe-toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(choiceAppearance.uniformOptionWidth)}
                          onChange={(event) =>
                            updateChoiceAppearance({
                              uniformOptionWidth: event.target.checked,
                            })
                          }
                        />
                        <span>{t("props.uniformWidthLabel")}</span>
                      </label>
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.optionPrefix",
                      label: t("props.optionPrefix"),
                      controlType: "select",
                      options: [
                        { value: "none", label: t("props.none") },
                        { value: "letters", label: "A) B) C)" },
                        { value: "letters-lower", label: "a) b) c)" },
                        { value: "numbers", label: "1) 2) 3)" },
                        { value: "numbers-dot", label: "1. 2. 3." },
                        { value: "roman-upper", label: "I) II) III)" },
                        { value: "roman-lower", label: "i) ii) iii)" },
                      ],
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.optionPrefix"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.optionPrefix"
                      )
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.optionPrefix")}</span>
                      <select
                        className="qe-input"
                        value={choiceAppearance.optionPrefix || "none"}
                        onChange={(event) =>
                          updateChoiceAppearance({
                            optionPrefix: event.target.value as
                              | "none"
                              | "letters"
                              | "numbers",
                          })
                        }
                      >
                        <option value="none">{t("props.none")}</option>
                        <option value="letters">A) B) C)</option>
                        <option value="letters-lower">a) b) c)</option>
                        <option value="numbers">1) 2) 3)</option>
                        <option value="numbers-dot">1. 2. 3.</option>
                        <option value="roman-upper">I) II) III)</option>
                        <option value="roman-lower">i) ii) iii)</option>
                      </select>
                    </div>
                  </ShortcutableControl>
                </div>
              </AccordionContent>
            </div>

            {/* Yazı Stili */}
            <div
              className={`qe-properties-section ${
                openSection === "choice-text-style"
                  ? "qe-properties-section--active"
                  : ""
              }`}
            >
              <div
                className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                  openSection === "choice-text-style"
                    ? "qe-properties-section__title--open"
                    : "qe-properties-section__title--closed"
                }`}
                onClick={() =>
                  setOpenSection(
                    openSection === "choice-text-style"
                      ? null
                      : "choice-text-style"
                  )
                }
              >
                <span>{t("props.textStyle")}</span>
                <svg
                  className={`qe-properties-section__icon ${
                    openSection === "choice-text-style"
                      ? "qe-properties-section__icon--open"
                      : ""
                  }`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <AccordionContent isOpen={openSection === "choice-text-style"}>
                <div
                  className="qe-properties-section__content"
                  style={{ padding: 0 }}
                >
                  <ShortcutableControl
                      shortcutConfig={{
                        elementType: "choice",
                        propertyPath: "data.appearance.fontFamily",
                        label: t("props.fontFamily"),
                        controlType: "select",
                        options: FONT_OPTIONS,
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.appearance.fontFamily"
                      )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.fontFamily")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.fontFamily")}</span>
                      <select
                        className="qe-input"
                        value={normalizeFontFamilyValue(
                          choiceAppearance.fontFamily
                        )}
                        onChange={(e) =>
                          updateChoiceAppearance({ fontFamily: e.target.value })
                        }
                      >
                        {FONT_OPTIONS.map((font) => (
                          <option key={font.value} value={font.value}>
                            {font.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.fontSize",
                      label: t("props.size"),
                      controlType: "number",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.fontSize"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.fontSize")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.size")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        {...createNumberInputHandlers(
                          choiceAppearance.fontSize,
                          null,
                          null,
                          (value) => updateChoiceAppearance({ fontSize: value })
                        )}
                      />
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.fontWeight",
                      label: t("props.fontWeight"),
                      controlType: "select",
                      options: [
                        { value: "300", label: t("props.fontWeightThin") },
                        { value: "400", label: t("props.fontWeightNormal") },
                        { value: "600", label: t("props.fontWeightMedium") },
                      ],
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.fontWeight"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.fontWeight")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.thickness")}</span>
                      <select
                        className="qe-input"
                        value={choiceAppearance.fontWeight}
                        onChange={(e) =>
                          updateChoiceAppearance({
                            fontWeight: Number(e.target.value),
                          })
                        }
                      >
                        <option value="300">{t("props.fontWeightThin")}</option>
                        <option value="400">Normal</option>
                        <option value="600">{t("props.fontWeightMedium")}</option>
                      </select>
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.textAlign",
                      label: t("props.alignment"),
                      controlType: "segmented",
                      options: [
                        { value: "left", label: t("props.left") },
                        { value: "center", label: t("props.center") },
                        { value: "right", label: t("props.right") },
                      ],
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.textAlign"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.textAlign")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.alignment")}</span>
                      <select
                        className="qe-input"
                        value={choiceAppearance.textAlign}
                        onChange={(e) =>
                          updateChoiceAppearance({
                            textAlign: e.target.value as
                              | "left"
                              | "center"
                              | "right",
                          })
                        }
                      >
                        <option value="left">{t("props.left")}</option>
                        <option value="center">{t("props.center")}</option>
                        <option value="right">{t("props.right")}</option>
                      </select>
                    </div>
                  </ShortcutableControl>
                </div>
              </AccordionContent>
            </div>

            {/* Renkler */}
            <div
              className={`qe-properties-section ${
                openSection === "choice-colors"
                  ? "qe-properties-section--active"
                  : ""
              }`}
            >
              <div
                className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                  openSection === "choice-colors"
                    ? "qe-properties-section__title--open"
                    : "qe-properties-section__title--closed"
                }`}
                onClick={() =>
                  setOpenSection(
                    openSection === "choice-colors" ? null : "choice-colors"
                  )
                }
              >
                <span>{t("props.colors")}</span>
                <svg
                  className={`qe-properties-section__icon ${
                    openSection === "choice-colors"
                      ? "qe-properties-section__icon--open"
                      : ""
                  }`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <AccordionContent isOpen={openSection === "choice-colors"}>
                <div
                  className="qe-properties-section__content"
                  style={{ padding: 0 }}
                >
                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.optionBackground",
                      label: t("props.background"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.optionBackground"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.optionBackground"
                      )
                    }
                  >
                    {renderColorControl(
                      t("props.background"),
                      choiceAppearance.optionBackground,
                      (c) => updateChoiceAppearance({ optionBackground: c })
                    )}
                  </ShortcutableControl>
                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.optionHoverBackground",
                      label: t("props.backgroundHover"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.optionHoverBackground"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.optionHoverBackground"
                      )
                    }
                  >
                    {renderColorControl(
                      t("props.backgroundHover"),
                      choiceAppearance.optionHoverBackground,
                      (c) =>
                        updateChoiceAppearance({ optionHoverBackground: c })
                    )}
                  </ShortcutableControl>
                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.optionActiveBackground",
                      label: t("props.backgroundActive"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.optionActiveBackground"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.optionActiveBackground"
                      )
                    }
                  >
                    {renderColorControl(
                      t("props.backgroundActive"),
                      choiceAppearance.optionActiveBackground,
                      (c) =>
                        updateChoiceAppearance({ optionActiveBackground: c })
                    )}
                  </ShortcutableControl>
                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.optionTextColor",
                      label: t("props.textColor"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.optionTextColor"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.optionTextColor"
                      )
                    }
                  >
                    {renderColorControl(
                      t("props.textColor"),
                      choiceAppearance.optionTextColor,
                      (c) => updateChoiceAppearance({ optionTextColor: c }),
                      { allowTransparent: false }
                    )}
                  </ShortcutableControl>
                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.optionHoverTextColor",
                      label: t("props.textColorHover"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.optionHoverTextColor"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.optionHoverTextColor"
                      )
                    }
                  >
                    {renderColorControl(
                      t("props.textColorHover"),
                      choiceAppearance.optionHoverTextColor,
                      (c) =>
                        updateChoiceAppearance({ optionHoverTextColor: c }),
                      { allowTransparent: false }
                    )}
                  </ShortcutableControl>
                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.optionActiveTextColor",
                      label: t("props.textColorActive"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.optionActiveTextColor"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.optionActiveTextColor"
                      )
                    }
                  >
                    {renderColorControl(
                      t("props.textColorActive"),
                      choiceAppearance.optionActiveTextColor,
                      (c) =>
                        updateChoiceAppearance({ optionActiveTextColor: c }),
                      { allowTransparent: false }
                    )}
                  </ShortcutableControl>
                </div>
              </AccordionContent>
            </div>

            {/* Kenarlık & Boşluk */}
            <div
              className={`qe-properties-section ${
                openSection === "choice-borders"
                  ? "qe-properties-section--active"
                  : ""
              }`}
            >
              <div
                className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                  openSection === "choice-borders"
                    ? "qe-properties-section__title--open"
                    : "qe-properties-section__title--closed"
                }`}
                onClick={() =>
                  setOpenSection(
                    openSection === "choice-borders" ? null : "choice-borders"
                  )
                }
              >
                <span>{t("props.borderAndSpacing")}</span>
                <svg
                  className={`qe-properties-section__icon ${
                    openSection === "choice-borders"
                      ? "qe-properties-section__icon--open"
                      : ""
                  }`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <AccordionContent isOpen={openSection === "choice-borders"}>
                <div
                  className="qe-properties-section__content"
                  style={{ padding: 0 }}
                >
                  <div className="qe-form__field">
                    <div className="qe-inline-group">
                      <span className="qe-form__label">{t("props.padding")}</span>
                      <label className="qe-inline-input">
                        <span className="qe-inline-input__label">X</span>
                        <ShortcutableControl
                          shortcutConfig={{
                            elementType: "choice",
                            propertyPath: "data.appearance.optionPaddingX",
                            label: t("props.paddingX"),
                            controlType: "number",
                            min: 0,
                          }}
                          onAddShortcut={onAddShortcut}
                          isInShortcuts={isPropertyInShortcuts(
                            "data.appearance.optionPaddingX"
                          )}
                          onRemoveShortcut={() =>
                            handleRemoveShortcutByPath(
                              "data.appearance.optionPaddingX"
                            )
                          }
                        >
                          <input
                            className="qe-input"
                            type="number"
                            min={0}
                            value={choiceAppearance.optionPaddingX}
                            onChange={(e) =>
                              updateChoiceAppearance({
                                optionPaddingX: Math.max(
                                  0,
                                  Number(e.target.value) || 0
                                ),
                              })
                            }
                          />
                        </ShortcutableControl>
                      </label>
                      <label className="qe-inline-input">
                        <span className="qe-inline-input__label">Y</span>
                        <ShortcutableControl
                          shortcutConfig={{
                            elementType: "choice",
                            propertyPath: "data.appearance.optionPaddingY",
                            label: t("props.paddingY"),
                            controlType: "number",
                            min: 0,
                          }}
                          onAddShortcut={onAddShortcut}
                          isInShortcuts={isPropertyInShortcuts(
                            "data.appearance.optionPaddingY"
                          )}
                          onRemoveShortcut={() =>
                            handleRemoveShortcutByPath(
                              "data.appearance.optionPaddingY"
                            )
                          }
                        >
                          <input
                            className="qe-input"
                            type="number"
                            min={0}
                            value={choiceAppearance.optionPaddingY}
                            onChange={(e) =>
                              updateChoiceAppearance({
                                optionPaddingY: Math.max(
                                  0,
                                  Number(e.target.value) || 0
                                ),
                              })
                            }
                          />
                        </ShortcutableControl>
                      </label>
                    </div>
                  </div>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.optionBorderWidth",
                      label: t("props.borderWidth"),
                      controlType: "number",
                      min: 0,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.optionBorderWidth"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.optionBorderWidth"
                      )
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.borderWidth")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        value={choiceAppearance.optionBorderWidth}
                        onChange={(e) =>
                          updateChoiceAppearance({
                            optionBorderWidth: Math.max(
                              0,
                              Number(e.target.value) || 0
                            ),
                          })
                        }
                      />
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.optionBorderColor",
                      label: t("props.borderColor"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.optionBorderColor"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.optionBorderColor"
                      )
                    }
                  >
                    {renderColorControl(
                      t("props.borderColor"),
                      choiceAppearance.optionBorderColor,
                      (c) => updateChoiceAppearance({ optionBorderColor: c })
                    )}
                  </ShortcutableControl>
                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.optionHoverBorderColor",
                      label: t("props.borderColorHover"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.optionHoverBorderColor"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.optionHoverBorderColor"
                      )
                    }
                  >
                    {renderColorControl(
                      t("props.borderColorHover"),
                      choiceAppearance.optionHoverBorderColor,
                      (c) =>
                        updateChoiceAppearance({ optionHoverBorderColor: c })
                    )}
                  </ShortcutableControl>
                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.optionActiveBorderColor",
                      label: t("props.borderColorActive"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.optionActiveBorderColor"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.optionActiveBorderColor"
                      )
                    }
                  >
                    {renderColorControl(
                      t("props.borderColorActive"),
                      choiceAppearance.optionActiveBorderColor,
                      (c) =>
                        updateChoiceAppearance({ optionActiveBorderColor: c })
                    )}
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "choice",
                      propertyPath: "data.appearance.optionBorderRadius",
                      label: t("props.borderRadius"),
                      controlType: "number",
                      min: 0,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.optionBorderRadius"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.optionBorderRadius"
                      )
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.borderRadius")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        value={choiceAppearance.optionBorderRadius}
                        onChange={(e) =>
                          updateChoiceAppearance({
                            optionBorderRadius: Math.max(
                              0,
                              Number(e.target.value) || 0
                            ),
                          })
                        }
                      />
                    </div>
                  </ShortcutableControl>
                </div>
              </AccordionContent>
            </div>

            {/* Gösterge (Checkbox/Radio) */}
            <div
              className={`qe-properties-section ${
                openSection === "choice-indicator"
                  ? "qe-properties-section--active"
                  : ""
              }`}
            >
              <div
                className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                  openSection === "choice-indicator"
                    ? "qe-properties-section__title--open"
                    : "qe-properties-section__title--closed"
                }`}
                onClick={() =>
                  setOpenSection(
                    openSection === "choice-indicator"
                      ? null
                      : "choice-indicator"
                  )
                }
              >
                <span>{t("props.indicator")}</span>
                <svg
                  className={`qe-properties-section__icon ${
                    openSection === "choice-indicator"
                      ? "qe-properties-section__icon--open"
                      : ""
                  }`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <AccordionContent isOpen={openSection === "choice-indicator"}>
                <div
                  className="qe-properties-section__content"
                  style={{ padding: 0 }}
                >
                  <div className="qe-form__field">
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={choiceAppearance.showIndicator}
                        onChange={(e) =>
                          updateChoiceAppearance({
                            showIndicator: e.target.checked,
                          })
                        }
                      />
                      <span>{t("props.showIndicator")}</span>
                    </label>
                  </div>

                  {choiceAppearance.showIndicator && (
                    <>
                      <ShortcutableControl
                        shortcutConfig={{
                          elementType: "choice",
                          propertyPath: "data.appearance.indicatorType",
                          label: t("props.type"),
                          controlType: "select",
                          options: [
                            { value: "radio", label: "Radio" },
                            { value: "checkbox", label: "Checkbox" },
                            { value: "custom", label: "Custom" },
                          ],
                        }}
                        onAddShortcut={onAddShortcut}
                        isInShortcuts={isPropertyInShortcuts(
                          "data.appearance.indicatorType"
                        )}
                        onRemoveShortcut={() =>
                          handleRemoveShortcutByPath(
                            "data.appearance.indicatorType"
                          )
                        }
                      >
                        <div className="qe-form__field">
                          <span className="qe-form__label">{t("props.type")}</span>
                          <select
                            key={`indicator-type-${nonNullElement.id}`}
                            className="qe-input"
                            value={choiceAppearance.indicatorType}
                            onChange={(e) =>
                              updateChoiceAppearance({
                                indicatorType: e.target.value as
                                  | "checkbox"
                                  | "radio"
                                  | "custom",
                              })
                            }
                          >
                            <option value="radio">Radio</option>
                            <option value="checkbox">Checkbox</option>
                            <option value="custom">Custom</option>
                          </select>
                        </div>
                      </ShortcutableControl>

                      <ShortcutableControl
                        shortcutConfig={{
                          elementType: "choice",
                          propertyPath: "data.appearance.indicatorPosition",
                          label: t("props.position"),
                          controlType: "select",
                          options: [
                            { value: "leading", label: t("props.left") },
                            { value: "trailing", label: t("props.right") },
                          ],
                        }}
                        onAddShortcut={onAddShortcut}
                        isInShortcuts={isPropertyInShortcuts(
                          "data.appearance.indicatorPosition"
                        )}
                        onRemoveShortcut={() =>
                          handleRemoveShortcutByPath(
                            "data.appearance.indicatorPosition"
                          )
                        }
                      >
                        <div className="qe-form__field">
                          <span className="qe-form__label">{t("props.position")}</span>
                          <select
                            className="qe-input"
                            value={choiceAppearance.indicatorPosition}
                            onChange={(e) =>
                              updateChoiceAppearance({
                                indicatorPosition: e.target.value as
                                  | "leading"
                                  | "trailing",
                              })
                            }
                          >
                            <option value="leading">{t("props.left")}</option>
                            <option value="trailing">{t("props.right")}</option>
                          </select>
                        </div>
                      </ShortcutableControl>

                      {choiceAppearance.indicatorType === "custom" ? (
                        <>
                          <div className="qe-form__field">
                            <span className="qe-form__label">
                              {t("props.iconUrlNormal")}
                            </span>
                            <input
                              className="qe-input"
                              type="text"
                              value={
                                choiceAppearance.indicatorCustomIconUrl || ""
                              }
                              placeholder="https://..."
                              onChange={(e) =>
                                updateChoiceAppearance({
                                  indicatorCustomIconUrl: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="qe-form__field">
                            <span className="qe-form__label">
                              {t("props.iconUrlChecked")}
                            </span>
                            <input
                              className="qe-input"
                              type="text"
                              value={
                                choiceAppearance.indicatorCustomIconCheckedUrl ||
                                ""
                              }
                              placeholder="https://..."
                              onChange={(e) =>
                                updateChoiceAppearance({
                                  indicatorCustomIconCheckedUrl: e.target.value,
                                })
                              }
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "choice",
                              propertyPath: "data.appearance.indicatorColor",
                              label: t("props.indicatorColor"),
                              controlType: "color",
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.indicatorColor"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.indicatorColor"
                              )
                            }
                          >
                            {renderColorControl(
                              t("props.color"),
                              choiceAppearance.indicatorColor,
                              (c) =>
                                updateChoiceAppearance({ indicatorColor: c })
                            )}
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "choice",
                              propertyPath:
                                "data.appearance.indicatorActiveColor",
                              label: t("props.indicatorColorActive"),
                              controlType: "color",
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.indicatorActiveColor"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.indicatorActiveColor"
                              )
                            }
                          >
                            {renderColorControl(
                              t("props.colorActive"),
                              choiceAppearance.indicatorActiveColor,
                              (c) =>
                                updateChoiceAppearance({
                                  indicatorActiveColor: c,
                                })
                            )}
                          </ShortcutableControl>
                        </>
                      )}

                      <ShortcutableControl
                        shortcutConfig={{
                          elementType: "choice",
                          propertyPath: "data.appearance.indicatorSize",
                          label: t("props.size"),
                          controlType: "number",
                          min: 12,
                          max: 48,
                        }}
                        onAddShortcut={onAddShortcut}
                        isInShortcuts={isPropertyInShortcuts(
                          "data.appearance.indicatorSize"
                        )}
                        onRemoveShortcut={() =>
                          handleRemoveShortcutByPath(
                            "data.appearance.indicatorSize"
                          )
                        }
                      >
                        <div className="qe-form__field">
                          <span className="qe-form__label">{t("props.size")}</span>
                          <input
                            className="qe-input"
                            type="number"
                            min={12}
                            max={48}
                            value={choiceAppearance.indicatorSize}
                            onChange={(e) =>
                              updateChoiceAppearance({
                                indicatorSize: Math.max(
                                  12,
                                  Number(e.target.value) || 20
                                ),
                              })
                            }
                          />
                        </div>
                      </ShortcutableControl>
                    </>
                  )}
                </div>
              </AccordionContent>
            </div>

            {/* İkonlar */}
            <div
              className={`qe-properties-section ${
                openSection === "choice-icons"
                  ? "qe-properties-section--active"
                  : ""
              }`}
            >
              <div
                className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                  openSection === "choice-icons"
                    ? "qe-properties-section__title--open"
                    : "qe-properties-section__title--closed"
                }`}
                onClick={() =>
                  setOpenSection(
                    openSection === "choice-icons" ? null : "choice-icons"
                  )
                }
              >
                <span>{t("props.icons")}</span>
                <svg
                  className={`qe-properties-section__icon ${
                    openSection === "choice-icons"
                      ? "qe-properties-section__icon--open"
                      : ""
                  }`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <AccordionContent isOpen={openSection === "choice-icons"}>
                <div
                  className="qe-properties-section__content"
                  style={{ padding: 0 }}
                >
                  <div className="qe-form__field">
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={choiceAppearance.showOptionIcon}
                        onChange={(e) =>
                          updateChoiceAppearance({
                            showOptionIcon: e.target.checked,
                          })
                        }
                      />
                      <span>{t("props.showIcons")}</span>
                    </label>
                  </div>

                  {choiceAppearance.showOptionIcon && (
                    <>
                      <ShortcutableControl
                        shortcutConfig={{
                          elementType: "choice",
                          propertyPath: "data.appearance.optionIconPosition",
                          label: t("props.position"),
                          controlType: "select",
                          options: [
                            { value: "left", label: t("props.left") },
                            { value: "right", label: t("props.right") },
                            { value: "top", label: t("props.posTop") },
                            { value: "bottom", label: t("props.posBottom") },
                          ],
                        }}
                        onAddShortcut={onAddShortcut}
                        isInShortcuts={isPropertyInShortcuts(
                          "data.appearance.optionIconPosition"
                        )}
                        onRemoveShortcut={() =>
                          handleRemoveShortcutByPath(
                            "data.appearance.optionIconPosition"
                          )
                        }
                      >
                        <div className="qe-form__field">
                          <span className="qe-form__label">{t("props.position")}</span>
                          <select
                            className="qe-input"
                            value={choiceAppearance.optionIconPosition}
                            onChange={(e) =>
                              updateChoiceAppearance({
                                optionIconPosition: e.target.value as any,
                              })
                            }
                          >
                            <option value="left">{t("props.left")}</option>
                            <option value="right">{t("props.right")}</option>
                            <option value="top">{t("props.posTop")}</option>
                            <option value="bottom">{t("props.posBottom")}</option>
                          </select>
                        </div>
                      </ShortcutableControl>

                      <ShortcutableControl
                        shortcutConfig={{
                          elementType: "choice",
                          propertyPath: "data.appearance.optionIconSize",
                          label: t("props.iconSize"),
                          controlType: "number",
                          min: 12,
                          max: 64,
                        }}
                        onAddShortcut={onAddShortcut}
                        isInShortcuts={isPropertyInShortcuts(
                          "data.appearance.optionIconSize"
                        )}
                        onRemoveShortcut={() =>
                          handleRemoveShortcutByPath(
                            "data.appearance.optionIconSize"
                          )
                        }
                      >
                        <div className="qe-form__field">
                          <span className="qe-form__label">{t("props.iconSize")}</span>
                          <input
                            className="qe-input"
                            type="number"
                            min={12}
                            max={64}
                            value={choiceAppearance.optionIconSize}
                            onChange={(e) =>
                              updateChoiceAppearance({
                                optionIconSize: Math.max(
                                  12,
                                  Number(e.target.value) || 24
                                ),
                              })
                            }
                          />
                        </div>
                      </ShortcutableControl>
                    </>
                  )}
                </div>
              </AccordionContent>
            </div>
          </>
        ) : null}

        {nonNullElement.type === "audio" && audioStyle ? (
          <div
            className={`qe-properties-section ${
              openSection === "audio-settings"
                ? "qe-properties-section--active"
                : ""
            }`}
          >
            <div
              className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                openSection === "audio-settings"
                  ? "qe-properties-section__title--open"
                  : "qe-properties-section__title--closed"
              }`}
              onClick={() =>
                setOpenSection(
                  openSection === "audio-settings" ? null : "audio-settings"
                )
              }
            >
              <span>{t("props.audioSettings")}</span>
              <svg
                className={`qe-properties-section__icon ${
                  openSection === "audio-settings"
                    ? "qe-properties-section__icon--open"
                    : ""
                }`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <AccordionContent isOpen={openSection === "audio-settings"}>
              <div
                className="qe-properties-section__content"
                style={{ padding: 0 }}
              >
                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.audioUrl")}</span>
                  <button
                    type="button"
                    className={`qe-media-library-button ${
                      audioStyle.sourceUrl
                        ? "qe-media-library-button--has-value"
                        : ""
                    }`}
                    onClick={() => {
                      setSoundLibraryContext({
                        onSelect: (url) => {
                          updateAudioStyle({
                            sourceUrl: url,
                            showControls: false,
                            showIcon: true,
                            showLabel: false,
                          });
                          onResize(nonNullElement.id, {
                            width: 64,
                            height: 56,
                          });
                        },
                        currentUrl: audioStyle.sourceUrl,
                      });
                      setIsSoundLibraryOpen(true);
                    }}
                    title={
                      audioStyle.sourceUrl
                        ? t("props.audioClickToChange")
                        : t("props.audioSelectFromLibrary")
                    }
                  >
                    <svg
                      className="qe-media-library-button__icon"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 2v5.7c-.4-.23-.85-.37-1.33-.37-1.47 0-2.67 1.19-2.67 2.67s1.19 2.67 2.67 2.67 2.67-1.19 2.67-2.67V3.33h2.67V2H8z"
                        fill="currentColor"
                      />
                    </svg>
                    {audioStyle.sourceUrl
                      ? t("props.audioSelected")
                      : t("props.audioSelectFromSoundLibrary")}
                  </button>
                  <input
                    className="qe-input"
                    type="url"
                    placeholder="https://example.com/audio.mp3"
                    value={audioStyle.sourceUrl}
                    onChange={(event) =>
                      updateAudioStyle({ sourceUrl: event.target.value })
                    }
                    style={{ marginTop: "8px" }}
                  />
                  <span className="qe-form__help">
                    {t("props.audioUrlHelp")}
                  </span>
                </div>

                <div className="qe-form__field">
                  <label className="qe-toggle">
                    <input
                      type="checkbox"
                      checked={audioStyle.showIcon}
                      onChange={(event) =>
                        updateAudioStyle({ showIcon: event.target.checked })
                      }
                    />
                    {t("props.showIcon")}
                  </label>
                </div>

                <div className="qe-form__field">
                  <label className="qe-toggle">
                    <input
                      type="checkbox"
                      checked={audioStyle.showLabel}
                      onChange={(event) =>
                        updateAudioStyle({ showLabel: event.target.checked })
                      }
                    />
                    {t("props.showLabel")}
                  </label>
                </div>

                {audioStyle.showLabel && (
                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.visibleLabel")}</span>
                    <input
                      className="qe-input"
                      type="text"
                      placeholder={t("props.audioPlayPlaceholder")}
                      value={audioStyle.label}
                      onChange={(event) =>
                        updateAudioStyle({ label: event.target.value })
                      }
                    />
                  </div>
                )}

                <div className="qe-form__field">
                  <label className="qe-toggle">
                    <input
                      type="checkbox"
                      checked={audioStyle.autoPlay}
                      onChange={(event) =>
                        updateAudioStyle({ autoPlay: event.target.checked })
                      }
                    />
                    {t("props.autoPlay")}
                  </label>
                </div>

                <div className="qe-form__field">
                  <label className="qe-toggle">
                    <input
                      type="checkbox"
                      checked={audioStyle.loop}
                      onChange={(event) =>
                        updateAudioStyle({ loop: event.target.checked })
                      }
                    />
                    {t("props.loop")}
                  </label>
                </div>

                <div className="qe-form__field">
                  <label className="qe-toggle">
                    <input
                      type="checkbox"
                      checked={audioStyle.showControls}
                      onChange={(event) =>
                        updateAudioStyle({ showControls: event.target.checked })
                      }
                    />
                    {t("props.showControls")}
                  </label>
                </div>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "audio",
                    propertyPath: "data.style.backgroundColor",
                    label: t("props.backgroundColor"),
                    controlType: "color",
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.style.backgroundColor"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.backgroundColor")
                  }
                >
                  {renderColorControl(
                    t("props.backgroundColor"),
                    audioStyle.backgroundColor,
                    (color) => updateAudioStyle({ backgroundColor: color }),
                    { allowTransparent: true }
                  )}
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "audio",
                    propertyPath: "data.style.borderColor",
                    label: t("props.borderColor"),
                    controlType: "color",
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.style.borderColor"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.borderColor")
                  }
                >
                  {renderColorControl(
                    t("props.borderColor"),
                    audioStyle.borderColor,
                    (color) => updateAudioStyle({ borderColor: color }),
                    { allowTransparent: true }
                  )}
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "audio",
                    propertyPath: "data.style.borderWidth",
                    label: t("props.borderWidth"),
                    controlType: "number",
                    min: 0,
                    max: 20,
                    step: 1,
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.style.borderWidth"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.borderWidth")
                  }
                >
                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.borderWidth")}</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      {...createNumberInputHandlers(
                        audioStyle.borderWidth,
                        0,
                        20,
                        (value) => updateAudioStyle({ borderWidth: value })
                      )}
                    />
                  </div>
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "audio",
                    propertyPath: "data.style.borderRadius",
                    label: t("props.borderRadius"),
                    controlType: "number",
                    min: 0,
                    max: 100,
                    step: 1,
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.style.borderRadius"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.borderRadius")
                  }
                >
                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.borderRadius")}</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      {...createNumberInputHandlers(
                        audioStyle.borderRadius,
                        0,
                        100,
                        (value) => updateAudioStyle({ borderRadius: value })
                      )}
                    />
                  </div>
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "audio",
                    propertyPath: "data.style.textColor",
                    label: t("props.textColor"),
                    controlType: "color",
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts("data.style.textColor")}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.textColor")
                  }
                >
                  {renderColorControl(
                    t("props.textColor"),
                    audioStyle.textColor,
                    (color) => updateAudioStyle({ textColor: color }),
                    { allowTransparent: false }
                  )}
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "audio",
                    propertyPath: "data.style.iconColor",
                    label: t("props.iconColor"),
                    controlType: "color",
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts("data.style.iconColor")}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.iconColor")
                  }
                >
                  {renderColorControl(
                    t("props.iconColor"),
                    audioStyle.iconColor,
                    (color) => updateAudioStyle({ iconColor: color }),
                    { allowTransparent: false }
                  )}
                </ShortcutableControl>

                <div className="qe-inline-group">
                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "audio",
                      propertyPath: "data.style.paddingX",
                      label: t("props.horizontalPadding"),
                      controlType: "number",
                      min: 0,
                      max: 100,
                      step: 1,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts("data.style.paddingX")}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.style.paddingX")
                    }
                  >
                    <label className="qe-inline-input">
                      <span className="qe-inline-input__label">
                        Yatay Dolgu
                      </span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        {...createNumberInputHandlers(
                          audioStyle.paddingX,
                          0,
                          null,
                          (value) => updateAudioStyle({ paddingX: value })
                        )}
                      />
                    </label>
                  </ShortcutableControl>
                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "audio",
                      propertyPath: "data.style.paddingY",
                      label: t("props.verticalPadding"),
                      controlType: "number",
                      min: 0,
                      max: 100,
                      step: 1,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts("data.style.paddingY")}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.style.paddingY")
                    }
                  >
                    <label className="qe-inline-input">
                      <span className="qe-inline-input__label">
                        Dikey Dolgu
                      </span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        {...createNumberInputHandlers(
                          audioStyle.paddingY,
                          0,
                          null,
                          (value) => updateAudioStyle({ paddingY: value })
                        )}
                      />
                    </label>
                  </ShortcutableControl>
                </div>
              </div>
            </AccordionContent>
          </div>
        ) : null}

        {nonNullElement.type === "slide" && videoStyle ? (
          <div
            className={`qe-properties-section ${
              openSection === "video-settings"
                ? "qe-properties-section--active"
                : ""
            }`}
          >
            <div
              className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                openSection === "video-settings"
                  ? "qe-properties-section__title--open"
                  : "qe-properties-section__title--closed"
              }`}
              onClick={() =>
                setOpenSection(
                  openSection === "video-settings" ? null : "video-settings"
                )
              }
            >
              <span>{t("props.videoSettings")}</span>
              <svg
                className={`qe-properties-section__icon ${
                  openSection === "video-settings"
                    ? "qe-properties-section__icon--open"
                    : ""
                }`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <AccordionContent isOpen={openSection === "video-settings"}>
              <div
                className="qe-properties-section__content"
                style={{ padding: 0 }}
              >
                <div className="qe-form__field">
                  <span className="qe-form__label">Video URL'si</span>
                  <button
                    type="button"
                    className={`qe-media-library-button ${
                      videoStyle.sourceUrl
                        ? "qe-media-library-button--has-value"
                        : ""
                    }`}
                    onClick={() => {
                      setVideoLibraryContext({
                        onSelect: (url) => updateVideoStyle({ sourceUrl: url }),
                        currentUrl: videoStyle.sourceUrl,
                      });
                      setIsVideoLibraryOpen(true);
                    }}
                    title={
                      videoStyle.sourceUrl
                        ? t("props.videoClickToChange")
                        : t("props.videoSelectFromLibrary")
                    }
                  >
                    <svg
                      className="qe-media-library-button__icon"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 4v8l6-4-6-4z"
                        fill="currentColor"
                      />
                    </svg>
                    {videoStyle.sourceUrl
                      ? t("props.videoSelected")
                      : t("props.selectFromMediaLibrary")}
                  </button>
                  <input
                    className="qe-input"
                    type="url"
                    placeholder="https://example.com/video.mp4"
                    value={videoStyle.sourceUrl}
                    onChange={(event) =>
                      updateVideoStyle({ sourceUrl: event.target.value })
                    }
                    style={{ marginTop: "8px" }}
                  />
                  <span className="qe-form__help">
                    {t("props.videoUrlHelp")}
                  </span>
                </div>

                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.label")}</span>
                  <input
                    className="qe-input"
                    type="text"
                    placeholder="Video"
                    value={videoStyle.label}
                    onChange={(event) =>
                      updateVideoStyle({ label: event.target.value })
                    }
                  />
                </div>

                <div className="qe-form__field">
                  <label className="qe-toggle">
                    <input
                      type="checkbox"
                      checked={videoStyle.autoPlay}
                      onChange={(event) =>
                        updateVideoStyle({ autoPlay: event.target.checked })
                      }
                    />
                    {t("props.autoPlay")}
                  </label>
                </div>

                <div className="qe-form__field">
                  <label className="qe-toggle">
                    <input
                      type="checkbox"
                      checked={videoStyle.loop}
                      onChange={(event) =>
                        updateVideoStyle({ loop: event.target.checked })
                      }
                    />
                    {t("props.loop")}
                  </label>
                </div>

                <div className="qe-form__field">
                  <label className="qe-toggle">
                    <input
                      type="checkbox"
                      checked={videoStyle.showControls}
                      onChange={(event) =>
                        updateVideoStyle({ showControls: event.target.checked })
                      }
                    />
                    {t("props.showControls")}
                  </label>
                </div>

                <div className="qe-form__field">
                  <span className="qe-form__label">
                    {t("props.videoEndElements")}
                  </span>
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      padding: "12px",
                      maxHeight: "200px",
                      overflowY: "auto",
                      backgroundColor: "#f9fafb",
                    }}
                  >
                    {element && elements
                      .filter((el) => el.id !== element.id)
                      .map((el) => {
                        const isSelected =
                          videoStyle.showElementsAfterVideo.includes(el.id);
                        const elementLabel = deriveElementLabel(el);
                        return (
                          <label
                            key={el.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "6px 0",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(event) => {
                                const currentIds =
                                  videoStyle.showElementsAfterVideo || [];
                                if (event.target.checked) {
                                  updateVideoStyle({
                                    showElementsAfterVideo: [
                                      ...currentIds,
                                      el.id,
                                    ],
                                  });
                                } else {
                                  updateVideoStyle({
                                    showElementsAfterVideo: currentIds.filter(
                                      (id) => id !== el.id
                                    ),
                                  });
                                }
                              }}
                              style={{
                                width: "16px",
                                height: "16px",
                                cursor: "pointer",
                              }}
                            />
                            <span style={{ fontSize: "14px" }}>
                              {elementLabel} ({el.type})
                            </span>
                          </label>
                        );
                      })}
                    {(!element || elements.filter((el) => el.id !== element.id).length === 0) && (
                      <div
                        style={{
                          color: "#6b7280",
                          fontSize: "14px",
                          fontStyle: "italic",
                        }}
                      >
                        {t("props.noOtherElements")}
                      </div>
                    )}
                  </div>
                  <span className="qe-form__help">
                    {t("props.videoElementsHelp")}
                  </span>
                </div>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "slide",
                    propertyPath: "data.style.borderColor",
                    label: t("props.borderColor"),
                    controlType: "color",
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.style.borderColor"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.borderColor")
                  }
                >
                  {renderColorControl(
                    t("props.borderColor"),
                    videoStyle.borderColor,
                    (color) => updateVideoStyle({ borderColor: color }),
                    { allowTransparent: false }
                  )}
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "slide",
                    propertyPath: "data.style.borderWidth",
                    label: t("props.borderWidth"),
                    controlType: "number",
                    min: 0,
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.style.borderWidth"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.borderWidth")
                  }
                >
                  <label className="qe-inline-input">
                    <span className="qe-inline-input__label">
                      {t("props.borderWidthLabel")}
                    </span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      {...createNumberInputHandlers(
                        videoStyle.borderWidth,
                        0,
                        null,
                        (value) => updateVideoStyle({ borderWidth: value })
                      )}
                    />
                  </label>
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "slide",
                    propertyPath: "data.style.borderRadius",
                    label: t("props.borderRadius"),
                    controlType: "number",
                    min: 0,
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.style.borderRadius"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.borderRadius")
                  }
                >
                  <label className="qe-inline-input">
                    <span className="qe-inline-input__label">
                      {t("props.cornerRoundness")}
                    </span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      {...createNumberInputHandlers(
                        videoStyle.borderRadius,
                        0,
                        null,
                        (value) => updateVideoStyle({ borderRadius: value })
                      )}
                    />
                  </label>
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "slide",
                    propertyPath: "data.style.marginX",
                    label: t("props.horizontalMargin"),
                    controlType: "number",
                    min: 0,
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.style.marginX"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.marginX")
                  }
                >
                  <label className="qe-inline-input">
                    <span className="qe-inline-input__label">
                      {t("props.horizontalOuterMargin")}
                    </span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      {...createNumberInputHandlers(
                        videoStyle.marginX,
                        0,
                        null,
                        (value) => updateVideoStyle({ marginX: value })
                      )}
                    />
                  </label>
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "slide",
                    propertyPath: "data.style.marginY",
                    label: t("props.verticalMargin"),
                    controlType: "number",
                    min: 0,
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.style.marginY"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.marginY")
                  }
                >
                  <label className="qe-inline-input">
                    <span className="qe-inline-input__label">
                      {t("props.verticalOuterMargin")}
                    </span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      {...createNumberInputHandlers(
                        videoStyle.marginY,
                        0,
                        null,
                        (value) => updateVideoStyle({ marginY: value })
                      )}
                    />
                  </label>
                </ShortcutableControl>
              </div>
            </AccordionContent>
          </div>
        ) : null}

        {nonNullElement.type === "push-to-talk" && pushToTalkAppearance ? (
          <div
            className={`qe-properties-section ${
              openSection === "push-to-talk-settings"
                ? "qe-properties-section--active"
                : ""
            }`}
          >
            <div
              className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                openSection === "push-to-talk-settings"
                  ? "qe-properties-section__title--open"
                  : "qe-properties-section__title--closed"
              }`}
              onClick={() =>
                setOpenSection(
                  openSection === "push-to-talk-settings"
                    ? null
                    : "push-to-talk-settings"
                )
              }
            >
              <span>{t("props.pushToTalkSettings")}</span>
              <svg
                className={`qe-properties-section__icon ${
                  openSection === "push-to-talk-settings"
                    ? "qe-properties-section__icon--open"
                    : ""
                }`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <AccordionContent
              isOpen={openSection === "push-to-talk-settings"}
            >
              <div
                className="qe-properties-section__content"
                style={{ padding: 0 }}
              >
                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.buttonDimensions")}</span>
                  <div className="qe-inline-group">
                    <label className="qe-inline-input">
                      <span className="qe-inline-input__label">{t("props.buttonSizePx")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={44}
                        {...createNumberInputHandlers(
                          pushToTalkAppearance.buttonSizePx ?? 72,
                          44,
                          200,
                          (value) =>
                            updatePushToTalkAppearance({ buttonSizePx: value })
                        )}
                      />
                    </label>
                    <label className="qe-inline-input">
                      <span className="qe-inline-input__label">
                        {t("props.contentPx")}
                      </span>
                      <input
                        className="qe-input"
                        type="number"
                        min={16}
                        {...createNumberInputHandlers(
                          pushToTalkAppearance.buttonContentSizePx ?? 28,
                          16,
                          null,
                          (value) =>
                            updatePushToTalkAppearance({
                              buttonContentSizePx: value,
                            })
                        )}
                      />
                    </label>
                    <label className="qe-inline-input">
                      <span className="qe-inline-input__label">
                        Metin (px)
                      </span>
                      <input
                        className="qe-input"
                        type="number"
                        min={10}
                        {...createNumberInputHandlers(
                          pushToTalkAppearance.buttonTextFontSize ?? 14,
                          10,
                          36,
                          (value) =>
                            updatePushToTalkAppearance({
                              buttonTextFontSize: value,
                            })
                        )}
                      />
                    </label>
                  </div>
                </div>

                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.canvasStatus")}</span>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className={`qe-button qe-button--text ${
                        (pushToTalkAppearance.editorPreviewStatus || "idle") ===
                        "idle"
                          ? "qe-button--primary"
                          : ""
                      }`}
                      onClick={() =>
                        updatePushToTalkAppearance({ editorPreviewStatus: "idle" })
                      }
                    >
                      Beklemede
                    </button>
                    <button
                      type="button"
                      className={`qe-button qe-button--text ${
                        (pushToTalkAppearance.editorPreviewStatus || "idle") ===
                        "listening"
                          ? "qe-button--primary"
                          : ""
                      }`}
                      onClick={() =>
                        updatePushToTalkAppearance({
                          editorPreviewStatus: "listening",
                        })
                      }
                    >
                      Dinlerken
                    </button>
                    <button
                      type="button"
                      className={`qe-button qe-button--text ${
                        (pushToTalkAppearance.editorPreviewStatus || "idle") ===
                        "error"
                          ? "qe-button--primary"
                          : ""
                      }`}
                      onClick={() =>
                        updatePushToTalkAppearance({
                          editorPreviewStatus: "error",
                        })
                      }
                    >
                      Hata
                    </button>
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
                    {t("props.canvasStatusHelp")}
                  </div>
                  {(pushToTalkAppearance.editorPreviewStatus || "idle") ===
                    "error" && (
                    <>
                      <div
                        style={{
                          marginTop: 10,
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            fontSize: "14px",
                            color: "#334155",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={pushToTalkAppearance.showMicButton ?? true}
                            onChange={(event) =>
                              updatePushToTalkAppearance({
                                showMicButton: event.target.checked,
                              })
                            }
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                              margin: 0,
                            }}
                          />
                          <span>{t("props.micButton")}</span>
                        </label>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            fontSize: "14px",
                            color: "#334155",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={pushToTalkAppearance.showErrorAlert ?? true}
                            onChange={(event) =>
                              updatePushToTalkAppearance({
                                showErrorAlert: event.target.checked,
                              })
                            }
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                              margin: 0,
                            }}
                          />
                          <span>{t("props.errorAlert")}</span>
                        </label>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            fontSize: "14px",
                            color: "#334155",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={pushToTalkAppearance.showManualInput ?? true}
                            onChange={(event) =>
                              updatePushToTalkAppearance({
                                showManualInput: event.target.checked,
                              })
                            }
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                              margin: 0,
                            }}
                          />
                          <span>{t("props.textArea")}</span>
                        </label>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            fontSize: "14px",
                            color: "#334155",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={pushToTalkAppearance.showManualInputLabel ?? true}
                            onChange={(event) =>
                              updatePushToTalkAppearance({
                                showManualInputLabel: event.target.checked,
                              })
                            }
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                              margin: 0,
                            }}
                          />
                          <span>{t("props.textAreaTitle")}</span>
                        </label>
                      </div>
                      <div className="qe-inline-group" style={{ marginTop: 10 }}>
                        <label
                          className="qe-inline-input"
                          style={{ flex: 1, minWidth: 240 }}
                        >
                          <span className="qe-inline-input__label">
                            {t("props.errorMessagePreview")}
                          </span>
                          <input
                            className="qe-input"
                            type="text"
                            value={pushToTalkAppearance.errorPreviewMessage || ""}
                            placeholder={t("props.errorMessagePlaceholder")}
                            onChange={(event) =>
                              updatePushToTalkAppearance({
                                errorPreviewMessage: event.target.value,
                              })
                            }
                          />
                        </label>
                      </div>
                      <div className="qe-inline-group">
                        <label className="qe-inline-input" style={{ flex: 1, minWidth: 240 }}>
                          <span className="qe-inline-input__label">
                            {t("props.textAreaLabel")}
                          </span>
                          <input
                            className="qe-input"
                            type="text"
                            value={pushToTalkAppearance.transcriptionLabel || ""}
                            placeholder={t("props.textAreaLabelPlaceholder")}
                            onChange={(event) =>
                              updatePushToTalkAppearance({
                                transcriptionLabel: event.target.value,
                              })
                            }
                          />
                        </label>
                      </div>
                      <div className="qe-inline-group">
                        <label className="qe-inline-input" style={{ flex: 1, minWidth: 240 }}>
                          <span className="qe-inline-input__label">
                            {t("props.textAreaPlaceholder")}
                          </span>
                          <input
                            className="qe-input"
                            type="text"
                            value={pushToTalkAppearance.transcriptionPlaceholder}
                            onChange={(event) =>
                              updatePushToTalkAppearance({
                                transcriptionPlaceholder: event.target.value,
                              })
                            }
                          />
                        </label>
                      </div>
                      <div className="qe-inline-group">
                        <label className="qe-inline-input">
                          <span className="qe-inline-input__label">
                            Hata Zemin
                          </span>
                          <input
                            className="qe-input qe-input--color"
                            type="color"
                            value={
                              pushToTalkAppearance.errorBackgroundColor || "#fef2f2"
                            }
                            onChange={(event) =>
                              updatePushToTalkAppearance({
                                errorBackgroundColor: event.target.value,
                              })
                            }
                          />
                        </label>
                        <label className="qe-inline-input">
                          <span className="qe-inline-input__label">
                            {t("props.errorBorder")}
                          </span>
                          <input
                            className="qe-input qe-input--color"
                            type="color"
                            value={pushToTalkAppearance.errorBorderColor || "#fecaca"}
                            onChange={(event) =>
                              updatePushToTalkAppearance({
                                errorBorderColor: event.target.value,
                              })
                            }
                          />
                        </label>
                        <label className="qe-inline-input">
                          <span className="qe-inline-input__label">{t("props.errorText")}</span>
                          <input
                            className="qe-input qe-input--color"
                            type="color"
                            value={pushToTalkAppearance.errorTextColor || "#dc2626"}
                            onChange={(event) =>
                              updatePushToTalkAppearance({
                                errorTextColor: event.target.value,
                              })
                            }
                          />
                        </label>
                      </div>
                    </>
                  )}
                </div>

                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.buttonShape")}</span>
                  <div className="qe-inline-group">
                    <label className="qe-inline-input">
                      <span className="qe-inline-input__label">{t("props.shape")}</span>
                      <select
                        className="qe-input"
                        value={pushToTalkAppearance.buttonShape || "circle"}
                        onChange={(event) => {
                          const nextShape = event.target.value as
                            | "circle"
                            | "square";
                          updatePushToTalkAppearance({
                            buttonShape: nextShape,
                            buttonCornerRadiusPx: nextShape === "circle" ? 999 : 12,
                          });
                        }}
                      >
                        <option value="circle">{t("props.shapeCircle")}</option>
                        <option value="square">{t("props.shapeSquare")}</option>
                      </select>
                    </label>
                  </div>
                  <div className="qe-inline-group">
                    <label className="qe-inline-input">
                      <span className="qe-inline-input__label">{t("props.roundness")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        {...createNumberInputHandlers(
                          pushToTalkAppearance.buttonCornerRadiusPx ?? 999,
                          0,
                          999,
                          (value) =>
                            updatePushToTalkAppearance({
                              buttonCornerRadiusPx: value,
                            })
                        )}
                      />
                    </label>
                    <label className="qe-inline-input">
                      <span className="qe-inline-input__label">{t("props.padding")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        {...createNumberInputHandlers(
                          pushToTalkAppearance.buttonPaddingPx ?? 0,
                          0,
                          24,
                          (value) =>
                            updatePushToTalkAppearance({
                              buttonPaddingPx: value,
                            })
                        )}
                      />
                    </label>
                  </div>
                  <div className="qe-inline-group" style={{ marginTop: 8 }}>
                    <label className="qe-inline-input">
                      <span className="qe-inline-input__label">
                        {t("props.borderWidth")}
                      </span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        {...createNumberInputHandlers(
                          pushToTalkAppearance.buttonBorderWidthPx ?? 0,
                          0,
                          12,
                          (value) =>
                            updatePushToTalkAppearance({
                              buttonBorderWidthPx: value,
                            })
                        )}
                      />
                    </label>
                    {renderColorControl(
                      t("props.borderColor"),
                      pushToTalkAppearance.buttonBorderColor || "transparent",
                      (color) =>
                        updatePushToTalkAppearance({
                          buttonBorderColor: color,
                        }),
                      { allowTransparent: true }
                    )}
                  </div>
                </div>

                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.buttonColors")}</span>
                  <div className="qe-inline-group">
                    {renderColorControl(
                      t("props.waiting"),
                      pushToTalkAppearance.buttonIdleColor,
                      (color) =>
                        updatePushToTalkAppearance({ buttonIdleColor: color }),
                      { allowTransparent: false }
                    )}
                    {renderColorControl(
                      t("props.listeningColor"),
                      pushToTalkAppearance.buttonListeningColor,
                      (color) =>
                        updatePushToTalkAppearance({
                          buttonListeningColor: color,
                        }),
                      { allowTransparent: false }
                    )}
                  </div>
                </div>

                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.buttonTextColors")}</span>
                  <div className="qe-inline-group">
                    {renderColorControl(
                      t("props.waiting"),
                      pushToTalkAppearance.buttonIdleTextColor,
                      (color) =>
                        updatePushToTalkAppearance({
                          buttonIdleTextColor: color,
                        }),
                      { allowTransparent: false }
                    )}
                    {renderColorControl(
                      t("props.listeningColor"),
                      pushToTalkAppearance.buttonListeningTextColor,
                      (color) =>
                        updatePushToTalkAppearance({
                          buttonListeningTextColor: color,
                        }),
                      { allowTransparent: false }
                    )}
                  </div>
                </div>

                <div className="qe-form__field">
                  {renderColorControl(
                    t("props.waveColor"),
                    pushToTalkAppearance.waveformColor,
                    (color) =>
                      updatePushToTalkAppearance({ waveformColor: color }),
                    { allowTransparent: false }
                  )}
                </div>

                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.buttonContent")}</span>
                  <div className="qe-inline-group">
                    <label className="qe-inline-input">
                      <span className="qe-inline-input__label">{t("props.contentTypeLabel")}</span>
                      <select
                        className="qe-input"
                        value={pushToTalkAppearance.buttonContentType || "icon"}
                        onChange={(event) => {
                          const nextType = event.target.value as
                            | "icon"
                            | "text"
                            | "image";
                          updatePushToTalkAppearance({
                            buttonContentType: nextType,
                            buttonImageUrl:
                              nextType === "image"
                                ? pushToTalkAppearance.buttonImageUrl || ""
                                : "",
                          });
                        }}
                      >
                        <option value="icon">{t("props.iconDefault")}</option>
                        <option value="image">{t("props.visualMedia")}</option>
                        <option value="text">{t("props.contentTypeText")}</option>
                      </select>
                    </label>
                  </div>

                  {pushToTalkAppearance.buttonContentType === "text" && (
                    <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                      <div
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          padding: 10,
                          background: "#ffffff",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#334155",
                            marginBottom: 8,
                          }}
                        >
                          {t("props.waitingState")}
                        </div>
                        <label className="qe-inline-input" style={{ flex: 1 }}>
                          <span className="qe-inline-input__label">
                            {t("props.idleButtonText")}
                          </span>
                          <input
                            className="qe-input"
                            type="text"
                            placeholder={t("props.exampleStart")}
                            value={pushToTalkAppearance.idleButtonText || ""}
                            onChange={(event) =>
                              updatePushToTalkAppearance({
                                idleButtonText: event.target.value,
                              })
                            }
                          />
                        </label>
                      </div>

                      <div
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          padding: 10,
                          background: "#ffffff",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#334155",
                            marginBottom: 8,
                          }}
                        >
                          {t("props.listeningState")}
                        </div>
                        <label className="qe-inline-input" style={{ flex: 1 }}>
                          <span className="qe-inline-input__label">
                            {t("props.idleButtonText")}
                          </span>
                          <input
                            className="qe-input"
                            type="text"
                            placeholder={t("props.exampleStop")}
                            value={pushToTalkAppearance.listeningButtonText || ""}
                            onChange={(event) =>
                              updatePushToTalkAppearance({
                                listeningButtonText: event.target.value,
                              })
                            }
                          />
                        </label>
                      </div>
                    </div>
                  )}

                  {pushToTalkAppearance.buttonContentType === "image" && (
                    <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "64px 1fr",
                          gap: 10,
                          alignItems: "center",
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          padding: 10,
                          background: "#ffffff",
                        }}
                      >
                        <div
                          style={{
                            width: 64,
                            height: 64,
                            borderRadius: 12,
                            border: "1px dashed #cbd5e1",
                            background: "#f8fafc",
                            display: "grid",
                            placeItems: "center",
                            overflow: "hidden",
                          }}
                        >
                          {(
                            pushToTalkAppearance.buttonImageUrlIdle ||
                            pushToTalkAppearance.buttonImageUrl
                          ) ? (
                            <img
                              src={
                                pushToTalkAppearance.buttonImageUrlIdle ||
                                pushToTalkAppearance.buttonImageUrl ||
                                ""
                              }
                              alt=""
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                display: "block",
                              }}
                            />
                          ) : (
                            <span style={{ fontSize: 11, color: "#64748b" }}>
                              {t("props.empty")}
                            </span>
                          )}
                        </div>
                        <div style={{ display: "grid", gap: 8 }}>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#334155",
                            }}
                          >
                            Beklemede
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button
                              type="button"
                              className="qe-button"
                              onClick={() => {
                                setMediaLibraryContext({
                                  type: "image",
                                  currentUrl:
                                    pushToTalkAppearance.buttonImageUrlIdle ||
                                    pushToTalkAppearance.buttonImageUrl ||
                                    "",
                                  onSelect: (url: string) => {
                                    updatePushToTalkAppearance({
                                      buttonImageUrl: url,
                                      buttonImageUrlIdle: url,
                                    });
                                  },
                                });
                                setIsMediaLibraryOpen(true);
                              }}
                            >
                              {t("props.selectVisual")}
                            </button>
                            {(pushToTalkAppearance.buttonImageUrlIdle ||
                              pushToTalkAppearance.buttonImageUrl) && (
                              <button
                                type="button"
                                className="qe-button qe-button--text"
                                onClick={() =>
                                  updatePushToTalkAppearance({
                                    buttonImageUrlIdle: "",
                                    buttonImageUrl:
                                      pushToTalkAppearance.buttonImageUrlListening
                                        ? pushToTalkAppearance.buttonImageUrl
                                        : "",
                                  })
                                }
                              >
                                {t("props.remove")}
                              </button>
                            )}
                            {(pushToTalkAppearance.buttonImageUrlIdle ||
                              pushToTalkAppearance.buttonImageUrl) && (
                              <button
                                type="button"
                                className="qe-button qe-button--text"
                                onClick={() =>
                                  setImagePreview({
                                    url:
                                      pushToTalkAppearance.buttonImageUrlIdle ||
                                      pushToTalkAppearance.buttonImageUrl ||
                                      "",
                                    name: "push-to-talk-idle",
                                  })
                                }
                              >
                                {t("props.preview")}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "64px 1fr",
                          gap: 10,
                          alignItems: "center",
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          padding: 10,
                          background: "#ffffff",
                        }}
                      >
                        <div
                          style={{
                            width: 64,
                            height: 64,
                            borderRadius: 12,
                            border: "1px dashed #cbd5e1",
                            background: "#f8fafc",
                            display: "grid",
                            placeItems: "center",
                            overflow: "hidden",
                          }}
                        >
                          {(
                            pushToTalkAppearance.buttonImageUrlListening ||
                            pushToTalkAppearance.buttonImageUrl
                          ) ? (
                            <img
                              src={
                                pushToTalkAppearance.buttonImageUrlListening ||
                                pushToTalkAppearance.buttonImageUrl ||
                                ""
                              }
                              alt=""
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                display: "block",
                              }}
                            />
                          ) : (
                            <span style={{ fontSize: 11, color: "#64748b" }}>
                              {t("props.empty")}
                            </span>
                          )}
                        </div>
                        <div style={{ display: "grid", gap: 8 }}>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#334155",
                            }}
                          >
                            Dinlerken
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button
                              type="button"
                              className="qe-button"
                              onClick={() => {
                                setMediaLibraryContext({
                                  type: "image",
                                  currentUrl:
                                    pushToTalkAppearance.buttonImageUrlListening ||
                                    pushToTalkAppearance.buttonImageUrl ||
                                    "",
                                  onSelect: (url: string) => {
                                    updatePushToTalkAppearance({
                                      buttonImageUrl: url,
                                      buttonImageUrlListening: url,
                                    });
                                  },
                                });
                                setIsMediaLibraryOpen(true);
                              }}
                            >
                              {t("props.selectVisual")}
                            </button>
                            {(pushToTalkAppearance.buttonImageUrlListening ||
                              pushToTalkAppearance.buttonImageUrl) && (
                              <button
                                type="button"
                                className="qe-button qe-button--text"
                                onClick={() =>
                                  updatePushToTalkAppearance({
                                    buttonImageUrlListening: "",
                                    buttonImageUrl:
                                      pushToTalkAppearance.buttonImageUrlIdle
                                        ? pushToTalkAppearance.buttonImageUrl
                                        : "",
                                  })
                                }
                              >
                                {t("props.remove")}
                              </button>
                            )}
                            {(pushToTalkAppearance.buttonImageUrlListening ||
                              pushToTalkAppearance.buttonImageUrl) && (
                              <button
                                type="button"
                                className="qe-button qe-button--text"
                                onClick={() =>
                                  setImagePreview({
                                    url:
                                      pushToTalkAppearance.buttonImageUrlListening ||
                                      pushToTalkAppearance.buttonImageUrl ||
                                      "",
                                    name: "push-to-talk-listening",
                                  })
                                }
                              >
                                {t("props.preview")}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {(pushToTalkAppearance.buttonImageUrl ||
                        pushToTalkAppearance.buttonImageUrlIdle ||
                        pushToTalkAppearance.buttonImageUrlListening) && (
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          <button
                            type="button"
                            className="qe-button qe-button--text"
                            onClick={() =>
                              updatePushToTalkAppearance({
                                buttonImageUrl: "",
                                buttonImageUrlIdle: "",
                                buttonImageUrlListening: "",
                              })
                            }
                          >
                            {t("props.clearAll")}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="qe-form__field">
                  <span className="qe-form__label">
                    {t("props.errorPlaceholderTitle")}
                  </span>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
                    {t("props.errorPlaceholderHelpNew")}
                  </div>
                </div>
              </div>
            </AccordionContent>
          </div>
        ) : null}

        {/* MathType Settings */}
        {isMathTypeElement && mathTypeStyle ? (
          <div
            className={`qe-properties-section ${
              openSection === "mathtype-settings"
                ? "qe-properties-section--active"
                : ""
            }`}
          >
            <div
              className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                openSection === "mathtype-settings"
                  ? "qe-properties-section__title--open"
                  : "qe-properties-section__title--closed"
              }`}
              onClick={() =>
                setOpenSection(
                  openSection === "mathtype-settings"
                    ? null
                    : "mathtype-settings"
                )
              }
            >
              <span>{t("props.mathSettings")}</span>
              <svg
                className={`qe-properties-section__icon ${
                  openSection === "mathtype-settings"
                    ? "qe-properties-section__icon--open"
                    : ""
                }`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <AccordionContent isOpen={openSection === "mathtype-settings"}>
              <div
                className="qe-properties-section__content"
                style={{ padding: 0 }}
              >
                <div className="qe-form__field">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "8px",
                    }}
                  >
                    <span className="qe-form__label">{t("props.mathmlFormula")}</span>
                    <button
                      className="qe-button qe-button--primary"
                      style={{
                        padding: "4px 12px",
                        fontSize: "12px",
                        height: "auto",
                      }}
                      onClick={() => {
                        setEditingMathOptionId(null); // null means editing the main element
                        setEditingMathOrderListItemId(null);
                        setIsMathEditorOpen(true);
                      }}
                      type="button"
                    >
                      {t("props.openEditor")}
                    </button>
                  </div>
                  <textarea
                    className="qe-input"
                    rows={3}
                    placeholder="<math><mi>x</mi><mo>=</mo><mfrac>...</mfrac></math>"
                    value={mathTypeStyle.mathml}
                    onChange={(event) =>
                      updateMathTypeStyle({ mathml: event.target.value })
                    }
                  />
                </div>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "mathtype",
                    propertyPath: "data.style.fontSize",
                    label: t("props.fontSize"),
                    controlType: "number",
                    step: 1,
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts("data.style.fontSize")}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.fontSize")
                  }
                >
                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.fontSize")}</span>
                    <input
                      className="qe-input"
                      type="number"
                      {...createNumberInputHandlers(
                        mathTypeStyle.fontSize,
                        null,
                        null,
                        (value) => updateMathTypeStyle({ fontSize: value })
                      )}
                    />
                  </div>
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "mathtype",
                    propertyPath: "data.style.color",
                    label: t("props.textColor"),
                    controlType: "color",
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts("data.style.color")}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.color")
                  }
                >
                  {renderColorControl(
                    t("props.textColor"),
                    mathTypeStyle.color,
                    (color) => updateMathTypeStyle({ color }),
                    { allowTransparent: false }
                  )}
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "mathtype",
                    propertyPath: "data.style.backgroundColor",
                    label: t("props.backgroundColor"),
                    controlType: "color",
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.style.backgroundColor"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.backgroundColor")
                  }
                >
                  {renderColorControl(
                    t("props.backgroundColor"),
                    mathTypeStyle.backgroundColor,
                    (color) => updateMathTypeStyle({ backgroundColor: color }),
                    { allowTransparent: true }
                  )}
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "mathtype",
                    propertyPath: "data.style.borderWidth",
                    label: t("props.borderWidth"),
                    controlType: "number",
                    min: 0,
                    max: 20,
                    step: 1,
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.style.borderWidth"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.borderWidth")
                  }
                >
                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.borderWidth")}</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      {...createNumberInputHandlers(
                        mathTypeStyle.borderWidth,
                        0,
                        20,
                        (value) => updateMathTypeStyle({ borderWidth: value })
                      )}
                    />
                  </div>
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "mathtype",
                    propertyPath: "data.style.borderColor",
                    label: t("props.borderColor"),
                    controlType: "color",
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.style.borderColor"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.borderColor")
                  }
                >
                  {renderColorControl(
                    t("props.borderColor"),
                    mathTypeStyle.borderColor,
                    (color) => updateMathTypeStyle({ borderColor: color }),
                    { allowTransparent: false }
                  )}
                </ShortcutableControl>

                <ShortcutableControl
                  shortcutConfig={{
                    elementType: "mathtype",
                    propertyPath: "data.style.borderRadius",
                    label: t("props.borderRadius"),
                    controlType: "number",
                    min: 0,
                    max: 100,
                    step: 1,
                  }}
                  onAddShortcut={onAddShortcut}
                  isInShortcuts={isPropertyInShortcuts(
                    "data.style.borderRadius"
                  )}
                  onRemoveShortcut={() =>
                    handleRemoveShortcutByPath("data.style.borderRadius")
                  }
                >
                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.borderRadius")}</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      {...createNumberInputHandlers(
                        mathTypeStyle.borderRadius,
                        0,
                        100,
                        (value) => updateMathTypeStyle({ borderRadius: value })
                      )}
                    />
                  </div>
                </ShortcutableControl>

                <div className="qe-inline-inputs">
                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "mathtype",
                      propertyPath: "data.style.paddingX",
                      label: t("props.horizontalPadding"),
                      controlType: "number",
                      min: 0,
                      max: 100,
                      step: 1,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts("data.style.paddingX")}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.style.paddingX")
                    }
                  >
                    <label className="qe-inline-input">
                      <span className="qe-inline-input__label">
                        Yatay Dolgu
                      </span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        {...createNumberInputHandlers(
                          mathTypeStyle.paddingX,
                          0,
                          null,
                          (value) => updateMathTypeStyle({ paddingX: value })
                        )}
                      />
                    </label>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "mathtype",
                      propertyPath: "data.style.paddingY",
                      label: t("props.verticalPadding"),
                      controlType: "number",
                      min: 0,
                      max: 100,
                      step: 1,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts("data.style.paddingY")}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.style.paddingY")
                    }
                  >
                    <label className="qe-inline-input">
                      <span className="qe-inline-input__label">
                        Dikey Dolgu
                      </span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        {...createNumberInputHandlers(
                          mathTypeStyle.paddingY,
                          0,
                          null,
                          (value) => updateMathTypeStyle({ paddingY: value })
                        )}
                      />
                    </label>
                  </ShortcutableControl>
                </div>
              </div>
            </AccordionContent>
          </div>
        ) : null}

        {/* Drag Source Settings */}
        {nonNullElement.type === "drag-source" && dragSourceAppearance ? (
          <div
            className={`qe-properties-section ${
              openSection === "drag-source-settings"
                ? "qe-properties-section--active"
                : ""
            }`}
          >
            <div
              className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                openSection === "drag-source-settings"
                  ? "qe-properties-section__title--open"
                  : "qe-properties-section__title--closed"
              }`}
              onClick={() =>
                setOpenSection(
                  openSection === "drag-source-settings"
                    ? null
                    : "drag-source-settings"
                )
              }
            >
              <span>{t("props.dragSourceSettings")}</span>
              <svg
                className={`qe-properties-section__icon ${
                  openSection === "drag-source-settings"
                    ? "qe-properties-section__icon--open"
                    : ""
                }`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <AccordionContent isOpen={openSection === "drag-source-settings"}>
              <div
                className="qe-properties-section__content"
                style={{ padding: 0 }}
              >
                <div className="qe-properties-subgroup">
                  <div className="qe-panel__subtitle">Temel Bilgiler</div>

                  <div className="qe-form__field">
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          elementData.showLabel !== false &&
                          elementData.showLabel !== 0 &&
                          elementData.showLabel !== "0"
                        }
                        onChange={(e) =>
                          updateElementData({
                            showLabel: e.target.checked ? "1" : "0",
                          })
                        }
                        style={{
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                        }}
                      />
                      <span className="qe-form__label" style={{ margin: 0 }}>
                        {t("props.showLabel")}
                      </span>
                    </label>
                    <input
                      className="qe-input"
                      type="text"
                      value={(elementData.label as string) || ""}
                      onChange={(e) =>
                        updateElementData({ label: e.target.value })
                      }
                      placeholder={t("props.elementLabel")}
                    />
                  </div>

                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.idReadOnly")}</span>
                    <input
                      className="qe-input"
                      type="text"
                      value={nonNullElement.id}
                      disabled
                      style={{
                        backgroundColor: "#f3f4f6",
                        cursor: "not-allowed",
                      }}
                    />
                    <small
                      style={{
                        display: "block",
                        marginTop: "4px",
                        color: "#6b7280",
                        fontSize: "12px",
                      }}
                    >
                      {t("props.idAutoAssigned")}
                    </small>
                  </div>
                </div>

                <div className="qe-properties-subgroup">
                  <div className="qe-panel__subtitle">{t("props.appearanceSettings")}</div>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drag-source",
                      propertyPath: "data.appearance.backgroundColor",
                      label: t("props.backgroundColor"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.backgroundColor"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.backgroundColor"
                      )
                    }
                  >
                    {renderColorControl(
                      t("props.backgroundColor"),
                      dragSourceAppearance.backgroundColor,
                      (nextColor) =>
                        updateDragSourceAppearance({
                          backgroundColor: nextColor,
                        })
                    )}
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drag-source",
                      propertyPath: "data.appearance.textColor",
                      label: t("props.textColor"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.textColor"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.textColor")
                    }
                  >
                    {renderColorControl(
                      t("props.textColor"),
                      dragSourceAppearance.textColor,
                      (nextColor) =>
                        updateDragSourceAppearance({ textColor: nextColor }),
                      { allowTransparent: false }
                    )}
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drag-source",
                      propertyPath: "data.appearance.borderColor",
                      label: t("props.borderColor"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.borderColor"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.borderColor")
                    }
                  >
                    {renderColorControl(
                      t("props.borderColor"),
                      dragSourceAppearance.borderColor,
                      (nextColor) =>
                        updateDragSourceAppearance({ borderColor: nextColor })
                    )}
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drag-source",
                      propertyPath: "data.appearance.borderWidth",
                      label: t("props.borderWidth"),
                      controlType: "number",
                      min: 0,
                      max: 20,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.borderWidth"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.borderWidth")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.borderWidth")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        max={20}
                        {...createNumberInputHandlers(
                          dragSourceAppearance.borderWidth,
                          0,
                          20,
                          (value) =>
                            updateDragSourceAppearance({ borderWidth: value })
                        )}
                      />
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drag-source",
                      propertyPath: "data.appearance.borderRadius",
                      label: t("props.borderRadius"),
                      controlType: "number",
                      min: 0,
                      max: 100,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.borderRadius"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.borderRadius")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.borderRadius")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        max={100}
                        {...createNumberInputHandlers(
                          dragSourceAppearance.borderRadius,
                          0,
                          100,
                          (value) =>
                            updateDragSourceAppearance({ borderRadius: value })
                        )}
                      />
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drag-source",
                      propertyPath: "data.appearance.paddingX",
                      label: t("props.horizontalPadding"),
                      controlType: "number",
                      min: 0,
                      max: 100,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.paddingX"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.paddingX")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.horizontalPadding")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        max={100}
                        {...createNumberInputHandlers(
                          dragSourceAppearance.paddingX,
                          0,
                          100,
                          (value) =>
                            updateDragSourceAppearance({ paddingX: value })
                        )}
                      />
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drag-source",
                      propertyPath: "data.appearance.paddingY",
                      label: t("props.verticalPadding"),
                      controlType: "number",
                      min: 0,
                      max: 100,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.paddingY"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.paddingY")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.verticalPadding")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        max={100}
                        {...createNumberInputHandlers(
                          dragSourceAppearance.paddingY,
                          0,
                          100,
                          (value) =>
                            updateDragSourceAppearance({ paddingY: value })
                        )}
                      />
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drag-source",
                      propertyPath: "data.appearance.labelPosition",
                      label: t("props.labelPosition"),
                      controlType: "select",
                      options: [
                        { value: "top-left", label: t("props.topLeft") },
                        { value: "top-center", label: t("props.topCenter") },
                        { value: "top-right", label: t("props.topRight") },
                        { value: "center", label: t("props.center") },
                        { value: "bottom-left", label: t("props.posBottomLeft") },
                        { value: "bottom-center", label: t("props.posBottom") },
                        { value: "bottom-right", label: t("props.posBottomRight") },
                      ],
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.labelPosition"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.labelPosition"
                      )
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.labelPosition")}</span>
                      <select
                        className="qe-input"
                        value={dragSourceAppearance.labelPosition}
                        onChange={(e) =>
                          updateDragSourceAppearance({
                            labelPosition: e.target.value as
                              | "top-left"
                              | "top-center"
                              | "top-right"
                              | "center"
                              | "bottom-left"
                              | "bottom-center"
                              | "bottom-right",
                          })
                        }
                      >
                        <option value="top-left">{t("props.topLeft")}</option>
                        <option value="top-center">{t("props.topCenter")}</option>
                        <option value="top-right">{t("props.topRight")}</option>
                        <option value="center">{t("props.center")}</option>
                        <option value="bottom-left">{t("props.posBottomLeft")}</option>
                        <option value="bottom-center">{t("props.posBottom")}</option>
                        <option value="bottom-right">{t("props.posBottomRight")}</option>
                      </select>
                    </div>
                  </ShortcutableControl>
                </div>

                <div className="qe-properties-subgroup">
                  <div className="qe-panel__subtitle">{t("props.visualContent")}</div>

                  <div className="qe-form__field">
                    <label className="qe-form__checkbox-label">
                      <input
                        type="checkbox"
                        checked={dragSourceAppearance.useImage}
                        onChange={(e) =>
                          updateDragSourceAppearance({
                            useImage: e.target.checked,
                            // useImage false yapıldığında imageUrl'yi temizle
                            ...(e.target.checked
                              ? {}
                              : { imageUrl: "", imageAlt: "" }),
                          })
                        }
                      />
                      <span className="qe-form__checkbox-text">
                        {t("props.showAsImage")}
                      </span>
                    </label>
                  </div>

                  {/* Geometrik şekil modu */}
                  <div className="qe-form__field">
                    <label className="qe-form__checkbox-label">
                      <input
                        type="checkbox"
                        checked={dragSourceAppearance.useShape === true}
                        disabled={dragSourceAppearance.useImage === true}
                        onChange={(e) =>
                          updateDragSourceAppearance({
                            useShape: e.target.checked,
                          })
                        }
                      />
                      <span className="qe-form__checkbox-text">
                        {t("props.showAsShape")}
                      </span>
                    </label>
                    {dragSourceAppearance.useImage === true ? (
                      <small style={{ color: "#94a3b8", fontSize: 11 }}>
                        Resim modu açıkken şekil modu devre dışı.
                      </small>
                    ) : null}
                  </div>

                  {dragSourceAppearance.useShape === true &&
                  dragSourceAppearance.useImage !== true ? (
                    <div className="qe-properties-callout">
                      <span className="qe-properties-callout__title">
                        🔷 Şekil ayarları
                      </span>
                      <div className="qe-form__field">
                        <span className="qe-form__label">Şekil</span>
                        <select
                          className="qe-input"
                          value={dragSourceAppearance.shapeKind ?? "circle"}
                          onChange={(e) =>
                            updateDragSourceAppearance({
                              shapeKind: e.target.value,
                            })
                          }
                        >
                          <option value="circle">Daire</option>
                          <option value="square">Kare</option>
                          <option value="rectangle">Dikdörtgen</option>
                          <option value="ellipse">Elips</option>
                          <option value="triangle">Üçgen</option>
                          <option value="right-triangle">Dik Üçgen</option>
                          <option value="pentagon">Beşgen</option>
                          <option value="hexagon">Altıgen</option>
                          <option value="octagon">Sekizgen</option>
                          <option value="star">Yıldız</option>
                          <option value="rhombus">Eşkenar Dörtgen</option>
                          <option value="trapezoid">Yamuk</option>
                          <option value="parallelogram">Paralelkenar</option>
                          <option value="heart">Kalp</option>
                          <option value="crescent">Hilal</option>
                          <option value="arrow">Ok</option>
                        </select>
                      </div>
                      {renderColorControl(
                        "Dolgu Rengi",
                        dragSourceAppearance.shapeFill ??
                          dragSourceAppearance.backgroundColor,
                        (next) =>
                          updateDragSourceAppearance({ shapeFill: next })
                      )}
                      {renderColorControl(
                        "Kenar Rengi",
                        dragSourceAppearance.shapeStroke ??
                          dragSourceAppearance.borderColor,
                        (next) =>
                          updateDragSourceAppearance({ shapeStroke: next })
                      )}
                      <div className="qe-form__field">
                        <span className="qe-form__label">
                          Kenar Kalınlığı (px)
                        </span>
                        <input
                          className="qe-input"
                          type="number"
                          min={0}
                          max={20}
                          value={
                            dragSourceAppearance.shapeStrokeWidth ??
                            dragSourceAppearance.borderWidth
                          }
                          onChange={(e) =>
                            updateDragSourceAppearance({
                              shapeStrokeWidth: Math.max(
                                0,
                                Math.min(20, Number(e.target.value) || 0)
                              ),
                            })
                          }
                        />
                      </div>
                    </div>
                  ) : null}

                  {dragSourceAppearance.useImage ? (
                    <div className="qe-properties-callout">
                      <span className="qe-properties-callout__title">
                        {t("props.visualContent")}
                      </span>

                      <div className="qe-form__field">
                        <span className="qe-form__label">{t("props.image")}</span>
                        <button
                          type="button"
                          className={`qe-media-library-button ${
                            dragSourceAppearance.imageUrl
                              ? "qe-media-library-button--has-value"
                              : ""
                          }`}
                          onClick={() => {
                            setMediaLibraryContext({
                              type: "drag-source",
                              onSelect: (url) =>
                                updateDragSourceAppearance({ imageUrl: url }),
                              currentUrl: dragSourceAppearance.imageUrl,
                            });
                            setIsMediaLibraryOpen(true);
                          }}
                        >
                          <svg
                            className="qe-media-library-button__icon"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M14 2H2C1.44772 2 1 2.44772 1 3V13C1 13.5523 1.44772 14 2 14H14C14.5523 14 15 13.5523 15 13V3C15 2.44772 14.5523 2 14 2Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M5.5 7C6.32843 7 7 6.32843 7 5.5C7 4.67157 6.32843 4 5.5 4C4.67157 4 4 4.67157 4 5.5C4 6.32843 4.67157 7 5.5 7Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M15 10L11 6L2 15"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          {dragSourceAppearance.imageUrl
                            ? t("props.imageSelected")
                            : t("props.selectFromMediaLibrary")}
                        </button>
                        {dragSourceAppearance.imageUrl && (
                          <div
                            style={{
                              marginTop: "8px",
                              fontSize: "12px",
                              color: "var(--qe-text-muted)",
                            }}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setImagePreview({
                                  url: dragSourceAppearance.imageUrl,
                                  name: "Drag Source Resim",
                                })
                              }
                              style={{
                                color: "var(--qe-accent)",
                                textDecoration: "underline",
                                background: "none",
                                border: "none",
                                padding: 0,
                                cursor: "pointer",
                                fontSize: "12px",
                                fontFamily: "inherit",
                              }}
                            >
                              {t("props.viewCurrentImage")}
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="qe-form__field">
                        <span className="qe-form__label">{t("props.altText")}</span>
                        <input
                          className="qe-input"
                          type="text"
                          value={dragSourceAppearance.imageAlt}
                          onChange={(e) =>
                            updateDragSourceAppearance({
                              imageAlt: e.target.value,
                            })
                          }
                        />
                      </div>

                      <ShortcutableControl
                        shortcutConfig={{
                          elementType: "drag-source",
                          propertyPath: "data.appearance.imageObjectFit",
                          label: t("props.fitting"),
                          controlType: "select",
                          options: [
                            { value: "contain", label: t("props.objectFitContain") },
                            { value: "cover", label: t("props.objectFitCover") },
                            { value: "fill", label: t("props.objectFitFill") },
                            { value: "scale-down", label: t("props.objectFitScaleDown") },
                            { value: "none", label: t("props.objectFitNone") },
                          ],
                        }}
                        onAddShortcut={onAddShortcut}
                        isInShortcuts={isPropertyInShortcuts(
                          "data.appearance.imageObjectFit"
                        )}
                        onRemoveShortcut={() =>
                          handleRemoveShortcutByPath(
                            "data.appearance.imageObjectFit"
                          )
                        }
                      >
                        <div className="qe-form__field">
                          <span className="qe-form__label">{t("props.fitting")}</span>
                          <select
                            className="qe-input"
                            value={dragSourceAppearance.imageObjectFit}
                            onChange={(e) =>
                              updateDragSourceAppearance({
                                imageObjectFit: e.target
                                  .value as ObjectFitOption,
                              })
                            }
                          >
                            <option value="contain">{t("props.objectFitContain")}</option>
                            <option value="cover">{t("props.objectFitCover")}</option>
                            <option value="fill">{t("props.objectFitFill")}</option>
                            <option value="scale-down">{t("props.objectFitScaleDown")}</option>
                            <option value="none">{t("props.objectFitNone")}</option>
                          </select>
                        </div>
                      </ShortcutableControl>
                    </div>
                  ) : null}
                </div>

                <div className="qe-properties-subgroup">
                  <div className="qe-panel__subtitle">{t("props.behaviorSettings")}</div>

                  <div className="qe-form__field">
                    <label className="qe-form__checkbox-label">
                      <input
                        type="checkbox"
                        checked={dragSourceAppearance?.clonable === true}
                        onChange={(e) =>
                          updateDragSourceAppearance({
                            clonable: e.target.checked,
                            ...(e.target.checked
                              ? {}
                              : { showNumbering: false }),
                          })
                        }
                      />
                      <span className="qe-form__checkbox-text">
                        {t("props.cloneable")}
                      </span>
                    </label>
                  </div>

                  {dragSourceAppearance?.clonable === true ? (
                    <>
                      <ShortcutableControl
                        shortcutConfig={{
                          elementType: "drag-source",
                          propertyPath: "data.appearance.maxClones",
                          label: t("props.maxCopyCount"),
                          controlType: "number",
                          min: 0,
                          step: 1,
                        }}
                        onAddShortcut={onAddShortcut}
                        isInShortcuts={isPropertyInShortcuts(
                          "data.appearance.maxClones"
                        )}
                        onRemoveShortcut={() =>
                          handleRemoveShortcutByPath(
                            "data.appearance.maxClones"
                          )
                        }
                      >
                        <div className="qe-form__field">
                          <span className="qe-form__label">
                            {t("props.maxCopyCount")}
                          </span>
                          <input
                            className="qe-input"
                            type="number"
                            min={0}
                            step={1}
                            {...createNumberInputHandlers(
                              Math.max(
                                0,
                                Math.floor(dragSourceAppearance.maxClones ?? 0)
                              ),
                              0,
                              null,
                              (value) =>
                                updateDragSourceAppearance({
                                  maxClones: Math.floor(value),
                                })
                            )}
                          />
                          <small
                            style={{
                              display: "block",
                              marginTop: "4px",
                              color: "#6b7280",
                              fontSize: "12px",
                            }}
                          >
                            {t("props.unlimitedCopiesHint")}
                          </small>
                        </div>
                      </ShortcutableControl>

                      <div className="qe-form__field">
                        <label className="qe-form__checkbox-label">
                          <input
                            type="checkbox"
                            checked={
                              dragSourceAppearance.showNumbering ?? false
                            }
                            onChange={(event) =>
                              updateDragSourceAppearance({
                                showNumbering: event.target.checked,
                              })
                            }
                          />
                          <span className="qe-form__checkbox-text">
                            {t("props.showNumberingInTarget")}
                          </span>
                        </label>
                      </div>
                      {dragSourceAppearance.showNumbering ? (
                        <>
                          <div className="qe-form__field">
                            <span className="qe-form__label">
                              Numara Font Boyutu
                            </span>
                            <input
                              className="qe-input"
                              type="number"
                              {...createNumberInputHandlers(
                                dragSourceAppearance.numberingFontSize ?? 11,
                                8,
                                36,
                                (value: number) =>
                                  updateDragSourceAppearance({
                                    numberingFontSize: value,
                                  })
                              )}
                            />
                          </div>
                          <div className="qe-form__field">
                            {renderColorControl(
                              t("props.numberColor"),
                              dragSourceAppearance.numberingTextColor ??
                                "#f9fafb",
                              (nextColor) =>
                                updateDragSourceAppearance({
                                  numberingTextColor: nextColor,
                                }),
                              { allowTransparent: false }
                            )}
                          </div>
                          <div className="qe-form__field">
                            {renderColorControl(
                              t("props.numberBackground"),
                              dragSourceAppearance.numberingBackgroundColor ??
                                "transparent",
                              (nextColor) =>
                                updateDragSourceAppearance({
                                  numberingBackgroundColor: nextColor,
                                }),
                              { allowTransparent: true }
                            )}
                          </div>
                          <div className="qe-form__field">
                            <span className="qe-form__label">
                              {t("props.borderRadius")}
                            </span>
                            <input
                              className="qe-input"
                              type="number"
                              {...createNumberInputHandlers(
                                dragSourceAppearance.numberingBorderRadius ??
                                  999,
                                0,
                                999,
                                (value: number) =>
                                  updateDragSourceAppearance({
                                    numberingBorderRadius: value,
                                  })
                              )}
                            />
                          </div>
                          <div className="qe-form__field">
                            <span className="qe-form__label">
                              Numara Konumu
                            </span>
                            <select
                              className="qe-input"
                              value={
                                dragSourceAppearance.numberingPosition ??
                                "top-left"
                              }
                              onChange={(event) =>
                                updateDragSourceAppearance({
                                  numberingPosition: event.target.value as
                                    | "top-left"
                                    | "top-right"
                                    | "bottom-right"
                                    | "bottom-left"
                                    | "center",
                                })
                              }
                            >
                              <option value="center">{t("props.center")}</option>
                              <option value="top-left">{t("props.topLeft")}</option>
                              <option value="top-right">{t("props.topRight")}</option>
                              <option value="bottom-right">{t("props.posBottomRight")}</option>
                              <option value="bottom-left">{t("props.posBottomLeft")}</option>
                            </select>
                          </div>
                        </>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </div>
            </AccordionContent>
          </div>
        ) : null}

        {/* Drop Target Settings */}
        {nonNullElement.type === "drop-target" && dropTargetAppearance ? (
          <div
            className={`qe-properties-section ${
              openSection === "drop-target-settings"
                ? "qe-properties-section--active"
                : ""
            }`}
          >
            <div
              className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                openSection === "drop-target-settings"
                  ? "qe-properties-section__title--open"
                  : "qe-properties-section__title--closed"
              }`}
              onClick={() =>
                setOpenSection(
                  openSection === "drop-target-settings"
                    ? null
                    : "drop-target-settings"
                )
              }
            >
              <span>{t("props.dropTargetSettings")}</span>
              <svg
                className={`qe-properties-section__icon ${
                  openSection === "drop-target-settings"
                    ? "qe-properties-section__icon--open"
                    : ""
                }`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <AccordionContent isOpen={openSection === "drop-target-settings"}>
              <div
                className="qe-properties-section__content"
                style={{ padding: 0 }}
              >
                <div className="qe-properties-subgroup">
                  <div className="qe-panel__subtitle">Temel Bilgiler</div>

                  <div className="qe-form__field">
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          elementData.showLabel !== false &&
                          elementData.showLabel !== 0 &&
                          elementData.showLabel !== "0"
                        }
                        onChange={(e) =>
                          updateElementData({
                            showLabel: e.target.checked ? "1" : "0",
                          })
                        }
                        style={{
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                        }}
                      />
                      <span className="qe-form__label" style={{ margin: 0 }}>
                        {t("props.showLabel")}
                      </span>
                    </label>
                    <input
                      className="qe-input"
                      type="text"
                      value={(elementData.label as string) || ""}
                      onChange={(e) =>
                        updateElementData({ label: e.target.value })
                      }
                      placeholder={t("props.dropTargetPlaceholderText")}
                    />
                  </div>

                  <div className="qe-form__field">
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          elementData.showPlaceholder !== false &&
                          elementData.showPlaceholder !== 0 &&
                          elementData.showPlaceholder !== "0"
                        }
                        onChange={(e) =>
                          updateElementData({
                            showPlaceholder: e.target.checked ? "1" : "0",
                          })
                        }
                        style={{
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                        }}
                      />
                      <span className="qe-form__label" style={{ margin: 0 }}>
                        {t("props.showEmptyMessage")}
                      </span>
                    </label>
                    {elementData.showPlaceholder !== false &&
                      elementData.showPlaceholder !== 0 &&
                      elementData.showPlaceholder !== "0" && (
                        <>
                          <input
                            className="qe-input"
                            type="text"
                            value={dropTargetPlaceholder ?? ""}
                            onChange={(e) =>
                              updateDropTargetPlaceholder(e.target.value)
                            }
                            placeholder={t("renderers.dropTarget.placeholder")}
                          />
                          <small
                            style={{
                              display: "block",
                              marginTop: "4px",
                              color: "#6b7280",
                              fontSize: "12px",
                            }}
                          >
                            {t("props.emptyMessageHint")}
                          </small>
                        </>
                      )}
                  </div>

                </div>

                {/* Izgara Düzeni — hedef alanı hücrelere böl; çizgiler her
                    köşeden eşit görünür. Kabul edilen öğeler ve maksimum
                    artık Cevap bölümünden yönetilir. */}
                <div className="qe-properties-subgroup">
                  <div className="qe-panel__subtitle">Izgara Düzeni</div>
                  <div className="qe-form__field">
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={dropTargetAppearance.useGrid === true}
                        onChange={(e) =>
                          updateDropTargetAppearance({
                            useGrid: e.target.checked,
                          })
                        }
                      />
                      <span className="qe-form__label" style={{ margin: 0 }}>
                        Izgara düzenini etkinleştir
                      </span>
                    </label>
                  </div>
                  {dropTargetAppearance.useGrid ? (
                    <>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 8,
                        }}
                      >
                        <div className="qe-form__field">
                          <span className="qe-form__label">Satır</span>
                          <input
                            className="qe-input"
                            type="number"
                            min={1}
                            max={10}
                            value={dropTargetAppearance.gridRows ?? 2}
                            onChange={(e) =>
                              updateDropTargetAppearance({
                                gridRows: Math.max(
                                  1,
                                  Math.min(10, Number(e.target.value) || 2)
                                ),
                              })
                            }
                          />
                        </div>
                        <div className="qe-form__field">
                          <span className="qe-form__label">Sütun</span>
                          <input
                            className="qe-input"
                            type="number"
                            min={1}
                            max={10}
                            value={dropTargetAppearance.gridCols ?? 3}
                            onChange={(e) =>
                              updateDropTargetAppearance({
                                gridCols: Math.max(
                                  1,
                                  Math.min(10, Number(e.target.value) || 3)
                                ),
                              })
                            }
                          />
                        </div>
                      </div>
                      <div className="qe-form__field">
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={
                              dropTargetAppearance.showGridLines !== false
                            }
                            onChange={(e) =>
                              updateDropTargetAppearance({
                                showGridLines: e.target.checked,
                              })
                            }
                          />
                          <span
                            className="qe-form__label"
                            style={{ margin: 0 }}
                          >
                            Izgara çizgilerini göster
                          </span>
                        </label>
                      </div>
                      {dropTargetAppearance.showGridLines !== false
                        ? renderColorControl(
                            "Izgara Çizgi Rengi",
                            dropTargetAppearance.gridLineColor ??
                              "rgba(15,23,42,0.08)",
                            (next) =>
                              updateDropTargetAppearance({ gridLineColor: next })
                          )
                        : null}
                    </>
                  ) : null}
                </div>

                <div className="qe-properties-subgroup">
                  <div className="qe-panel__subtitle">{t("props.appearanceSettings")}</div>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drop-target",
                      propertyPath: "data.appearance.backgroundColor",
                      label: t("props.backgroundColor"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.backgroundColor"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.backgroundColor"
                      )
                    }
                  >
                    {renderColorControl(
                      t("props.backgroundColor"),
                      dropTargetAppearance.backgroundColor,
                      (nextColor) =>
                        updateDropTargetAppearance({
                          backgroundColor: nextColor,
                        })
                    )}
                  </ShortcutableControl>
                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drop-target",
                      propertyPath: "data.appearance.borderColor",
                      label: t("props.borderColor"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.borderColor"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.borderColor")
                    }
                  >
                    {renderColorControl(
                      t("props.borderColor"),
                      dropTargetAppearance.borderColor,
                      (nextColor) =>
                        updateDropTargetAppearance({ borderColor: nextColor })
                    )}
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drop-target",
                      propertyPath: "data.appearance.borderWidth",
                      label: t("props.borderWidth"),
                      controlType: "number",
                      min: 0,
                      max: 20,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.borderWidth"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.borderWidth")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.borderWidth")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        max={20}
                        {...createNumberInputHandlers(
                          dropTargetAppearance.borderWidth,
                          0,
                          20,
                          (value) =>
                            updateDropTargetAppearance({ borderWidth: value })
                        )}
                      />
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drop-target",
                      propertyPath: "data.appearance.borderStyle",
                      label: t("props.borderStyle"),
                      controlType: "select",
                      options: [
                        { value: "solid", label: t("props.borderStyleSolid") },
                        { value: "dashed", label: t("props.borderStyleDashed") },
                        { value: "dotted", label: t("props.borderStyleDotted") },
                        { value: "none", label: t("props.none") },
                      ],
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.borderStyle"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.borderStyle")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.borderStyle")}</span>
                      <select
                        className="qe-input"
                        value={dropTargetAppearance.borderStyle}
                        onChange={(e) =>
                          updateDropTargetAppearance({
                            borderStyle: e.target.value as
                              | "solid"
                              | "dashed"
                              | "dotted"
                              | "none",
                          })
                        }
                      >
                        <option value="solid">{t("props.borderStyleSolid")}</option>
                        <option value="dashed">{t("props.borderStyleDashed")}</option>
                        <option value="dotted">{t("props.borderStyleDotted")}</option>
                        <option value="none">{t("props.none")}</option>
                      </select>
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drop-target",
                      propertyPath: "data.appearance.borderRadius",
                      label: t("props.borderRadius"),
                      controlType: "number",
                      min: 0,
                      max: 100,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.borderRadius"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.borderRadius")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.borderRadius")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        max={100}
                        {...createNumberInputHandlers(
                          dropTargetAppearance.borderRadius,
                          0,
                          100,
                          (value) =>
                            updateDropTargetAppearance({ borderRadius: value })
                        )}
                      />
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drop-target",
                      propertyPath: "data.appearance.paddingX",
                      label: t("props.horizontalPadding"),
                      controlType: "number",
                      min: 0,
                      max: 100,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.paddingX"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.paddingX")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.horizontalPadding")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        max={100}
                        {...createNumberInputHandlers(
                          dropTargetAppearance.paddingX,
                          0,
                          100,
                          (value) =>
                            updateDropTargetAppearance({ paddingX: value })
                        )}
                      />
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drop-target",
                      propertyPath: "data.appearance.paddingY",
                      label: t("props.verticalPadding"),
                      controlType: "number",
                      min: 0,
                      max: 100,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.paddingY"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.paddingY")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.verticalPadding")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        max={100}
                        {...createNumberInputHandlers(
                          dropTargetAppearance.paddingY,
                          0,
                          100,
                          (value) =>
                            updateDropTargetAppearance({ paddingY: value })
                        )}
                      />
                    </div>
                  </ShortcutableControl>
                </div>

                <div className="qe-properties-subgroup">
                  <div className="qe-panel__subtitle">{t("props.labelSettings")}</div>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drop-target",
                      propertyPath: "data.appearance.labelPosition",
                      label: t("props.labelPosition"),
                      controlType: "select",
                      options: [
                        { value: "top-left", label: t("props.topLeft") },
                        { value: "top-center", label: t("props.topCenter") },
                        { value: "top-right", label: t("props.topRight") },
                        { value: "center", label: t("props.center") },
                        { value: "bottom-left", label: t("props.posBottomLeft") },
                        { value: "bottom-center", label: t("props.posBottom") },
                        { value: "bottom-right", label: t("props.posBottomRight") },
                      ],
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.labelPosition"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.labelPosition"
                      )
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.labelPosition")}</span>
                      <select
                        className="qe-input"
                        value={dropTargetAppearance.labelPosition}
                        onChange={(e) =>
                          updateDropTargetAppearance({
                            labelPosition: e.target.value as
                              | "top-left"
                              | "top-center"
                              | "top-right"
                              | "center"
                              | "bottom-left"
                              | "bottom-center"
                              | "bottom-right",
                          })
                        }
                      >
                        <option value="top-left">{t("props.topLeft")}</option>
                        <option value="top-center">{t("props.topCenter")}</option>
                        <option value="top-right">{t("props.topRight")}</option>
                        <option value="center">{t("props.center")}</option>
                        <option value="bottom-left">{t("props.posBottomLeft")}</option>
                        <option value="bottom-center">{t("props.posBottom")}</option>
                        <option value="bottom-right">{t("props.posBottomRight")}</option>
                      </select>
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drop-target",
                      propertyPath: "data.appearance.labelTextColor",
                      label: t("props.labelTextColor"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.labelTextColor"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.labelTextColor"
                      )
                    }
                  >
                    {renderColorControl(
                      t("props.labelTextColor"),
                      dropTargetAppearance.labelTextColor,
                      (nextColor) =>
                        updateDropTargetAppearance({
                          labelTextColor: nextColor,
                        }),
                      { allowTransparent: false }
                    )}
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drop-target",
                      propertyPath: "data.appearance.labelBackground",
                      label: t("props.labelBackground"),
                      controlType: "color",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.labelBackground"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.labelBackground"
                      )
                    }
                  >
                    {renderColorControl(
                      t("props.labelBackground"),
                      dropTargetAppearance.labelBackground,
                      (nextColor) =>
                        updateDropTargetAppearance({
                          labelBackground: nextColor,
                        })
                    )}
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drop-target",
                      propertyPath: "data.appearance.labelPaddingX",
                      label: t("props.labelHorizontalPadding"),
                      controlType: "number",
                      min: 0,
                      max: 100,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.labelPaddingX"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.labelPaddingX"
                      )
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">
                        {t("props.labelHorizontalPadding")}
                      </span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        max={100}
                        {...createNumberInputHandlers(
                          dropTargetAppearance.labelPaddingX,
                          0,
                          100,
                          (value) =>
                            updateDropTargetAppearance({ labelPaddingX: value })
                        )}
                      />
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drop-target",
                      propertyPath: "data.appearance.labelPaddingY",
                      label: t("props.labelVerticalPadding"),
                      controlType: "number",
                      min: 0,
                      max: 100,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.labelPaddingY"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.labelPaddingY"
                      )
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">
                        {t("props.labelVerticalPadding")}
                      </span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        max={100}
                        {...createNumberInputHandlers(
                          dropTargetAppearance.labelPaddingY,
                          0,
                          100,
                          (value) =>
                            updateDropTargetAppearance({ labelPaddingY: value })
                        )}
                      />
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drop-target",
                      propertyPath: "data.appearance.labelBorderRadius",
                      label: t("props.labelCornerRadius"),
                      controlType: "number",
                      min: 0,
                      max: 100,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.labelBorderRadius"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.labelBorderRadius"
                      )
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">
                        {t("props.labelCornerRadius")}
                      </span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        max={100}
                        value={dropTargetAppearance.labelBorderRadius}
                        onChange={(e) =>
                          updateDropTargetAppearance({
                            labelBorderRadius: Math.max(
                              0,
                              Number(e.target.value) || 0
                            ),
                          })
                        }
                      />
                    </div>
                  </ShortcutableControl>
                </div>

                <div className="qe-properties-subgroup">
                  <div className="qe-panel__subtitle">{t("props.contentLayout")}</div>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drop-target",
                      propertyPath: "data.appearance.contentLayout",
                      label: t("props.contentPlacement"),
                      controlType: "select",
                      options: [
                        { value: "vertical", label: t("props.layoutVertical") },
                        { value: "horizontal", label: t("props.layoutHorizontal") },
                      ],
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.contentLayout"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.contentLayout"
                      )
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.contentPlacement")}</span>
                      <select
                        className="qe-input"
                        value={dropTargetAppearance.contentLayout}
                        onChange={(e) =>
                          updateDropTargetAppearance({
                            contentLayout: e.target.value as
                              | "vertical"
                              | "horizontal",
                          })
                        }
                      >
                        <option value="vertical">{t("props.layoutVertical")}</option>
                        <option value="horizontal">{t("props.layoutHorizontal")}</option>
                      </select>
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drop-target",
                      propertyPath: "data.appearance.contentAlignment",
                      label: t("props.contentAlignment"),
                      controlType: "select",
                      options: [
                        { value: "start", label: t("props.start") },
                        { value: "center", label: t("props.center") },
                        { value: "end", label: t("props.end") },
                      ],
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.contentAlignment"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.contentAlignment"
                      )
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.contentAlignment")}</span>
                      <select
                        className="qe-input"
                        value={dropTargetAppearance.contentAlignment}
                        onChange={(e) =>
                          updateDropTargetAppearance({
                            contentAlignment: e.target.value as
                              | "start"
                              | "center"
                              | "end",
                          })
                        }
                      >
                        <option value="start">{t("props.start")}</option>
                        <option value="center">{t("props.center")}</option>
                        <option value="end">{t("props.end")}</option>
                      </select>
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "drop-target",
                      propertyPath: "data.appearance.itemSpacing",
                      label: t("props.itemGap"),
                      controlType: "number",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.itemSpacing"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.itemSpacing"
                      )
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">
                        {t("props.itemSpacing")}
                      </span>
                      <input
                        className="qe-input"
                        type="number"
                        {...createNumberInputHandlers(
                          dropTargetAppearance.itemSpacing,
                          null,
                          null,
                          (value) =>
                            updateDropTargetAppearance({
                              itemSpacing: value,
                            })
                        )}
                      />
                    </div>
                  </ShortcutableControl>
                </div>

                <div className="qe-properties-subgroup">
                  <div className="qe-panel__subtitle">{t("props.backgroundImageLabel")}</div>

                  <div className="qe-form__field">
                    <label className="qe-properties-toggle">
                      <input
                        type="checkbox"
                        checked={dropTargetAppearance.useBackgroundImage}
                        onChange={(e) =>
                          updateDropTargetAppearance({
                            useBackgroundImage: e.target.checked,
                          })
                        }
                      />
                      <span>{t("props.enableBackgroundImage")}</span>
                    </label>
                  </div>

                  {dropTargetAppearance.useBackgroundImage ? (
                    <div className="qe-properties-callout">
                      <span className="qe-properties-callout__title">
                        Arkaplan resmi
                      </span>

                      <div className="qe-form__field">
                        <span className="qe-form__label">{t("props.image")}</span>
                        <button
                          type="button"
                          className={`qe-media-library-button ${
                            dropTargetAppearance.backgroundImageUrl
                              ? "qe-media-library-button--has-value"
                              : ""
                          }`}
                          onClick={() => {
                            setMediaLibraryContext({
                              type: "drop-target",
                              onSelect: (url) =>
                                updateDropTargetAppearance({
                                  backgroundImageUrl: url,
                                }),
                              currentUrl:
                                dropTargetAppearance.backgroundImageUrl,
                            });
                            setIsMediaLibraryOpen(true);
                          }}
                        >
                          <svg
                            className="qe-media-library-button__icon"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M14 2H2C1.44772 2 1 2.44772 1 3V13C1 13.5523 1.44772 14 2 14H14C14.5523 14 15 13.5523 15 13V3C15 2.44772 14.5523 2 14 2Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M5.5 7C6.32843 7 7 6.32843 7 5.5C7 4.67157 6.32843 4 5.5 4C4.67157 4 4 4.67157 4 5.5C4 6.32843 4.67157 7 5.5 7Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M15 10L11 6L2 15"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          {dropTargetAppearance.backgroundImageUrl
                            ? t("props.imageSelected")
                            : t("props.selectFromMediaLibrary")}
                        </button>
                        {dropTargetAppearance.backgroundImageUrl && (
                          <div
                            style={{
                              marginTop: "8px",
                              fontSize: "12px",
                              color: "var(--qe-text-muted)",
                            }}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setImagePreview({
                                  url: dropTargetAppearance.backgroundImageUrl,
                                  name: "Arka Plan Resim",
                                })
                              }
                              style={{
                                color: "var(--qe-accent)",
                                textDecoration: "underline",
                                background: "none",
                                border: "none",
                                padding: 0,
                                cursor: "pointer",
                                fontSize: "12px",
                                fontFamily: "inherit",
                              }}
                            >
                              {t("props.viewCurrentImage")}
                            </button>
                          </div>
                        )}
                      </div>

                      <ShortcutableControl
                        shortcutConfig={{
                          elementType: "drop-target",
                          propertyPath: "data.appearance.backgroundImageFit",
                          label: t("props.fitting"),
                          controlType: "select",
                          options: [
                            { value: "cover", label: t("props.objectFitCover") },
                            { value: "contain", label: t("props.objectFitContain") },
                            { value: "fill", label: t("props.objectFitFill") },
                            { value: "scale-down", label: t("props.objectFitScaleDown") },
                            { value: "none", label: t("props.objectFitNone") },
                          ],
                        }}
                        onAddShortcut={onAddShortcut}
                        isInShortcuts={isPropertyInShortcuts(
                          "data.appearance.backgroundImageFit"
                        )}
                        onRemoveShortcut={() =>
                          handleRemoveShortcutByPath(
                            "data.appearance.backgroundImageFit"
                          )
                        }
                      >
                        <div className="qe-form__field">
                          <span className="qe-form__label">{t("props.fitting")}</span>
                          <select
                            className="qe-input"
                            value={dropTargetAppearance.backgroundImageFit}
                            onChange={(e) =>
                              updateDropTargetAppearance({
                                backgroundImageFit: e.target
                                  .value as ObjectFitOption,
                              })
                            }
                          >
                            <option value="cover">{t("props.objectFitCover")}</option>
                            <option value="contain">{t("props.objectFitContain")}</option>
                            <option value="fill">{t("props.objectFitFill")}</option>
                            <option value="scale-down">{t("props.objectFitScaleDown")}</option>
                            <option value="none">{t("props.objectFitNone")}</option>
                          </select>
                        </div>
                      </ShortcutableControl>

                      <ShortcutableControl
                        shortcutConfig={{
                          elementType: "drop-target",
                          propertyPath:
                            "data.appearance.backgroundImagePosition",
                          label: t("props.position"),
                          controlType: "select",
                          options: [
                            { value: "center center", label: t("props.posCenter") },
                            { value: "top center", label: t("props.posTop") },
                            { value: "bottom center", label: t("props.posBottom") },
                            { value: "center left", label: t("props.left") },
                            { value: "center right", label: t("props.right") },
                            { value: "top left", label: t("props.posTopLeft") },
                            { value: "top right", label: t("props.posTopRight") },
                            { value: "bottom left", label: t("props.posBottomLeft") },
                            { value: "bottom right", label: t("props.posBottomRight") },
                          ],
                        }}
                        onAddShortcut={onAddShortcut}
                        isInShortcuts={isPropertyInShortcuts(
                          "data.appearance.backgroundImagePosition"
                        )}
                        onRemoveShortcut={() =>
                          handleRemoveShortcutByPath(
                            "data.appearance.backgroundImagePosition"
                          )
                        }
                      >
                        <div className="qe-form__field">
                          <span className="qe-form__label">Konum</span>
                          <select
                            className="qe-input"
                            value={dropTargetAppearance.backgroundImagePosition}
                            onChange={(e) =>
                              updateDropTargetAppearance({
                                backgroundImagePosition: e.target.value,
                              })
                            }
                          >
                            <option value="center center">{t("props.posCenter")}</option>
                            <option value="top center">{t("props.posTop")}</option>
                            <option value="bottom center">{t("props.posBottom")}</option>
                            <option value="center left">{t("props.left")}</option>
                            <option value="center right">{t("props.right")}</option>
                            <option value="top left">{t("props.posTopLeft")}</option>
                            <option value="top right">{t("props.posTopRight")}</option>
                            <option value="bottom left">{t("props.posBottomLeft")}</option>
                            <option value="bottom right">{t("props.posBottomRight")}</option>
                          </select>
                        </div>
                      </ShortcutableControl>

                      <ShortcutableControl
                        shortcutConfig={{
                          elementType: "drop-target",
                          propertyPath:
                            "data.appearance.backgroundImageOpacity",
                          label: t("props.opacity"),
                          controlType: "number",
                          min: 0,
                          max: 1,
                          step: 0.1,
                        }}
                        onAddShortcut={onAddShortcut}
                        isInShortcuts={isPropertyInShortcuts(
                          "data.appearance.backgroundImageOpacity"
                        )}
                        onRemoveShortcut={() =>
                          handleRemoveShortcutByPath(
                            "data.appearance.backgroundImageOpacity"
                          )
                        }
                      >
                        <div className="qe-form__field">
                          <span className="qe-form__label">{t("props.opacity")}</span>
                          <input
                            className="qe-input"
                            type="number"
                            min={0}
                            max={1}
                            step={0.1}
                            {...createNumberInputHandlers(
                              dropTargetAppearance.backgroundImageOpacity ?? 0,
                              0,
                              1,
                              (value) =>
                                updateDropTargetAppearance({
                                  backgroundImageOpacity: value,
                                })
                            )}
                          />
                        </div>
                      </ShortcutableControl>
                    </div>
                  ) : null}
                </div>

                {dropAreaConfig ? (
                  <div className="qe-properties-subgroup">
                    <div className="qe-panel__subtitle">{t("props.dropArea")}</div>

                    <label className="qe-form__checkbox-label">
                      <input
                        type="checkbox"
                        checked={dropAreaEnabled}
                        onChange={(e) =>
                          updateDropArea({ enabled: e.target.checked })
                        }
                      />
                      <span className="qe-form__checkbox-text">
                        {t("props.restrictDropArea")}
                      </span>
                    </label>

                    <small
                      style={{
                        color: "#6b7280",
                        fontSize: "12px",
                      }}
                    >
                      Tuval boyutu: {elementWidth || "?"}×{elementHeight || "?"}{" "}
                      px
                    </small>

                    {dropAreaEnabled ? (
                      <>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                            gap: "10px",
                          }}
                        >
                          <div className="qe-form__field" style={{ margin: 0 }}>
                            <span className="qe-form__label">X (px)</span>
                            <input
                              className="qe-input"
                              type="number"
                              min={0}
                              value={dropAreaConfig.x}
                              onChange={(e) => {
                                const raw = Number(e.target.value) || 0;
                                const normalized = Math.max(0, raw);
                                const maxX =
                                  elementWidth > 0
                                    ? Math.max(
                                        0,
                                        elementWidth - dropAreaConfig.width
                                      )
                                    : undefined;
                                updateDropArea({
                                  x:
                                    maxX !== undefined
                                      ? Math.min(normalized, maxX)
                                      : normalized,
                                });
                              }}
                            />
                          </div>

                          <div className="qe-form__field" style={{ margin: 0 }}>
                            <span className="qe-form__label">Y (px)</span>
                            <input
                              className="qe-input"
                              type="number"
                              min={0}
                              value={dropAreaConfig.y}
                              onChange={(e) => {
                                const raw = Number(e.target.value) || 0;
                                const normalized = Math.max(0, raw);
                                const maxY =
                                  elementHeight > 0
                                    ? Math.max(
                                        0,
                                        elementHeight - dropAreaConfig.height
                                      )
                                    : undefined;
                                updateDropArea({
                                  y:
                                    maxY !== undefined
                                      ? Math.min(normalized, maxY)
                                      : normalized,
                                });
                              }}
                            />
                          </div>

                          <div className="qe-form__field" style={{ margin: 0 }}>
                            <span className="qe-form__label">{t("props.width")}</span>
                            <input
                              className="qe-input"
                              type="number"
                              min={0}
                              value={dropAreaConfig.width}
                              onChange={(e) => {
                                const raw = Number(e.target.value) || 0;
                                const normalized = Math.max(0, raw);
                                const maxWidthValue =
                                  elementWidth > 0 ? elementWidth : normalized;
                                updateDropArea({
                                  width: Math.min(normalized, maxWidthValue),
                                });
                              }}
                            />
                          </div>

                          <div className="qe-form__field" style={{ margin: 0 }}>
                            <span className="qe-form__label">{t("props.height")}</span>
                            <input
                              className="qe-input"
                              type="number"
                              min={0}
                              value={dropAreaConfig.height}
                              onChange={(e) => {
                                const raw = Number(e.target.value) || 0;
                                const normalized = Math.max(0, raw);
                                const maxHeightValue =
                                  elementHeight > 0
                                    ? elementHeight
                                    : normalized;
                                updateDropArea({
                                  height: Math.min(normalized, maxHeightValue),
                                });
                              }}
                            />
                          </div>
                        </div>

                        <small
                          style={{
                            color: "#6b7280",
                            fontSize: "12px",
                          }}
                        >
                          {t("props.dropAreaCoordinatesHint")}
                        </small>

                        <label className="qe-form__checkbox-label">
                          <input
                            type="checkbox"
                            checked={
                              (dropAreaConfig.showOutline as any) === "1" ||
                              (dropAreaConfig.showOutline as any) === true ||
                              (dropAreaConfig.showOutline as any) === 1
                            }
                            onChange={(e) =>
                              updateDropArea({ showOutline: e.target.checked })
                            }
                          />
                          <span className="qe-form__checkbox-text">
                            {t("props.showDropAreaOutline")}
                          </span>
                        </label>

                        {(dropAreaConfig.showOutline as any) === "1" ||
                        (dropAreaConfig.showOutline as any) === true ||
                        (dropAreaConfig.showOutline as any) === 1 ? (
                          <>
                            <ShortcutableControl
                              shortcutConfig={{
                                elementType: "drop-target",
                                propertyPath: "data.dropArea.outlineColor",
                                label: t("props.frameColor"),
                                controlType: "color",
                              }}
                              onAddShortcut={onAddShortcut}
                              isInShortcuts={isPropertyInShortcuts(
                                "data.dropArea.outlineColor"
                              )}
                              onRemoveShortcut={() =>
                                handleRemoveShortcutByPath(
                                  "data.dropArea.outlineColor"
                                )
                              }
                            >
                              {renderColorControl(
                                t("props.frameColor"),
                                dropAreaConfig.outlineColor,
                                (nextColor) =>
                                  updateDropArea({ outlineColor: nextColor })
                              )}
                            </ShortcutableControl>

                            <ShortcutableControl
                              shortcutConfig={{
                                elementType: "drop-target",
                                propertyPath: "data.dropArea.outlineWidth",
                                label: t("props.frameWidth"),
                                controlType: "number",
                                min: 0,
                                max: 20,
                              }}
                              onAddShortcut={onAddShortcut}
                              isInShortcuts={isPropertyInShortcuts(
                                "data.dropArea.outlineWidth"
                              )}
                              onRemoveShortcut={() =>
                                handleRemoveShortcutByPath(
                                  "data.dropArea.outlineWidth"
                                )
                              }
                            >
                              <div className="qe-form__field">
                                <span className="qe-form__label">
                                  {t("props.frameWidth")}
                                </span>
                                <input
                                  className="qe-input"
                                  type="number"
                                  min={0}
                                  max={20}
                                  value={dropAreaConfig.outlineWidth}
                                  onChange={(e) =>
                                    updateDropArea({
                                      outlineWidth: Math.max(
                                        0,
                                        Number(e.target.value) || 0
                                      ),
                                    })
                                  }
                                />
                              </div>
                            </ShortcutableControl>
                          </>
                        ) : null}

                        <label className="qe-form__checkbox-label">
                          <input
                            type="checkbox"
                            checked={
                              (dropAreaConfig.highlightEnabled as any) ===
                                "1" ||
                              (dropAreaConfig.highlightEnabled as any) ===
                                true ||
                              (dropAreaConfig.highlightEnabled as any) === 1
                            }
                            onChange={(e) =>
                              updateDropArea({
                                highlightEnabled: e.target.checked,
                              })
                            }
                          />
                          <span className="qe-form__checkbox-text">
                            {t("props.highlightOnDrag")}
                          </span>
                        </label>

                        {dropAreaConfig.highlightEnabled ? (
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "drop-target",
                              propertyPath: "data.dropArea.highlightColor",
                              label: t("props.highlightColor"),
                              controlType: "color",
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.dropArea.highlightColor"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.dropArea.highlightColor"
                              )
                            }
                          >
                            {renderColorControl(
                              t("props.highlightColor"),
                              dropAreaConfig.highlightColor,
                              (nextColor) =>
                                updateDropArea({ highlightColor: nextColor })
                            )}
                          </ShortcutableControl>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </AccordionContent>
          </div>
        ) : null}

        {/* Order List Settings */}
        {nonNullElement.type === "order-list" && orderListAppearance ? (
          <div
            className={`qe-properties-section ${
              openSection === "order-list-settings"
                ? "qe-properties-section--active"
                : ""
            }`}
          >
            <div
              className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                openSection === "order-list-settings"
                  ? "qe-properties-section__title--open"
                  : "qe-properties-section__title--closed"
              }`}
              onClick={() =>
                setOpenSection(
                  openSection === "order-list-settings"
                    ? null
                    : "order-list-settings"
                )
              }
            >
              <span>{t("props.orderListSettings")}</span>
              <svg
                className={`qe-properties-section__icon ${
                  openSection === "order-list-settings"
                    ? "qe-properties-section__icon--open"
                    : ""
                }`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <AccordionContent isOpen={openSection === "order-list-settings"}>
              <div
                className="qe-properties-section__content"
                style={{ padding: 0 }}
              >
                {(() => {
                  const updateOrderListAppearance = (
                    changes: Partial<OrderListAppearance>
                  ) => {
                    updateElementData({
                      appearance: {
                        ...orderListAppearance,
                        ...changes,
                      },
                    });
                  };

                  return (
                    <>
                      {/* Label Ayarları */}
                      <div className="qe-properties-subgroup">
                        <div className="qe-panel__subtitle">
                          {t("props.labelSettings")}
                        </div>

                        <div className="qe-form__field">
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginBottom: "8px",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={
                                elementData.showLabel !== false &&
                                elementData.showLabel !== 0 &&
                                elementData.showLabel !== "0"
                              }
                              onChange={(e) => {
                                updateElementData({
                                  showLabel: e.target.checked,
                                });
                              }}
                              style={{
                                width: "16px",
                                height: "16px",
                                cursor: "pointer",
                              }}
                            />
                            <span
                              className="qe-form__label"
                              style={{ margin: 0 }}
                            >
                              {t("props.showLabel")}
                            </span>
                          </label>
                        </div>

                        <>
                          <div className="qe-form__field">
                            <span className="qe-form__label">{t("props.labelText")}</span>
                            <input
                              className="qe-input"
                              type="text"
                              value={
                                typeof elementData.label === "string"
                                  ? elementData.label
                                  : t("props.sortArea")
                              }
                              onChange={(e) => {
                                updateElementData({
                                  label: e.target.value,
                                });
                              }}
                              placeholder={t("props.sortArea")}
                            />
                          </div>

                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.labelTextColor",
                              label: t("props.labelTextColor"),
                              controlType: "color",
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.labelTextColor"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.labelTextColor"
                              )
                            }
                          >
                            {renderColorControl(
                              t("props.labelTextColor"),
                              orderListAppearance.labelTextColor,
                              (nextColor) =>
                                updateOrderListAppearance({
                                  labelTextColor: nextColor,
                                })
                            )}
                          </ShortcutableControl>

                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.labelBackground",
                              label: t("props.labelBackground"),
                              controlType: "color",
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.labelBackground"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.labelBackground"
                              )
                            }
                          >
                            {renderColorControl(
                              t("props.labelBackground"),
                              orderListAppearance.labelBackground,
                              (nextColor) =>
                                updateOrderListAppearance({
                                  labelBackground: nextColor,
                                }),
                              { allowTransparent: true }
                            )}
                          </ShortcutableControl>

                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.labelBorderRadius",
                              label: t("props.labelRounding"),
                              controlType: "number",
                              min: 0,
                              max: 100,
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.labelBorderRadius"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.labelBorderRadius"
                              )
                            }
                          >
                            <div className="qe-form__field">
                              <span className="qe-form__label">
                                {t("props.labelRounding")}
                              </span>
                              <input
                                className="qe-input"
                                type="number"
                                min={0}
                                max={100}
                                {...createNumberInputHandlers(
                                  orderListAppearance.labelBorderRadius,
                                  0,
                                  100,
                                  (value) =>
                                    updateOrderListAppearance({
                                      labelBorderRadius: value,
                                    })
                                )}
                              />
                            </div>
                          </ShortcutableControl>

                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.labelPaddingX",
                              label: t("props.labelPaddingH"),
                              controlType: "number",
                              min: 0,
                              max: 100,
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.labelPaddingX"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.labelPaddingX"
                              )
                            }
                          >
                            <div className="qe-form__field">
                              <span className="qe-form__label">
                                {t("props.labelPaddingH")}
                              </span>
                              <input
                                className="qe-input"
                                type="number"
                                min={0}
                                max={100}
                                {...createNumberInputHandlers(
                                  orderListAppearance.labelPaddingX,
                                  0,
                                  100,
                                  (value) =>
                                    updateOrderListAppearance({
                                      labelPaddingX: value,
                                    })
                                )}
                              />
                            </div>
                          </ShortcutableControl>

                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.labelPaddingY",
                              label: t("props.labelPaddingV"),
                              controlType: "number",
                              min: 0,
                              max: 100,
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.labelPaddingY"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.labelPaddingY"
                              )
                            }
                          >
                            <div className="qe-form__field">
                              <span className="qe-form__label">
                                {t("props.labelPaddingV")}
                              </span>
                              <input
                                className="qe-input"
                                type="number"
                                min={0}
                                max={100}
                                {...createNumberInputHandlers(
                                  orderListAppearance.labelPaddingY,
                                  0,
                                  100,
                                  (value) =>
                                    updateOrderListAppearance({
                                      labelPaddingY: value,
                                    })
                                )}
                              />
                            </div>
                          </ShortcutableControl>
                        </>
                      </div>

                      <div className="qe-properties-subgroup">
                        <div className="qe-form__field">
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <span className="qe-form__label">{t("props.elements")}</span>
                            <button
                              type="button"
                              className="qe-button qe-button--small"
                              onClick={() => {
                                const items =
                                  (elementData.items as Array<OrderListItem>) ||
                                  [];
                                const newItemId = `item-${Date.now()}`;
                                updateElementData({
                                  items: [
                                    ...items,
                                    {
                                      id: newItemId,
                                      contentType: "text",
                                      label: `${t("props.elementLabel")} ${items.length + 1}`,
                                    },
                                  ],
                                });
                              }}
                            >
                              {t("props.addNewItem")}
                            </button>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "10px",
                              padding: "10px",
                              backgroundColor: "#f9fafb",
                              borderRadius: "6px",
                              border: "1px solid #e5e7eb",
                            }}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.dataTransfer.dropEffect = "move";
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const draggedId = draggedOrderListItemId;
                              if (!draggedId) return;

                              const items =
                                (elementData.items as Array<OrderListItem>) ||
                                [];
                              const draggedIndex = items.findIndex(
                                (i) => i.id === draggedId
                              );
                              if (draggedIndex === -1) {
                                setDraggedOrderListItemId(null);
                                return;
                              }

                              // Find drop target using elementFromPoint for more accurate detection
                              const container = e.currentTarget as HTMLElement;
                              const allItems = container.querySelectorAll(
                                "[data-order-list-item-id]"
                              );

                              let targetIndex = -1;
                              for (let i = 0; i < allItems.length; i++) {
                                const item = allItems[i] as HTMLElement;
                                const rect = item.getBoundingClientRect();
                                const y = e.clientY;

                                // Check if drop is in the upper or lower half of the item
                                const itemCenterY = rect.top + rect.height / 2;
                                if (y <= itemCenterY) {
                                  const targetId = item.getAttribute(
                                    "data-order-list-item-id"
                                  );
                                  if (targetId) {
                                    targetIndex = items.findIndex(
                                      (i) => i.id === targetId
                                    );
                                    break;
                                  }
                                }
                              }

                              // If no target found or dropped at the end, append to the end
                              if (targetIndex === -1) {
                                targetIndex = items.length;
                              }

                              // Adjust target index if dragging from before the target
                              if (draggedIndex < targetIndex) {
                                targetIndex -= 1;
                              }

                              // Don't move if position hasn't changed
                              if (
                                targetIndex === draggedIndex ||
                                targetIndex === draggedIndex + 1
                              ) {
                                setDraggedOrderListItemId(null);
                                return;
                              }

                              // Reorder items
                              const newItems = [...items];
                              const [movedItem] = newItems.splice(
                                draggedIndex,
                                1
                              );
                              newItems.splice(targetIndex, 0, movedItem);

                              updateElementData({ items: newItems });
                              setDraggedOrderListItemId(null);
                            }}
                          >
                            {((elementData.items as Array<OrderListItem>) || [])
                              .length === 0 ? (
                              <div
                                style={{
                                  color: "#6b7280",
                                  fontSize: "13px",
                                  fontStyle: "italic",
                                  textAlign: "center",
                                  padding: "8px",
                                }}
                              >
                                {t("props.noItemsAdded")}
                              </div>
                            ) : (
                              <>
                                {(
                                  (elementData.items as Array<OrderListItem>) ||
                                  []
                                )
                                  .slice(0, isOrderListExpanded ? undefined : 3)
                                  .map((item, index) => {
                                    const actualIndex = (
                                      (elementData.items as Array<OrderListItem>) ||
                                      []
                                    ).indexOf(item);
                                    return (
                                      <div
                                        key={item.id}
                                        data-order-list-item-id={item.id}
                                        draggable={true}
                                        onDragStart={(e) => {
                                          setDraggedOrderListItemId(item.id);
                                          e.dataTransfer.effectAllowed = "move";
                                          e.dataTransfer.setData(
                                            "text/plain",
                                            item.id
                                          );
                                        }}
                                        onDragEnd={() => {
                                          setDraggedOrderListItemId(null);
                                        }}
                                        style={{
                                          display: "flex",
                                          gap: "12px",
                                          alignItems: "flex-start",
                                          padding: "12px",
                                          marginBottom: "8px",
                                          backgroundColor: "#ffffff",
                                          borderRadius: "6px",
                                          border: "1px solid #e2e8f0",
                                          boxShadow:
                                            draggedOrderListItemId === item.id
                                              ? "0 2px 8px rgba(0, 0, 0, 0.08)"
                                              : "0 1px 2px rgba(0, 0, 0, 0.04)",
                                          cursor:
                                            draggedOrderListItemId === item.id
                                              ? "grabbing"
                                              : "grab",
                                          opacity:
                                            draggedOrderListItemId === item.id
                                              ? 0.7
                                              : 1,
                                          transition: "all 0.15s ease",
                                          position: "relative",
                                        }}
                                        onMouseEnter={(e) => {
                                          if (
                                            draggedOrderListItemId !== item.id
                                          ) {
                                            e.currentTarget.style.boxShadow =
                                              "0 2px 6px rgba(0, 0, 0, 0.06)";
                                            e.currentTarget.style.borderColor =
                                              "#cbd5e1";
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          if (
                                            draggedOrderListItemId !== item.id
                                          ) {
                                            e.currentTarget.style.boxShadow =
                                              "0 1px 2px rgba(0, 0, 0, 0.04)";
                                            e.currentTarget.style.borderColor =
                                              "#e2e8f0";
                                          }
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: "6px",
                                            flexShrink: 0,
                                            paddingTop: "2px",
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: "28px",
                                              height: "28px",
                                              borderRadius: "6px",
                                              backgroundColor: "#475569",
                                              color: "white",
                                              fontSize: "12px",
                                              fontWeight: 600,
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                            }}
                                          >
                                            {actualIndex + 1}
                                          </div>
                                          <div
                                            style={{
                                              cursor: "grab",
                                              padding: "2px",
                                              borderRadius: "3px",
                                              opacity: 0.4,
                                              transition: "opacity 0.2s",
                                            }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.opacity =
                                                "0.7";
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.opacity =
                                                "0.4";
                                            }}
                                          >
                                            <svg
                                              width="10"
                                              height="10"
                                              viewBox="0 0 12 12"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                            >
                                              <circle
                                                cx="2"
                                                cy="2"
                                                r="0.8"
                                                fill="#64748b"
                                              />
                                              <circle
                                                cx="6"
                                                cy="2"
                                                r="0.8"
                                                fill="#64748b"
                                              />
                                              <circle
                                                cx="10"
                                                cy="2"
                                                r="0.8"
                                                fill="#64748b"
                                              />
                                              <circle
                                                cx="2"
                                                cy="6"
                                                r="0.8"
                                                fill="#64748b"
                                              />
                                              <circle
                                                cx="6"
                                                cy="6"
                                                r="0.8"
                                                fill="#64748b"
                                              />
                                              <circle
                                                cx="10"
                                                cy="6"
                                                r="0.8"
                                                fill="#64748b"
                                              />
                                              <circle
                                                cx="2"
                                                cy="10"
                                                r="0.8"
                                                fill="#64748b"
                                              />
                                              <circle
                                                cx="6"
                                                cy="10"
                                                r="0.8"
                                                fill="#64748b"
                                              />
                                              <circle
                                                cx="10"
                                                cy="10"
                                                r="0.8"
                                                fill="#64748b"
                                              />
                                            </svg>
                                          </div>
                                        </div>
                                        <div
                                          style={{
                                            flex: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "8px",
                                            minWidth: 0,
                                          }}
                                        >
                                          <select
                                            className="qe-input"
                                            value={
                                              item.contentType === "mathlive"
                                                ? "mathtype"
                                                : item.contentType || "text"
                                            }
                                            onChange={(e) => {
                                              const items =
                                                (elementData.items as Array<OrderListItem>) ||
                                                [];
                                              const contentType = e.target
                                                .value as
                                                | "text"
                                                | "image"
                                                | "mathtype";
                                              const updatedItems = items.map(
                                                (i) => {
                                                  if (i.id === item.id) {
                                                    // Reset content when changing type
                                                    if (contentType === "text") {
                                                      return {
                                                        ...i,
                                                        contentType,
                                                        label: i.label || "",
                                                      };
                                                    }
                                                    if (contentType === "image") {
                                                      return {
                                                        ...i,
                                                        contentType,
                                                        imageUrl:
                                                          i.imageUrl || "",
                                                        imageAlt:
                                                          i.imageAlt || "",
                                                      };
                                                    }
                                                    return {
                                                      ...i,
                                                      contentType: "mathtype",
                                                      mathml: i.mathml || "",
                                                    };
                                                  }
                                                  return i;
                                                }
                                              );
                                              updateElementData({
                                                items: updatedItems,
                                              });
                                              if (contentType === "mathtype") {
                                                setEditingMathOptionId(null);
                                                setEditingMathOrderListItemId(
                                                  item.id
                                                );
                                                setIsMathEditorOpen(true);
                                              }
                                            }}
                                            onMouseDown={(e) => {
                                              e.stopPropagation();
                                            }}
                                            style={{
                                              fontSize: "13px",
                                              padding: "7px 10px",
                                              borderRadius: "4px",
                                              border: "1px solid #d1d5db",
                                              backgroundColor: "#ffffff",
                                              fontWeight: 400,
                                              color: "#374151",
                                              cursor: "pointer",
                                              transition: "all 0.15s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.borderColor =
                                                "#9ca3af";
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.borderColor =
                                                "#d1d5db";
                                            }}
                                          >
                                            <option value="text">{t("props.contentTypeText")}</option>
                                            <option value="image">{t("props.image")}</option>
                                            <option value="mathtype">
                                              Matematik (MathType)
                                            </option>
                                          </select>
                                          <div className="qe-form__field">
                                            <span className="qe-form__label">
                                              {t("props.frameSize")}
                                            </span>
                                            <div className="qe-inline-group">
                                              <label className="qe-inline-input">
                                                <span className="qe-inline-input__label">
                                                  {t("props.width")}
                                                </span>
                                                <input
                                                  className="qe-input"
                                                  type="number"
                                                  value={item.frameWidth ?? ""}
                                                  placeholder="120"
                                                  onChange={(event) => {
                                                    const raw =
                                                      event.target.value;
                                                    const items =
                                                      (elementData.items as Array<OrderListItem>) ||
                                                      [];
                                                    const updatedItems = items.map(
                                                      (i) =>
                                                        i.id === item.id
                                                          ? {
                                                              ...i,
                                                              frameWidth:
                                                                raw === ""
                                                                  ? undefined
                                                                  : Number(
                                                                      raw
                                                                    ) || 0,
                                                            }
                                                          : i
                                                    );
                                                    updateElementData({
                                                      items: updatedItems,
                                                    });
                                                  }}
                                                />
                                              </label>
                                              <label className="qe-inline-input">
                                                <span className="qe-inline-input__label">
                                                  {t("props.height")}
                                                </span>
                                                <input
                                                  className="qe-input"
                                                  type="number"
                                                  value={item.frameHeight ?? ""}
                                                  placeholder="80"
                                                  onChange={(event) => {
                                                    const raw =
                                                      event.target.value;
                                                    const items =
                                                      (elementData.items as Array<OrderListItem>) ||
                                                      [];
                                                    const updatedItems = items.map(
                                                      (i) =>
                                                        i.id === item.id
                                                          ? {
                                                              ...i,
                                                              frameHeight:
                                                                raw === ""
                                                                  ? undefined
                                                                  : Number(
                                                                      raw
                                                                    ) || 0,
                                                            }
                                                          : i
                                                    );
                                                    updateElementData({
                                                      items: updatedItems,
                                                    });
                                                  }}
                                                />
                                              </label>
                                            </div>
                                            <span className="qe-form__help">
                                              {t("props.autoSizeHelpShort")}
                                            </span>
                                          </div>
                                          {(item.contentType || "text") ===
                                            "text" && (
                                            <input
                                              className="qe-input"
                                              type="text"
                                              value={item.label || ""}
                                              onChange={(e) => {
                                                const items =
                                                  (elementData.items as Array<OrderListItem>) ||
                                                  [];
                                                const updatedItems = items.map(
                                                  (i) =>
                                                    i.id === item.id
                                                      ? {
                                                          ...i,
                                                          label: e.target.value,
                                                        }
                                                      : i
                                                );
                                                updateElementData({
                                                  items: updatedItems,
                                                });
                                              }}
                                              placeholder={t("props.elementLabelPlaceholder")}
                                              onMouseDown={(e) => {
                                                e.stopPropagation();
                                              }}
                                              style={{
                                                borderRadius: "4px",
                                                border: "1px solid #d1d5db",
                                                padding: "7px 10px",
                                                fontSize: "13px",
                                                transition: "all 0.15s ease",
                                                backgroundColor: "#ffffff",
                                              }}
                                              onFocus={(e) => {
                                                e.target.style.borderColor =
                                                  "#6b7280";
                                                e.target.style.boxShadow =
                                                  "0 0 0 2px rgba(107, 114, 128, 0.1)";
                                              }}
                                              onBlur={(e) => {
                                                e.target.style.borderColor =
                                                  "#d1d5db";
                                                e.target.style.boxShadow =
                                                  "none";
                                              }}
                                            />
                                          )}
                                          {(item.contentType || "text") ===
                                            "mathtype" ||
                                          (item.contentType || "text") ===
                                            "mathlive" ? (
                                            <div className="qe-form__field">
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "space-between",
                                                  marginBottom: "8px",
                                                }}
                                              >
                                                <span className="qe-form__label">
                                                  {t("props.mathmlFormula")}
                                                </span>
                                                <button
                                                  className="qe-button qe-button--primary"
                                                  style={{
                                                    padding: "4px 12px",
                                                    fontSize: "12px",
                                                    height: "auto",
                                                  }}
                                                  onClick={() => {
                                                    setEditingMathOptionId(null);
                                                    setEditingMathOrderListItemId(
                                                      item.id
                                                    );
                                                    setIsMathEditorOpen(true);
                                                  }}
                                                  type="button"
                                                >
                                                  {t("props.openEditor")}
                                                </button>
                                              </div>
                                              <textarea
                                                className="qe-input qe-textarea"
                                                value={
                                                  item.mathml ||
                                                  item.latex ||
                                                  ""
                                                }
                                                placeholder="<math><mi>x</mi><mo>=</mo><mfrac>...</mfrac></math>"
                                                onChange={(event) => {
                                                  const items =
                                                    (elementData.items as Array<OrderListItem>) ||
                                                    [];
                                                  const updatedItems =
                                                    items.map((i) =>
                                                      i.id === item.id
                                                        ? {
                                                            ...i,
                                                            mathml:
                                                              event.target
                                                                .value,
                                                          }
                                                        : i
                                                    );
                                                  updateElementData({
                                                    items: updatedItems,
                                                  });
                                                }}
                                                rows={3}
                                              />
                                            </div>
                                          ) : null}
                                          {(item.contentType || "text") ===
                                            "image" && (
                                            <>
                                              <button
                                                type="button"
                                                className={`qe-media-library-button ${
                                                  item.imageUrl
                                                    ? "qe-media-library-button--has-value"
                                                    : ""
                                                }`}
                                                onClick={() => {
                                                  const items =
                                                    (elementData.items as Array<OrderListItem>) ||
                                                    [];
                                                  setMediaLibraryContext({
                                                    type: "order-list-item",
                                                    onSelect: (url) => {
                                                      const updatedItems =
                                                        items.map((i) =>
                                                          i.id === item.id
                                                            ? {
                                                                ...i,
                                                                imageUrl: url,
                                                              }
                                                            : i
                                                        );
                                                      updateElementData({
                                                        items: updatedItems,
                                                      });
                                                    },
                                                    currentUrl: item.imageUrl,
                                                  });
                                                  setIsMediaLibraryOpen(true);
                                                }}
                                                onMouseDown={(e) => {
                                                  e.stopPropagation();
                                                }}
                                                style={{
                                                  width: "100%",
                                                  minWidth: 0,
                                                  fontSize: "12px",
                                                  padding: "7px 12px",
                                                  whiteSpace: "normal",
                                                  wordBreak: "break-word",
                                                  lineHeight: "1.4",
                                                }}
                                              >
                                                <svg
                                                  className="qe-media-library-button__icon"
                                                  width="16"
                                                  height="16"
                                                  viewBox="0 0 16 16"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                >
                                                  <path
                                                    d="M14 2H2C1.44772 2 1 2.44772 1 3V13C1 13.5523 1.44772 14 2 14H14C14.5523 14 15 13.5523 15 13V3C15 2.44772 14.5523 2 14 2Z"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  />
                                                  <path
                                                    d="M5.5 7C6.32843 7 7 6.32843 7 5.5C7 4.67157 6.32843 4 5.5 4C4.67157 4 4 4.67157 4 5.5C4 6.32843 4.67157 7 5.5 7Z"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  />
                                                  <path
                                                    d="M15 10L11 6L2 15"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  />
                                                </svg>
                                                {item.imageUrl
                                                  ? t("props.imageSelected")
                                                  : t("props.selectFromMediaLibrary")}
                                              </button>
                                              {item.imageUrl && (
                                                <div
                                                  style={{
                                                    marginTop: "8px",
                                                    fontSize: "12px",
                                                    color:
                                                      "var(--qe-text-muted)",
                                                  }}
                                                >
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      setImagePreview({
                                                        url: item.imageUrl!,
                                                        name:
                                                          item.label || "Resim",
                                                      })
                                                    }
                                                    style={{
                                                      color: "var(--qe-accent)",
                                                      textDecoration:
                                                        "underline",
                                                      background: "none",
                                                      border: "none",
                                                      padding: 0,
                                                      cursor: "pointer",
                                                      fontSize: "12px",
                                                      fontFamily: "inherit",
                                                    }}
                                                  >
                                                    {t("props.viewCurrentImage")}
                                                  </button>
                                                </div>
                                              )}
                                              <input
                                                className="qe-input"
                                                type="text"
                                                value={item.imageAlt || ""}
                                                onChange={(e) => {
                                                  const items =
                                                    (elementData.items as Array<OrderListItem>) ||
                                                    [];
                                                  const updatedItems =
                                                    items.map((i) =>
                                                      i.id === item.id
                                                        ? {
                                                            ...i,
                                                            imageAlt:
                                                              e.target.value,
                                                          }
                                                        : i
                                                    );
                                                  updateElementData({
                                                    items: updatedItems,
                                                  });
                                                }}
                                                placeholder={t("props.altTextOptional")}
                                                onMouseDown={(e) => {
                                                  e.stopPropagation();
                                                }}
                                                style={{
                                                  borderRadius: "4px",
                                                  border: "1px solid #d1d5db",
                                                  padding: "7px 10px",
                                                  fontSize: "13px",
                                                  transition: "all 0.15s ease",
                                                  backgroundColor: "#ffffff",
                                                }}
                                                onFocus={(e) => {
                                                  e.target.style.borderColor =
                                                    "#6b7280";
                                                  e.target.style.boxShadow =
                                                    "0 0 0 2px rgba(107, 114, 128, 0.1)";
                                                }}
                                                onBlur={(e) => {
                                                  e.target.style.borderColor =
                                                    "#d1d5db";
                                                  e.target.style.boxShadow =
                                                    "none";
                                                }}
                                              />
                                            </>
                                          )}
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const items =
                                              (elementData.items as Array<OrderListItem>) ||
                                              [];
                                            const updatedItems = items.filter(
                                              (i) => i.id !== item.id
                                            );
                                            updateElementData({
                                              items: updatedItems,
                                            });
                                          }}
                                          title={t("props.deleteElement")}
                                          onMouseDown={(e) => {
                                            e.stopPropagation();
                                          }}
                                          style={{
                                            width: "32px",
                                            height: "32px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                            backgroundColor: "#ffffff",
                                            color: "#6b7280",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            transition: "all 0.15s ease",
                                            flexShrink: 0,
                                            fontSize: "18px",
                                            fontWeight: 400,
                                            lineHeight: 1,
                                            alignSelf: "flex-start",
                                            marginTop: "0",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                              "#f3f4f6";
                                            e.currentTarget.style.borderColor =
                                              "#d1d5db";
                                            e.currentTarget.style.color =
                                              "#374151";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                              "#ffffff";
                                            e.currentTarget.style.borderColor =
                                              "#e5e7eb";
                                            e.currentTarget.style.color =
                                              "#6b7280";
                                          }}
                                        >
                                          ×
                                        </button>
                                      </div>
                                    );
                                  })}
                                {(
                                  (elementData.items as Array<OrderListItem>) ||
                                  []
                                ).length > 3 && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setIsOrderListExpanded(
                                        !isOrderListExpanded
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      padding: "10px",
                                      marginTop: "4px",
                                      backgroundColor: "#ffffff",
                                      border: "1px solid #e5e7eb",
                                      borderRadius: "6px",
                                      color: "#374151",
                                      fontSize: "13px",
                                      fontWeight: 500,
                                      cursor: "pointer",
                                      transition: "all 0.15s ease",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "6px",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor =
                                        "#f9fafb";
                                      e.currentTarget.style.borderColor =
                                        "#d1d5db";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor =
                                        "#ffffff";
                                      e.currentTarget.style.borderColor =
                                        "#e5e7eb";
                                    }}
                                  >
                                    {isOrderListExpanded ? (
                                      <>
                                        <svg
                                          width="14"
                                          height="14"
                                          viewBox="0 0 14 14"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M3.5 8.75L7 5.25L10.5 8.75"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                        {t("props.showLess")}
                                      </>
                                    ) : (
                                      <>
                                        <svg
                                          width="14"
                                          height="14"
                                          viewBox="0 0 14 14"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M3.5 5.25L7 8.75L10.5 5.25"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                        {(
                                          (elementData.items as Array<OrderListItem>) ||
                                          []
                                        ).length - 3}{" "}
                                        {t("props.showMoreItems")}
                                      </>
                                    )}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="qe-properties-subgroup">
                        <div className="qe-panel__subtitle">
                          {t("props.viewAppearance")}
                        </div>

                        <>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.backgroundColor",
                              label: t("props.backgroundColor"),
                              controlType: "color",
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.backgroundColor"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.backgroundColor"
                              )
                            }
                          >
                            {renderColorControl(
                              t("props.backgroundColor"),
                              orderListAppearance.backgroundColor,
                              (nextColor) =>
                                updateOrderListAppearance({
                                  backgroundColor: nextColor,
                                })
                            )}
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.borderColor",
                              label: t("props.borderColor"),
                              controlType: "color",
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.borderColor"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.borderColor"
                              )
                            }
                          >
                            {renderColorControl(
                              t("props.borderColor"),
                              orderListAppearance.borderColor,
                              (nextColor) =>
                                updateOrderListAppearance({
                                  borderColor: nextColor,
                                })
                            )}
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.borderWidth",
                              label: t("props.borderWidth"),
                              controlType: "number",
                              min: 0,
                              max: 20,
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.borderWidth"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.borderWidth"
                              )
                            }
                          >
                            <div className="qe-form__field">
                              <span className="qe-form__label">
                                {t("props.borderWidth")}
                              </span>
                              <input
                                className="qe-input"
                                type="number"
                                min={0}
                                max={20}
                                {...createNumberInputHandlers(
                                  orderListAppearance.borderWidth,
                                  0,
                                  20,
                                  (value) =>
                                    updateOrderListAppearance({
                                      borderWidth: value,
                                    })
                                )}
                              />
                            </div>
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.borderStyle",
                              label: t("props.borderStyle"),
                              controlType: "select",
                              options: [
                                { value: "solid", label: t("props.borderStyleSolid") },
                                { value: "dashed", label: t("props.borderStyleDashed") },
                                { value: "dotted", label: t("props.borderStyleDotted") },
                                { value: "none", label: t("props.none") },
                              ],
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.borderStyle"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.borderStyle"
                              )
                            }
                          >
                            <div className="qe-form__field">
                              <span className="qe-form__label">
                                {t("props.kenarlıkStili")}
                              </span>
                              <select
                                className="qe-input"
                                value={orderListAppearance.borderStyle}
                                onChange={(e) =>
                                  updateOrderListAppearance({
                                    borderStyle: e.target.value as
                                      | "solid"
                                      | "dashed"
                                      | "dotted"
                                      | "none",
                                  })
                                }
                              >
                                <option value="solid">{t("props.borderStyleSolid")}</option>
                                <option value="dashed">{t("props.borderStyleDashed")}</option>
                                <option value="dotted">{t("props.borderStyleDotted")}</option>
                                <option value="none">{t("props.none")}</option>
                              </select>
                            </div>
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.borderRadius",
                              label: t("props.borderRadius"),
                              controlType: "number",
                              min: 0,
                              max: 100,
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.borderRadius"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.borderRadius"
                              )
                            }
                          >
                            <div className="qe-form__field">
                              <span className="qe-form__label">
                                {t("props.kenarlıkYuvarlak")}
                              </span>
                              <input
                                className="qe-input"
                                type="number"
                                min={0}
                                max={100}
                                {...createNumberInputHandlers(
                                  orderListAppearance.borderRadius,
                                  0,
                                  100,
                                  (value) =>
                                    updateOrderListAppearance({
                                      borderRadius: value,
                                    })
                                )}
                              />
                            </div>
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.paddingX",
                              label: t("props.paddingH"),
                              controlType: "number",
                              min: 0,
                              max: 200,
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.paddingX"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.paddingX"
                              )
                            }
                          >
                            <div className="qe-form__field">
                              <span className="qe-form__label">
                                {t("props.icBoslukYatay")}
                              </span>
                              <input
                                className="qe-input"
                                type="number"
                                min={0}
                                max={200}
                                {...createNumberInputHandlers(
                                  orderListAppearance.paddingX,
                                  0,
                                  200,
                                  (value) =>
                                    updateOrderListAppearance({
                                      paddingX: value,
                                    })
                                )}
                              />
                            </div>
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.paddingY",
                              label: t("props.paddingV"),
                              controlType: "number",
                              min: 0,
                              max: 200,
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.paddingY"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.paddingY"
                              )
                            }
                          >
                            <div className="qe-form__field">
                              <span className="qe-form__label">
                                {t("props.icBoslukDikey")}
                              </span>
                              <input
                                className="qe-input"
                                type="number"
                                min={0}
                                max={200}
                                {...createNumberInputHandlers(
                                  orderListAppearance.paddingY,
                                  0,
                                  200,
                                  (value) =>
                                    updateOrderListAppearance({
                                      paddingY: value,
                                    })
                                )}
                              />
                            </div>
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.itemSpacing",
                              label: t("props.itemGap"),
                              controlType: "number",
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.itemSpacing"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.itemSpacing"
                              )
                            }
                          >
                            <div className="qe-form__field">
                              <span className="qe-form__label">
                                {t("props.itemSpacing")}
                              </span>
                              <input
                                className="qe-input"
                                type="number"
                                {...createNumberInputHandlers(
                                  orderListAppearance.itemSpacing,
                                  null,
                                  null,
                                  (value) =>
                                    updateOrderListAppearance({
                                      itemSpacing: value,
                                    })
                                )}
                              />
                            </div>
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.contentLayout",
                              label: t("props.contentLayout"),
                              controlType: "select",
                              options: [
                                { value: "vertical", label: t("props.layoutVertical") },
                                { value: "horizontal", label: t("props.layoutHorizontal") },
                              ],
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.contentLayout"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.contentLayout"
                              )
                            }
                          >
                            <div className="qe-form__field">
                              <span className="qe-form__label">
                                {t("props.icerikDuzeni")}
                              </span>
                              <select
                                className="qe-input"
                                value={orderListAppearance.contentLayout}
                                onChange={(e) =>
                                  updateOrderListAppearance({
                                    contentLayout: e.target.value as
                                      | "vertical"
                                      | "horizontal",
                                  })
                                }
                              >
                                <option value="vertical">{t("props.layoutVertical")}</option>
                                <option value="horizontal">{t("props.layoutHorizontal")}</option>
                              </select>
                            </div>
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.contentAlignment",
                              label: t("props.contentAlignment"),
                              controlType: "select",
                              options: [
                                { value: "start", label: t("props.start") },
                                { value: "center", label: t("props.center") },
                                { value: "end", label: t("props.end") },
                              ],
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.contentAlignment"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.contentAlignment"
                              )
                            }
                          >
                            <div className="qe-form__field">
                              <span className="qe-form__label">
                                {t("props.icerikHizalama")}
                              </span>
                              <select
                                className="qe-input"
                                value={orderListAppearance.contentAlignment}
                                onChange={(e) =>
                                  updateOrderListAppearance({
                                    contentAlignment: e.target.value as
                                      | "start"
                                      | "center"
                                      | "end",
                                  })
                                }
                              >
                                <option value="start">{t("props.start")}</option>
                                <option value="center">{t("props.center")}</option>
                                <option value="end">{t("props.end")}</option>
                              </select>
                            </div>
                          </ShortcutableControl>
                          <div className="qe-form__field">
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={orderListAppearance.showNumbers}
                                onChange={(e) =>
                                  updateOrderListAppearance({
                                    showNumbers: e.target.checked,
                                  })
                                }
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  cursor: "pointer",
                                }}
                              />
                              <span
                                className="qe-form__label"
                                style={{ margin: 0 }}
                              >
                                {t("props.showNumbersToggle")}
                              </span>
                            </label>
                          </div>
                          {orderListAppearance.showNumbers && (
                            <>
                              <ShortcutableControl
                                shortcutConfig={{
                                  elementType: "order-list",
                                  propertyPath:
                                    "data.appearance.numberPosition",
                                  label: t("props.numberPosition"),
                                  controlType: "select",
                                  options: [
                                    { value: "top-left", label: t("props.topLeft") },
                                    { value: "top-center", label: t("props.topCenter") },
                                    { value: "top-right", label: t("props.topRight") },
                                    {
                                      value: "middle-left",
                                      label: t("props.middleLeft"),
                                    },
                                    { value: "center", label: t("props.center") },
                                    {
                                      value: "middle-right",
                                      label: t("props.middleRight"),
                                    },
                                    {
                                      value: "bottom-left",
                                      label: t("props.posBottomLeft"),
                                    },
                                    {
                                      value: "bottom-center",
                                      label: t("props.bottomCenter"),
                                    },
                                    {
                                      value: "bottom-right",
                                      label: t("props.posBottomRight"),
                                    },
                                  ],
                                }}
                                onAddShortcut={onAddShortcut}
                                isInShortcuts={isPropertyInShortcuts(
                                  "data.appearance.numberPosition"
                                )}
                                onRemoveShortcut={() =>
                                  handleRemoveShortcutByPath(
                                    "data.appearance.numberPosition"
                                  )
                                }
                              >
                                <div className="qe-form__field">
                                  <span className="qe-form__label">
                                    Numara Pozisyonu
                                  </span>
                                  <select
                                    className="qe-input"
                                    value={orderListAppearance.numberPosition}
                                    onChange={(e) =>
                                      updateOrderListAppearance({
                                        numberPosition: e.target.value as
                                          | "top-left"
                                          | "top-center"
                                          | "top-right"
                                          | "middle-left"
                                          | "center"
                                          | "middle-right"
                                          | "bottom-left"
                                          | "bottom-center"
                                          | "bottom-right",
                                      })
                                    }
                                  >
                                    <option value="top-left">{t("props.topLeft")}</option>
                                    <option value="top-center">{t("props.topCenter")}</option>
                                    <option value="top-right">{t("props.topRight")}</option>
                                    <option value="middle-left">{t("props.middleLeft")}</option>
                                    <option value="center">{t("props.center")}</option>
                                    <option value="middle-right">
                                      {t("props.middleRight")}
                                    </option>
                                    <option value="bottom-left">{t("props.posBottomLeft")}</option>
                                    <option value="bottom-center">
                                      Alt Orta
                                    </option>
                                    <option value="bottom-right">{t("props.posBottomRight")}</option>
                                  </select>
                                </div>
                              </ShortcutableControl>
                              <ShortcutableControl
                                shortcutConfig={{
                                  elementType: "order-list",
                                  propertyPath: "data.appearance.numberColor",
                                  label: t("props.numberColor"),
                                  controlType: "color",
                                }}
                                onAddShortcut={onAddShortcut}
                                isInShortcuts={isPropertyInShortcuts(
                                  "data.appearance.numberColor"
                                )}
                                onRemoveShortcut={() =>
                                  handleRemoveShortcutByPath(
                                    "data.appearance.numberColor"
                                  )
                                }
                              >
                                {renderColorControl(
                                  t("props.numberColor"),
                                  orderListAppearance.numberColor,
                                  (nextColor) =>
                                    updateOrderListAppearance({
                                      numberColor: nextColor,
                                    })
                                )}
                              </ShortcutableControl>
                              <ShortcutableControl
                                shortcutConfig={{
                                  elementType: "order-list",
                                  propertyPath:
                                    "data.appearance.numberBackground",
                                  label: t("props.numberBgColor"),
                                  controlType: "color",
                                }}
                                onAddShortcut={onAddShortcut}
                                isInShortcuts={isPropertyInShortcuts(
                                  "data.appearance.numberBackground"
                                )}
                                onRemoveShortcut={() =>
                                  handleRemoveShortcutByPath(
                                    "data.appearance.numberBackground"
                                  )
                                }
                              >
                                {renderColorControl(
                                  t("props.numberBgColor"),
                                  orderListAppearance.numberBackground,
                                  (nextColor) =>
                                    updateOrderListAppearance({
                                      numberBackground: nextColor,
                                    })
                                )}
                              </ShortcutableControl>
                            </>
                          )}
                          <div
                            className="qe-panel__subtitle"
                            style={{ marginTop: "16px" }}
                          >
                            {t("props.itemAppearance")}
                          </div>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.itemBackground",
                              label: t("props.itemBackgroundColor"),
                              controlType: "color",
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.itemBackground"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.itemBackground"
                              )
                            }
                          >
                            {renderColorControl(
                              t("props.itemBackgroundColor"),
                              orderListAppearance.itemBackground,
                              (nextColor) =>
                                updateOrderListAppearance({
                                  itemBackground: nextColor,
                                })
                            )}
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.itemBorderColor",
                              label: t("props.itemBorderColor"),
                              controlType: "color",
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.itemBorderColor"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.itemBorderColor"
                              )
                            }
                          >
                            {renderColorControl(
                              t("props.itemBorderColor"),
                              orderListAppearance.itemBorderColor,
                              (nextColor) =>
                                updateOrderListAppearance({
                                  itemBorderColor: nextColor,
                                })
                            )}
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.itemTextColor",
                              label: t("props.itemTextColor"),
                              controlType: "color",
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.itemTextColor"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.itemTextColor"
                              )
                            }
                          >
                            {renderColorControl(
                              t("props.itemTextColor"),
                              orderListAppearance.itemTextColor,
                              (nextColor) =>
                                updateOrderListAppearance({
                                  itemTextColor: nextColor,
                                })
                            )}
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.itemFontSize",
                              label: t("props.itemFontSize"),
                              controlType: "number",
                              min: 8,
                              max: 120,
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.itemFontSize"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.itemFontSize"
                              )
                            }
                          >
                            <div className="qe-form__field">
                              <span className="qe-form__label">
                                {t("props.itemFontSize")}
                              </span>
                              <input
                                className="qe-input"
                                type="number"
                                min={8}
                                max={120}
                                {...createNumberInputHandlers(
                                  orderListAppearance.itemFontSize,
                                  8,
                                  120,
                                  (value) =>
                                    updateOrderListAppearance({
                                      itemFontSize: value,
                                    })
                                )}
                              />
                            </div>
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.itemBorderWidth",
                              label: t("props.itemBorderWidth"),
                              controlType: "number",
                              min: 0,
                              max: 20,
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.itemBorderWidth"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.itemBorderWidth"
                              )
                            }
                          >
                            <div className="qe-form__field">
                              <span className="qe-form__label">
                                {t("props.itemBorderWidth")}
                              </span>
                              <input
                                className="qe-input"
                                type="number"
                                min={0}
                                max={20}
                                {...createNumberInputHandlers(
                                  orderListAppearance.itemBorderWidth,
                                  0,
                                  20,
                                  (value) =>
                                    updateOrderListAppearance({
                                      itemBorderWidth: value,
                                    })
                                )}
                              />
                            </div>
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.itemBorderRadius",
                              label: t("props.itemBorderRadius"),
                              controlType: "number",
                              min: 0,
                              max: 100,
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.itemBorderRadius"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.itemBorderRadius"
                              )
                            }
                          >
                            <div className="qe-form__field">
                              <span className="qe-form__label">
                                {t("props.itemBorderRadius")}
                              </span>
                              <input
                                className="qe-input"
                                type="number"
                                min={0}
                                max={100}
                                {...createNumberInputHandlers(
                                  orderListAppearance.itemBorderRadius,
                                  0,
                                  100,
                                  (value) =>
                                    updateOrderListAppearance({
                                      itemBorderRadius: value,
                                    })
                                )}
                              />
                            </div>
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.itemPaddingX",
                              label: t("props.itemPaddingH"),
                              controlType: "number",
                              min: 0,
                              max: 200,
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.itemPaddingX"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.itemPaddingX"
                              )
                            }
                          >
                            <div className="qe-form__field">
                              <span className="qe-form__label">
                                {t("props.itemPaddingH")}
                              </span>
                              <input
                                className="qe-input"
                                type="number"
                                min={0}
                                max={200}
                                {...createNumberInputHandlers(
                                  orderListAppearance.itemPaddingX,
                                  0,
                                  200,
                                  (value) =>
                                    updateOrderListAppearance({
                                      itemPaddingX: value,
                                    })
                                )}
                              />
                            </div>
                          </ShortcutableControl>
                          <ShortcutableControl
                            shortcutConfig={{
                              elementType: "order-list",
                              propertyPath: "data.appearance.itemPaddingY",
                              label: t("props.itemPaddingV"),
                              controlType: "number",
                              min: 0,
                              max: 200,
                            }}
                            onAddShortcut={onAddShortcut}
                            isInShortcuts={isPropertyInShortcuts(
                              "data.appearance.itemPaddingY"
                            )}
                            onRemoveShortcut={() =>
                              handleRemoveShortcutByPath(
                                "data.appearance.itemPaddingY"
                              )
                            }
                          >
                            <div className="qe-form__field">
                              <span className="qe-form__label">
                                {t("props.itemPaddingV")}
                              </span>
                              <input
                                className="qe-input"
                                type="number"
                                min={0}
                                max={200}
                                {...createNumberInputHandlers(
                                  orderListAppearance.itemPaddingY,
                                  0,
                                  200,
                                  (value) =>
                                    updateOrderListAppearance({
                                      itemPaddingY: value,
                                    })
                                )}
                              />
                            </div>
                          </ShortcutableControl>
                        </>
                      </div>
                    </>
                  );
                })()}
              </div>
            </AccordionContent>
          </div>
        ) : null}

        {/* Matching Point Settings */}
        {nonNullElement.type === "matching-point" && matchingPointAppearance ? (
          <div
            className={`qe-properties-section ${
              openSection === "matching-point-settings"
                ? "qe-properties-section--active"
                : ""
            }`}
          >
            <div
              className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                openSection === "matching-point-settings"
                  ? "qe-properties-section__title--open"
                  : "qe-properties-section__title--closed"
              }`}
              onClick={() =>
                setOpenSection(
                  openSection === "matching-point-settings"
                    ? null
                    : "matching-point-settings"
                )
              }
            >
              <span>{t("props.matchingPointSettings")}</span>
              <svg
                className={`qe-properties-section__icon ${
                  openSection === "matching-point-settings"
                    ? "qe-properties-section__icon--open"
                    : ""
                }`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <AccordionContent
              isOpen={openSection === "matching-point-settings"}
            >
              <div
                className="qe-properties-section__content"
                style={{ padding: 0 }}
              >
                <div className="qe-properties-subgroup">
                  <div className="qe-panel__subtitle">{t("props.generalSettings")}</div>

                  <div className="qe-form__field">
                    <span className="qe-form__label">{t("props.name")}</span>
                    <input
                      className="qe-input"
                      type="text"
                      value={(elementData.name as string) || ""}
                      onChange={(e) =>
                        onUpdate(nonNullElement.id, {
                          data: {
                            ...elementData,
                            name: e.target.value,
                          },
                        })
                      }
                      placeholder={
                        matchingPointType === "source"
                          ? t("props.sourcePointName")
                          : t("props.targetPointName")
                      }
                    />
                    <small
                      style={{
                        display: "block",
                        marginTop: "4px",
                        color: "#6b7280",
                        fontSize: "12px",
                      }}
                    >
                      {t("props.nameShownInAnswerKey")}
                    </small>
                  </div>
                </div>

                {/* Çerçeve Şekli — bu kareyi kare/dikdörtgen yap, ya da
                    tüm eşleştirme karelerini topluca aynı boyuta getir. */}
                <div className="qe-properties-subgroup">
                  <div className="qe-panel__subtitle">Çerçeve Şekli</div>
                  {(() => {
                    const w = nonNullElement.size.width;
                    const h = nonNullElement.size.height ?? w;
                    const isSquare = Math.abs(w - h) < 2;
                    return (
                      <>
                        <div className="qe-form__field">
                          <span className="qe-form__label">Bu kare</span>
                          <div className="qe-style-toggle-group">
                            <button
                              type="button"
                              className={`qe-style-toggle ${
                                isSquare ? "qe-style-toggle--active" : ""
                              }`}
                              onClick={() => {
                                const size = Math.max(w, h);
                                onUpdate(nonNullElement.id, {
                                  size: { width: size, height: size },
                                });
                              }}
                            >
                              Kare ({Math.max(w, h)}×{Math.max(w, h)})
                            </button>
                            <button
                              type="button"
                              className={`qe-style-toggle ${
                                !isSquare ? "qe-style-toggle--active" : ""
                              }`}
                              onClick={() => {
                                const newW = w;
                                const newH = Math.max(40, Math.round(w * 0.66));
                                onUpdate(nonNullElement.id, {
                                  size: { width: newW, height: newH },
                                });
                              }}
                            >
                              Dikdörtgen
                            </button>
                          </div>
                        </div>

                        <div className="qe-form__field">
                          <span className="qe-form__label">
                            Tüm karelere uygula
                          </span>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              type="button"
                              className="qe-button qe-button--ghost"
                              onClick={() => {
                                const size = Math.max(w, h);
                                elements
                                  .filter((el) => el.type === "matching-point")
                                  .forEach((el) => {
                                    onUpdate(el.id, {
                                      size: { width: size, height: size },
                                    });
                                  });
                              }}
                              style={{ flex: 1, fontSize: 12, padding: "6px 8px" }}
                            >
                              Hepsini Kare
                            </button>
                            <button
                              type="button"
                              className="qe-button qe-button--ghost"
                              onClick={() => {
                                const newW = w;
                                const newH = Math.max(40, Math.round(w * 0.66));
                                elements
                                  .filter((el) => el.type === "matching-point")
                                  .forEach((el) => {
                                    onUpdate(el.id, {
                                      size: { width: newW, height: newH },
                                    });
                                  });
                              }}
                              style={{ flex: 1, fontSize: 12, padding: "6px 8px" }}
                            >
                              Hepsini Dikdörtgen
                            </button>
                          </div>
                          <small style={{ color: "#94a3b8", fontSize: 11 }}>
                            Bu canvas'taki tüm eşleştirme karelerinin boyutunu
                            seçili karenin boyutuna eşitler.
                          </small>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* İçerik — noktayı renkli nokta yerine sayı/harf/resim
                    içeren kutu olarak göster. */}
                <div className="qe-properties-subgroup">
                  <div className="qe-panel__subtitle">İçerik</div>
                  <div className="qe-form__field">
                    <span className="qe-form__label">İçerik Tipi</span>
                    <select
                      className="qe-input"
                      value={matchingPointAppearance.contentType ?? "color"}
                      onChange={(e) =>
                        updateMatchingPointAppearance({
                          contentType: e.target.value as
                            | "color"
                            | "text"
                            | "image",
                        })
                      }
                    >
                      <option value="color">Renkli Nokta</option>
                      <option value="text">Yazı (sayı/harf)</option>
                      <option value="image">Resim</option>
                    </select>
                    <small
                      style={{
                        display: "block",
                        marginTop: 4,
                        color: "#6b7280",
                        fontSize: 12,
                      }}
                    >
                      "Renkli Nokta" küçük renkli nokta; "Yazı"/"Resim" ise
                      element boyutunda içerikli kutu olur.
                    </small>
                  </div>

                  {(matchingPointAppearance.contentType ?? "color") ===
                  "text" ? (
                    <>
                      <div className="qe-form__field">
                        <span className="qe-form__label">Yazı</span>
                        <input
                          className="qe-input"
                          type="text"
                          value={matchingPointAppearance.contentText ?? ""}
                          placeholder="örn. 1, A, elma"
                          onChange={(e) =>
                            updateMatchingPointAppearance({
                              contentText: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 8,
                        }}
                      >
                        <div className="qe-form__field">
                          <span className="qe-form__label">Yazı Boyutu (px)</span>
                          <input
                            className="qe-input"
                            type="number"
                            min={8}
                            max={120}
                            value={
                              matchingPointAppearance.contentFontSize ?? 24
                            }
                            onChange={(e) =>
                              updateMatchingPointAppearance({
                                contentFontSize: Math.max(
                                  8,
                                  Math.min(120, Number(e.target.value) || 24)
                                ),
                              })
                            }
                          />
                        </div>
                        <div className="qe-form__field">
                          <span className="qe-form__label">Kalınlık</span>
                          <select
                            className="qe-input"
                            value={
                              matchingPointAppearance.contentFontWeight ?? 600
                            }
                            onChange={(e) =>
                              updateMatchingPointAppearance({
                                contentFontWeight: Number(e.target.value) as
                                  | 400
                                  | 500
                                  | 600
                                  | 700
                                  | 800,
                              })
                            }
                          >
                            <option value={400}>İnce</option>
                            <option value={500}>Orta</option>
                            <option value={600}>Yarı Kalın</option>
                            <option value={700}>Kalın</option>
                            <option value={800}>Çok Kalın</option>
                          </select>
                        </div>
                      </div>
                      {renderColorControl(
                        "Yazı Rengi",
                        matchingPointAppearance.contentColor ?? "#1f2937",
                        (next) =>
                          updateMatchingPointAppearance({ contentColor: next })
                      )}
                    </>
                  ) : null}

                  {(matchingPointAppearance.contentType ?? "color") ===
                  "image" ? (
                    <div className="qe-form__field">
                      <span className="qe-form__label">Resim</span>
                      {/* Üç kaynak: URL, bilgisayardan yükle, medya kütüphanesi */}
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          alignItems: "center",
                        }}
                      >
                        <input
                          className="qe-input"
                          type="text"
                          value={matchingPointAppearance.contentImageUrl ?? ""}
                          placeholder="https://… veya URL"
                          onChange={(e) =>
                            updateMatchingPointAppearance({
                              contentImageUrl: e.target.value,
                            })
                          }
                          style={{ flex: 1 }}
                        />
                        <label
                          className="qe-button qe-button--ghost"
                          style={{
                            fontSize: 11,
                            padding: "6px 8px",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                          title="Bilgisayardan resim yükle"
                        >
                          Yükle
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const reader = new FileReader();
                              reader.onload = () => {
                                if (typeof reader.result === "string") {
                                  updateMatchingPointAppearance({
                                    contentImageUrl: reader.result,
                                  });
                                }
                              };
                              reader.readAsDataURL(file);
                              e.target.value = "";
                            }}
                          />
                        </label>
                      </div>
                      <button
                        type="button"
                        className={`qe-media-library-button ${
                          matchingPointAppearance.contentImageUrl
                            ? "qe-media-library-button--has-value"
                            : ""
                        }`}
                        style={{ marginTop: 6 }}
                        onClick={() => {
                          setMediaLibraryContext({
                            type: "matching-point",
                            onSelect: (url) =>
                              updateMatchingPointAppearance({
                                contentImageUrl: url,
                              }),
                            currentUrl:
                              matchingPointAppearance.contentImageUrl,
                          });
                          setIsMediaLibraryOpen(true);
                        }}
                      >
                        {matchingPointAppearance.contentImageUrl
                          ? t("props.imageSelected")
                          : t("props.selectFromMediaLibrary")}
                      </button>
                      <small
                        style={{
                          display: "block",
                          marginTop: 4,
                          color: "#6b7280",
                          fontSize: 12,
                        }}
                      >
                        Resim çerçeveye sığacak şekilde küçültülür (contain).
                      </small>
                    </div>
                  ) : null}
                </div>

                {/* Tahta Düzeni — tüm eşleştirme karelerini eşit aralıkla
                    yeniden diz + boyutlarını eşitle. */}
                <div className="qe-properties-subgroup">
                  <div className="qe-panel__subtitle">Tahta Düzeni</div>
                  <small
                    style={{
                      color: "#94a3b8",
                      fontSize: 11,
                      display: "block",
                      marginBottom: 6,
                    }}
                  >
                    Aşağıdaki değerlerden birini değiştirmek tüm eşleştirme
                    karelerini bu aralıklarla yeniden hizalar.
                  </small>
                  <MatchingBoardLayoutControl
                    elements={elements}
                    onMove={onMove}
                    onResize={onResize}
                  />
                </div>

                <div className="qe-properties-subgroup">
                  <button
                    type="button"
                    className="qe-button qe-button--ghost"
                    style={{ width: "100%" }}
                    onClick={applyMatchingStyleToAllBlocks}
                    title="Bu bloğun bağlantı çizgisi ve stil ayarlarını sorudaki tüm eşleştirme bloklarına uygular. Her bloğun kendi içerik metni/resmi korunur."
                  >
                    Stil ayarlarını tüm eşleştirme bloklarına uygula
                  </button>
                  <small
                    style={{
                      display: "block",
                      marginTop: 4,
                      color: "#6b7280",
                      fontSize: 12,
                    }}
                  >
                    Bağlantı çizgisi ve tüm stil ayarları uygulanır; her bloğun
                    kendi metni/resmi korunur.
                  </small>
                </div>

                <div className="qe-properties-subgroup">
                  <div className="qe-panel__subtitle">{t("props.pointSettings")}</div>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "matching-point",
                      propertyPath: "data.appearance.pointSize",
                      label: t("props.pointSize"),
                      controlType: "number",
                      min: 8,
                      max: 100,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.pointSize"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.pointSize")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.pointSizePx")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={8}
                        max={100}
                        value={matchingPointAppearance.pointSize}
                        onChange={(e) => {
                          const newSize = parseInt(e.target.value) || 16;
                          updateMatchingPointAppearance({
                            pointSize: newSize,
                          });
                          // Element boyutunu da güncelle
                          updateElementSize({
                            width: newSize,
                            height: newSize,
                          });
                        }}
                      />
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "matching-point",
                      propertyPath: "data.appearance.borderWidth",
                      label: t("props.borderThickness"),
                      controlType: "number",
                      min: 0,
                      max: 10,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.borderWidth"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.borderWidth")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">
                        {t("props.borderThicknessPx")}
                      </span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        max={10}
                        value={matchingPointAppearance.borderWidth}
                        onChange={(e) =>
                          updateMatchingPointAppearance({
                            borderWidth: parseInt(e.target.value) || 0,
                          })
                        }
                      />
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "matching-point",
                      propertyPath: "data.appearance.borderRadius",
                      label: t("props.borderRadius"),
                      controlType: "number",
                      min: 0,
                      max: 50,
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.borderRadius"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath("data.appearance.borderRadius")
                    }
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">
                        {t("props.cornerRoundnessPct")}
                      </span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        max={50}
                        value={matchingPointAppearance.borderRadius}
                        onChange={(e) =>
                          updateMatchingPointAppearance({
                            borderRadius: Math.min(
                              50,
                              Math.max(0, parseInt(e.target.value) || 0)
                            ),
                          })
                        }
                      />
                      <small className="qe-form__hint">
                        0% = Kare, 50% = Tam Yuvarlak
                      </small>
                    </div>
                  </ShortcutableControl>

                  <ShortcutableControl
                    shortcutConfig={{
                      elementType: "matching-point",
                      propertyPath: "data.appearance.enableHoverEffect",
                      label: t("props.hoverEffect"),
                      controlType: "toggle",
                    }}
                    onAddShortcut={onAddShortcut}
                    isInShortcuts={isPropertyInShortcuts(
                      "data.appearance.enableHoverEffect"
                    )}
                    onRemoveShortcut={() =>
                      handleRemoveShortcutByPath(
                        "data.appearance.enableHoverEffect"
                      )
                    }
                  >
                    <div className="qe-form__field">
                      <label className="qe-toggle">
                        <input
                          type="checkbox"
                          checked={matchingPointAppearance.enableHoverEffect}
                          onChange={(e) =>
                            updateMatchingPointAppearance({
                              enableHoverEffect: e.target.checked,
                            })
                          }
                        />
                        <span className="qe-toggle__slider"></span>
                        <span className="qe-toggle__label">
                          {t("props.hoverEffect")}
                        </span>
                      </label>
                      <small className="qe-form__hint">
                        {t("props.hoverEffectDesc")}
                      </small>
                    </div>
                  </ShortcutableControl>
                </div>

                {matchingPointType === "source" && (
                  <>
                    <div className="qe-properties-subgroup">
                      <div className="qe-panel__subtitle">
                        {t("props.connectionLine")}
                      </div>

                      <div className="qe-form__field">
                        <label
                          className="qe-toggle"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={
                              (elementData.allowMultipleConnections as boolean) ||
                              false
                            }
                            onChange={(e) =>
                              onUpdate(nonNullElement.id, {
                                data: {
                                  ...elementData,
                                  allowMultipleConnections: e.target.checked,
                                },
                              })
                            }
                          />
                          <span style={{ fontSize: "13px", fontWeight: 500 }}>
                            {t("props.allowMultipleConnections")}
                          </span>
                        </label>
                        <small
                          style={{
                            display: "block",
                            marginTop: "4px",
                            color: "#6b7280",
                            fontSize: "12px",
                          }}
                        >
                          {t("props.allowMultipleConnectionsDesc")}
                        </small>
                      </div>
                    </div>

                    <div className="qe-properties-subgroup">
                      <div className="qe-panel__subtitle">
                        Source Point Renkleri
                      </div>

                      <ShortcutableControl
                        shortcutConfig={{
                          elementType: "matching-point",
                          propertyPath: "data.appearance.sourceColor",
                          label: t("props.sourceColor"),
                          controlType: "color",
                        }}
                        onAddShortcut={onAddShortcut}
                        isInShortcuts={isPropertyInShortcuts(
                          "data.appearance.sourceColor"
                        )}
                        onRemoveShortcut={() =>
                          handleRemoveShortcutByPath(
                            "data.appearance.sourceColor"
                          )
                        }
                      >
                        {renderColorControl(
                          t("props.sourceColor"),
                          matchingPointAppearance.sourceColor,
                          (color) =>
                            updateMatchingPointAppearance({
                              sourceColor: color,
                            })
                        )}
                      </ShortcutableControl>

                      <ShortcutableControl
                        shortcutConfig={{
                          elementType: "matching-point",
                          propertyPath: "data.appearance.sourceBorderColor",
                          label: t("props.sourceBorderColor"),
                          controlType: "color",
                        }}
                        onAddShortcut={onAddShortcut}
                        isInShortcuts={isPropertyInShortcuts(
                          "data.appearance.sourceBorderColor"
                        )}
                        onRemoveShortcut={() =>
                          handleRemoveShortcutByPath(
                            "data.appearance.sourceBorderColor"
                          )
                        }
                      >
                        {renderColorControl(
                          t("props.sourceBorderColor"),
                          matchingPointAppearance.sourceBorderColor,
                          (color) =>
                            updateMatchingPointAppearance({
                              sourceBorderColor: color,
                            })
                        )}
                      </ShortcutableControl>
                    </div>

                    {matchingPointType === "source" && (
                      <div className="qe-properties-subgroup">
                        <div className="qe-panel__subtitle">
                          {t("props.connectionLine")}
                        </div>

                        <ShortcutableControl
                          shortcutConfig={{
                            elementType: "matching-point",
                            propertyPath: "data.appearance.connectionColor",
                            label: t("props.connectionColor"),
                            controlType: "color",
                          }}
                          onAddShortcut={onAddShortcut}
                          isInShortcuts={isPropertyInShortcuts(
                            "data.appearance.connectionColor"
                          )}
                          onRemoveShortcut={() =>
                            handleRemoveShortcutByPath(
                              "data.appearance.connectionColor"
                            )
                          }
                        >
                          {renderColorControl(
                            t("props.connectionColor"),
                            matchingPointAppearance.connectionColor,
                            (color) =>
                              updateMatchingPointAppearance({
                                connectionColor: color,
                              })
                          )}
                        </ShortcutableControl>

                        <ShortcutableControl
                          shortcutConfig={{
                            elementType: "matching-point",
                            propertyPath: "data.appearance.connectionWidth",
                            label: t("props.connectionWidth"),
                            controlType: "number",
                            min: 1,
                            max: 10,
                          }}
                          onAddShortcut={onAddShortcut}
                          isInShortcuts={isPropertyInShortcuts(
                            "data.appearance.connectionWidth"
                          )}
                          onRemoveShortcut={() =>
                            handleRemoveShortcutByPath(
                              "data.appearance.connectionWidth"
                            )
                          }
                        >
                          <div className="qe-form__field">
                            <span className="qe-form__label">
                              {t("props.connectionThickness")}
                            </span>
                            <input
                              className="qe-input"
                              type="number"
                              min={1}
                              max={10}
                              value={
                                connectionWidthInput !== null
                                  ? connectionWidthInput
                                  : matchingPointAppearance.connectionWidth
                              }
                              onChange={(e) => {
                                // Yazarken sadece local state'i güncelle, kontrol yapma
                                setConnectionWidthInput(e.target.value);
                              }}
                              onFocus={(e) => {
                                // Focus olduğunda local state'i başlat
                                setConnectionWidthInput(
                                  e.target.value ||
                                    String(
                                      matchingPointAppearance.connectionWidth
                                    )
                                );
                              }}
                              onBlur={(e) => {
                                // Focus kaybolduğunda validate et ve düzelt
                                const value = e.target.value.trim();
                                const numValue = parseInt(value, 10);

                                // Geçersiz değerler için varsayılan değer
                                let finalValue: number;
                                if (!value || isNaN(numValue) || numValue < 1) {
                                  finalValue = 2; // Varsayılan değer
                                } else if (numValue > 10) {
                                  finalValue = 10; // Max değer
                                } else {
                                  finalValue = numValue;
                                }

                                // Değeri güncelle
                                updateMatchingPointAppearance({
                                  connectionWidth: finalValue,
                                });

                                // Local state'i temizle
                                setConnectionWidthInput(null);
                              }}
                            />
                          </div>
                        </ShortcutableControl>

                        <ShortcutableControl
                          shortcutConfig={{
                            elementType: "matching-point",
                            propertyPath: "data.appearance.connectionStyle",
                            label: t("props.connectionStyle"),
                            controlType: "select",
                            options: [
                              { value: "solid", label: t("props.borderStyleSolid") },
                              { value: "dashed", label: t("props.borderStyleDashed") },
                              { value: "dotted", label: t("props.borderStyleDotted") },
                            ],
                          }}
                          onAddShortcut={onAddShortcut}
                          isInShortcuts={isPropertyInShortcuts(
                            "data.appearance.connectionStyle"
                          )}
                          onRemoveShortcut={() =>
                            handleRemoveShortcutByPath(
                              "data.appearance.connectionStyle"
                            )
                          }
                        >
                          <div className="qe-form__field">
                            <span className="qe-form__label">
                              {t("props.connectionStyle")}
                            </span>
                            <select
                              className="qe-input"
                              value={matchingPointAppearance.connectionStyle}
                              onChange={(e) =>
                                updateMatchingPointAppearance({
                                  connectionStyle: e.target.value as
                                    | "solid"
                                    | "dashed"
                                    | "dotted",
                                })
                              }
                            >
                              <option value="solid">{t("props.borderStyleSolid")}</option>
                              <option value="dashed">{t("props.borderStyleDashed")}</option>
                              <option value="dotted">{t("props.borderStyleDotted")}</option>
                            </select>
                          </div>
                        </ShortcutableControl>
                      </div>
                    )}
                  </>
                )}

                {matchingPointType === "target" && (
                  <div className="qe-properties-subgroup">
                    <div className="qe-panel__subtitle">
                      Target Point Renkleri
                    </div>

                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "matching-point",
                        propertyPath: "data.appearance.targetColor",
                        label: t("props.targetColor"),
                        controlType: "color",
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.appearance.targetColor"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath(
                          "data.appearance.targetColor"
                        )
                      }
                    >
                      {renderColorControl(
                        t("props.targetColor"),
                        matchingPointAppearance.targetColor,
                        (color) =>
                          updateMatchingPointAppearance({ targetColor: color })
                      )}
                    </ShortcutableControl>

                    <ShortcutableControl
                      shortcutConfig={{
                        elementType: "matching-point",
                        propertyPath: "data.appearance.targetBorderColor",
                        label: t("props.targetBorderColor"),
                        controlType: "color",
                      }}
                      onAddShortcut={onAddShortcut}
                      isInShortcuts={isPropertyInShortcuts(
                        "data.appearance.targetBorderColor"
                      )}
                      onRemoveShortcut={() =>
                        handleRemoveShortcutByPath(
                          "data.appearance.targetBorderColor"
                        )
                      }
                    >
                      {renderColorControl(
                        t("props.targetBorderColor"),
                        matchingPointAppearance.targetBorderColor,
                        (color) =>
                          updateMatchingPointAppearance({
                            targetBorderColor: color,
                          })
                      )}
                    </ShortcutableControl>
                  </div>
                )}
              </div>
            </AccordionContent>
          </div>
        ) : null}

        {/* Paint Area Settings Section */}
        {nonNullElement.type === "paint-area" && paintAreaAppearance ? (
          <div
            className={`qe-properties-section ${
              openSection === "paint-area-settings"
                ? "qe-properties-section--active"
                : ""
            }`}
          >
            <div
              className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                openSection === "paint-area-settings"
                  ? "qe-properties-section__title--open"
                  : "qe-properties-section__title--closed"
              }`}
              onClick={() =>
                setOpenSection(
                  openSection === "paint-area-settings"
                    ? null
                    : "paint-area-settings"
                )
              }
            >
              <span>{t("props.paintAreaSettings")}</span>
              <svg
                className={`qe-properties-section__icon ${
                  openSection === "paint-area-settings"
                    ? "qe-properties-section__icon--open"
                    : ""
                }`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <AccordionContent isOpen={openSection === "paint-area-settings"}>
              <div
                className="qe-properties-section__content"
                style={{ padding: 0 }}
              >
                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.shapeName")}</span>
                  <input
                    className="qe-input"
                    type="text"
                    value={paintAreaAppearance.name ?? ""}
                    onChange={(e) =>
                      updatePaintAreaAppearance({ name: e.target.value })
                    }
                    placeholder={t("props.shapeNamePlaceholder")}
                  />
                </div>
                {/* Doğru cevap rengi — önizlemede geri bildirim için */}
                <div className="qe-form__field">
                  {renderColorControl(
                    "Doğru Renk (cevap)",
                    paintAreaAppearance.correctColor ?? "transparent",
                    (next) =>
                      updatePaintAreaAppearance({
                        correctColor:
                          next === "transparent" ? undefined : next,
                      }),
                    { allowTransparent: true }
                  )}
                  <span className="qe-form__help">
                    Öğrenci bu şekli bu renge boyarsa yeşil çerçeve, farklı
                    renge boyarsa titreme gösterilir. Boş bırakılırsa geri
                    bildirim olmaz.
                  </span>
                </div>
                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.shapeType")}</span>
                  <select
                    className="qe-input"
                    value={paintAreaAppearance.shapeType}
                    onChange={(e) => {
                      const nextShape = e.target
                        .value as PaintAreaAppearance["shapeType"];
                      const nextAppearance = {
                        ...resolvePaintAreaAppearance(nonNullElement),
                        shapeType: nextShape,
                      };
                      const dataUpdate: Partial<EditorElement["data"]> = {
                        appearance: nextAppearance,
                      };
                      // Seed default vertices the first time a free polygon is
                      // picked, so the user has handles to drag.
                      if (nextShape === "polygon") {
                        const existing = (
                          nonNullElement.data as {
                            points?: Array<{ x: number; y: number }>;
                          }
                        ).points;
                        if (!Array.isArray(existing) || existing.length < 3) {
                          dataUpdate.points = DEFAULT_FREE_POLYGON_POINTS.map(
                            (p) => ({ ...p })
                          );
                        }
                        dataUpdate.editVertices = true;
                      }
                      updateElementData(dataUpdate);
                    }}
                  >
                    {PAINT_SHAPE_PICKER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.labelTr}
                      </option>
                    ))}
                    {/* Keep legacy values selectable if an old question uses one. */}
                    {![
                      ...PAINT_SHAPE_PICKER_OPTIONS.map((o) => o.value),
                    ].includes(paintAreaAppearance.shapeType) && (
                      <option value={paintAreaAppearance.shapeType}>
                        {paintAreaAppearance.shapeType}
                      </option>
                    )}
                  </select>
                </div>
                {paintAreaAppearance.shapeType === "polygon" && (
                  <div className="qe-form__field">
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        cursor: "pointer",
                      }}
                      title="Açıkken köşe noktalarını canvasta sürükleyebilirsin; kapalıyken boyama alanını normal şekilde büyütüp küçültebilirsin."
                    >
                      <input
                        type="checkbox"
                        checked={
                          (nonNullElement.data as { editVertices?: boolean })
                            .editVertices === true
                        }
                        onChange={(e) =>
                          updateElementData({ editVertices: e.target.checked })
                        }
                      />
                      <span>Köşe düzenleme modu (köşeleri sürükle)</span>
                    </label>
                    <small style={{ color: "#94a3b8", fontSize: 11 }}>
                      Boyama alanını yeniden boyutlandırmak için bu modu kapat.
                    </small>
                  </div>
                )}
                <div className="qe-form__field">
                  {renderColorControl(
                    t("props.borderColor"),
                    paintAreaAppearance.borderColor,
                    (color) =>
                      updatePaintAreaAppearance({
                        borderColor: color,
                      }),
                    { allowTransparent: false }
                  )}
                </div>
                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.borderWidth")}</span>
                  <input
                    className="qe-input"
                    type="number"
                    min={0}
                    max={20}
                    {...createNumberInputHandlers(
                      paintAreaAppearance.borderWidth,
                      0,
                      20,
                      (value) =>
                        updatePaintAreaAppearance({ borderWidth: value })
                    )}
                  />
                </div>
                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.borderStyle")}</span>
                  <select
                    className="qe-input"
                    value={paintAreaAppearance.borderStyle}
                    onChange={(e) =>
                      updatePaintAreaAppearance({
                        borderStyle: e.target.value as
                          | "solid"
                          | "dashed"
                          | "dotted"
                          | "none",
                      })
                    }
                  >
                    <option value="solid">{t("props.borderStyleSolid")}</option>
                    <option value="dashed">{t("props.borderStyleDashed")}</option>
                    <option value="dotted">{t("props.borderStyleDotted")}</option>
                    <option value="none">{t("props.none")}</option>
                  </select>
                </div>
                <div className="qe-form__field">
                  <div className="qe-form__help">
                    {t("props.paintAreaColorHelp")}
                  </div>
                </div>
              </div>
            </AccordionContent>
          </div>
        ) : null}

        {/* Click-to-Count ("Tıklayarak Sayma") Settings */}
        {nonNullElement.type === "count-area" ? (
          <AccordionSection
            title="Sayma Alanı Ayarları"
            isOpen={openSection === "count-area"}
            onToggle={() =>
              setOpenSection(openSection === "count-area" ? null : "count-area")
            }
          >
            {(() => {
              const data = elementData as {
                items?: Array<{
                  id: string;
                  type: string;
                  imageUrl: string;
                  position: { x: number; y: number };
                  size: { width: number; height: number };
                }>;
                appearance?: Record<string, unknown>;
              };
              const items = data.items ?? [];
              const ap = (data.appearance ?? {}) as {
                showNumbers?: boolean;
                speakOnClick?: boolean;
                numberingMode?: "total" | "per-type";
                numberBadgeBg?: string;
                numberBadgeColor?: string;
                numberBadgeSize?: number;
                numberBadgeFontWeight?: 400 | 500 | 600 | 700 | 800;
                numberBadgePosition?:
                  | "top-left"
                  | "top-right"
                  | "bottom-right"
                  | "bottom-left"
                  | "center";
                numberBadgeRadius?: number;
                backgroundColor?: string;
                borderColor?: string;
                borderWidth?: number;
                borderStyle?: "solid" | "dashed" | "dotted";
                showFrame?: boolean;
                startNumber?: number;
                incrementBy?: number;
                itemGap?: number;
                rowCount?: number; // satır sayısı (0 = otomatik)
                itemSize?: number;
              };
              const setItems = (next: typeof items) =>
                updateElementData({ items: next });
              const setAppearance = (patch: Record<string, unknown>) =>
                updateElementData({ appearance: { ...ap, ...patch } });

              // Auto-layout: place items as a grid sized to the area width.
              const ITEM_SIZE = ap.itemSize ?? 90;
              const PAD = 20;
              const GAP = ap.itemGap ?? 10;
              const areaW = nonNullElement.size.width - 2 * PAD;
              // "Satır" alanı SATIR SAYISI'dır (0 = otomatik). Satır başına
              // düşen öğe sayısını, toplam öğe / satır sayısına göre
              // hesaplarız. Böylece 1 satır → hepsi tek sırada, 3 satır →
              // 3 sıraya yayılır (öğe sayısı kadar).
              const computePerRow = (
                count: number,
                rows: number,
                size: number,
                gap: number
              ) => {
                if (rows > 0) {
                  return Math.max(1, Math.ceil(count / rows));
                }
                // 0 = otomatik: alan genişliğine göre sığdır
                return Math.max(1, Math.floor((areaW + gap) / (size + gap)));
              };
              const layoutAll = (list: typeof items) => {
                const perRow = computePerRow(
                  list.length,
                  ap.rowCount ?? 0,
                  ITEM_SIZE,
                  GAP
                );
                return list.map((it, idx) => {
                  const col = idx % perRow;
                  const row = Math.floor(idx / perRow);
                  return {
                    ...it,
                    position: {
                      x: PAD + col * (ITEM_SIZE + GAP),
                      y: PAD + row * (ITEM_SIZE + GAP),
                    },
                    size: { width: ITEM_SIZE, height: ITEM_SIZE },
                  };
                });
              };
              const reflowWith = (patch: {
                itemGap?: number;
                rowCount?: number;
                itemSize?: number;
              }) => {
                const newGap = patch.itemGap ?? GAP;
                const newSize = patch.itemSize ?? ITEM_SIZE;
                const newRowCount =
                  patch.rowCount !== undefined
                    ? patch.rowCount
                    : ap.rowCount ?? 0;
                const pr = computePerRow(
                  items.length,
                  newRowCount,
                  newSize,
                  newGap
                );
                const relaid = items.map((it, idx) => {
                  const col = idx % pr;
                  const row = Math.floor(idx / pr);
                  return {
                    ...it,
                    position: {
                      x: PAD + col * (newSize + newGap),
                      y: PAD + row * (newSize + newGap),
                    },
                    size: { width: newSize, height: newSize },
                  };
                });
                updateElementData({
                  items: relaid,
                  appearance: { ...ap, ...patch },
                });
              };
              const setGap = (n: number) =>
                reflowWith({ itemGap: Math.max(0, Math.min(60, Math.floor(n))) });
              const setRowCount = (n: number) =>
                reflowWith({
                  rowCount: Math.max(0, Math.min(20, Math.floor(n))),
                });
              const setItemSize = (n: number) =>
                reflowWith({
                  itemSize: Math.max(20, Math.min(400, Math.floor(n))),
                });

              // Group items by `type` — one inspector row per type.
              const groups = (() => {
                const map = new Map<
                  string,
                  { type: string; count: number; imageUrl: string }
                >();
                items.forEach((it) => {
                  const existing = map.get(it.type);
                  if (existing) {
                    existing.count += 1;
                    if (!existing.imageUrl && it.imageUrl)
                      existing.imageUrl = it.imageUrl;
                  } else {
                    map.set(it.type, {
                      type: it.type,
                      count: 1,
                      imageUrl: it.imageUrl || "",
                    });
                  }
                });
                return Array.from(map.values());
              })();

              const setGroupCount = (groupType: string, count: number) => {
                const n = Math.max(0, Math.min(50, Math.floor(count)));
                const others = items.filter((it) => it.type !== groupType);
                const same = items.filter((it) => it.type === groupType);
                const imageUrl = same[0]?.imageUrl || "";
                const newSame: typeof items = [];
                for (let i = 0; i < n; i++) {
                  if (same[i]) {
                    newSame.push(same[i]);
                  } else {
                    newSame.push({
                      id: `cnt-${Date.now()}-${Math.random()
                        .toString(36)
                        .slice(2, 6)}`,
                      type: groupType,
                      imageUrl,
                      position: { x: 0, y: 0 },
                      size: { width: ITEM_SIZE, height: ITEM_SIZE },
                    });
                  }
                }
                setItems(layoutAll([...others, ...newSame]));
              };
              const renameGroup = (oldType: string, newType: string) => {
                if (!newType.trim() || newType === oldType) return;
                setItems(
                  items.map((it) =>
                    it.type === oldType ? { ...it, type: newType } : it
                  )
                );
              };
              const setGroupImage = (groupType: string, url: string) => {
                setItems(
                  items.map((it) =>
                    it.type === groupType ? { ...it, imageUrl: url } : it
                  )
                );
              };
              const deleteGroup = (groupType: string) => {
                setItems(layoutAll(items.filter((it) => it.type !== groupType)));
              };
              const addGroup = () => {
                const idx = groups.length + 1;
                const newType = `nesne${idx}`;
                setItems(
                  layoutAll([
                    ...items,
                    {
                      id: `cnt-${Date.now()}-${Math.random()
                        .toString(36)
                        .slice(2, 6)}`,
                      type: newType,
                      imageUrl: "",
                      position: { x: 0, y: 0 },
                      size: { width: ITEM_SIZE, height: ITEM_SIZE },
                    },
                  ])
                );
              };

              const checkboxLabelStyle: React.CSSProperties = {
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
                fontSize: 13,
              };
              const colLabelStyle: React.CSSProperties = {
                display: "flex",
                flexDirection: "column",
                fontSize: 12,
              };
              const colSpanStyle: React.CSSProperties = { color: "#94a3b8" };

              return (
                <>
                  {/* Behavior toggles */}
                  <div className="qe-form__field">
                    <label style={checkboxLabelStyle}>
                      <input
                        type="checkbox"
                        checked={ap.showNumbers !== false}
                        onChange={(e) =>
                          setAppearance({ showNumbers: e.target.checked })
                        }
                      />
                      <span>Tıklayınca üstüne numara yaz</span>
                    </label>
                  </div>
                  <div className="qe-form__field">
                    <label style={checkboxLabelStyle}>
                      <input
                        type="checkbox"
                        checked={ap.speakOnClick !== false}
                        onChange={(e) =>
                          setAppearance({ speakOnClick: e.target.checked })
                        }
                      />
                      <span>Tıklayınca sayıyı sesli oku</span>
                    </label>
                  </div>
                  <div className="qe-form__field">
                    <span className="qe-form__label">Numaralandırma Stili</span>
                    <div className="qe-style-toggle-group">
                      <button
                        type="button"
                        className={`qe-style-toggle ${
                          (ap.numberingMode ?? "total") === "total"
                            ? "qe-style-toggle--active"
                            : ""
                        }`}
                        onClick={() => setAppearance({ numberingMode: "total" })}
                        title="Hepsini birlikte say: 1, 2, 3, 4…"
                      >
                        Toplamı numaralandır
                      </button>
                      <button
                        type="button"
                        className={`qe-style-toggle ${
                          ap.numberingMode === "per-type"
                            ? "qe-style-toggle--active"
                            : ""
                        }`}
                        onClick={() =>
                          setAppearance({ numberingMode: "per-type" })
                        }
                        title="Her tip ayrı sayılır (elma: 1, 2; muz: 1, 2)"
                      >
                        Ayrı ayrı numaralandır
                      </button>
                    </div>
                  </div>

                  {/* Counting series — start number & increment */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                      marginTop: 8,
                    }}
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">İlk Sayı</span>
                      <input
                        className="qe-input"
                        type="number"
                        step={1}
                        value={ap.startNumber ?? 1}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setAppearance({
                            startNumber: Number.isFinite(v) ? v : 1,
                          });
                        }}
                      />
                    </div>
                    <div className="qe-form__field">
                      <span className="qe-form__label">Artış Miktarı</span>
                      <input
                        className="qe-input"
                        type="number"
                        step={1}
                        value={ap.incrementBy ?? 1}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setAppearance({
                            incrementBy: Number.isFinite(v) && v !== 0 ? v : 1,
                          });
                        }}
                      />
                    </div>
                    <small
                      style={{
                        gridColumn: "1 / -1",
                        color: "#94a3b8",
                        fontSize: 11,
                      }}
                    >
                      Örnek: ilk sayı 2, artış 2 → 2, 4, 6, 8…
                    </small>
                  </div>

                  {/* Area frame */}
                  <div className="qe-properties-callout" style={{ marginTop: 4 }}>
                    <span className="qe-properties-callout__title">
                      🖼 Alan Çerçevesi
                    </span>
                    <div className="qe-form__field">
                      <label style={checkboxLabelStyle}>
                        <input
                          type="checkbox"
                          checked={ap.showFrame !== false}
                          onChange={(e) =>
                            setAppearance({ showFrame: e.target.checked })
                          }
                        />
                        <span>Çerçeveyi göster</span>
                      </label>
                    </div>
                    <div className="qe-form__field">
                      <span className="qe-form__label">Dolgu (arka plan)</span>
                      <div
                        style={{ display: "flex", gap: 6, alignItems: "center" }}
                      >
                        <input
                          type="color"
                          value={
                            ap.backgroundColor &&
                            ap.backgroundColor.startsWith("#")
                              ? ap.backgroundColor
                              : "#f8fafc"
                          }
                          onChange={(e) =>
                            setAppearance({ backgroundColor: e.target.value })
                          }
                          style={{
                            width: 40,
                            height: 30,
                            padding: 0,
                            border: "1px solid #cbd5e1",
                            borderRadius: 6,
                            cursor: "pointer",
                          }}
                        />
                        <button
                          type="button"
                          className="qe-button qe-button--ghost"
                          onClick={() =>
                            setAppearance({ backgroundColor: "transparent" })
                          }
                          style={{ fontSize: 11, padding: "4px 8px" }}
                          title="Arka planı şeffaf yap"
                        >
                          Şeffaf
                        </button>
                      </div>
                    </div>
                    {ap.showFrame !== false ? (
                      <>
                        <div className="qe-form__field">
                          <span className="qe-form__label">Kenarlık Rengi</span>
                          <input
                            type="color"
                            value={ap.borderColor ?? "#cbd5e1"}
                            onChange={(e) =>
                              setAppearance({ borderColor: e.target.value })
                            }
                            style={{
                              width: "100%",
                              height: 30,
                              padding: 0,
                              border: "1px solid #cbd5e1",
                              borderRadius: 6,
                              cursor: "pointer",
                            }}
                          />
                        </div>
                        <div
                          className="qe-form__field"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 8,
                          }}
                        >
                          <label style={colLabelStyle}>
                            <span style={colSpanStyle}>Kalınlık (px)</span>
                            <input
                              type="number"
                              min={0}
                              max={10}
                              value={ap.borderWidth ?? 2}
                              onChange={(e) =>
                                setAppearance({
                                  borderWidth: Math.max(
                                    0,
                                    Math.min(10, Number(e.target.value) || 0)
                                  ),
                                })
                              }
                              className="qe-input"
                              style={{ fontSize: 12 }}
                            />
                          </label>
                          <label style={colLabelStyle}>
                            <span style={colSpanStyle}>Stil</span>
                            <select
                              className="qe-input"
                              value={ap.borderStyle ?? "dashed"}
                              onChange={(e) =>
                                setAppearance({
                                  borderStyle: e.target.value as
                                    | "solid"
                                    | "dashed"
                                    | "dotted",
                                })
                              }
                              style={{ fontSize: 12 }}
                            >
                              <option value="solid">Düz</option>
                              <option value="dashed">Kesikli</option>
                              <option value="dotted">Noktalı</option>
                            </select>
                          </label>
                        </div>
                      </>
                    ) : null}
                  </div>

                  {/* Layout: item size + gap + per-row */}
                  <div
                    className="qe-form__field"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: 8,
                    }}
                  >
                    <label style={colLabelStyle}>
                      <span
                        style={colSpanStyle}
                        title="Resim oranı korunarak kutu boyutu"
                      >
                        Nesne (px)
                      </span>
                      <input
                        type="number"
                        min={20}
                        max={400}
                        value={ap.itemSize ?? 90}
                        onChange={(e) =>
                          setItemSize(Number(e.target.value) || 90)
                        }
                        className="qe-input"
                        style={{ fontSize: 12 }}
                      />
                    </label>
                    <label style={colLabelStyle}>
                      <span style={colSpanStyle}>Aralık (px)</span>
                      <input
                        type="number"
                        min={0}
                        max={60}
                        value={ap.itemGap ?? 10}
                        onChange={(e) => setGap(Number(e.target.value) || 0)}
                        className="qe-input"
                        style={{ fontSize: 12 }}
                      />
                    </label>
                    <label style={colLabelStyle}>
                      <span
                        style={colSpanStyle}
                        title="Kaç satıra yayılsın? 0 ise alan genişliğine göre otomatik. Örn. 1 = hepsi tek satır."
                      >
                        Satır (0=oto)
                      </span>
                      <input
                        type="number"
                        min={0}
                        max={20}
                        value={ap.rowCount ?? 0}
                        onChange={(e) =>
                          setRowCount(Number(e.target.value) || 0)
                        }
                        className="qe-input"
                        style={{ fontSize: 12 }}
                      />
                    </label>
                  </div>

                  {/* Number badge appearance */}
                  <div className="qe-properties-callout" style={{ marginTop: 4 }}>
                    <span className="qe-properties-callout__title">
                      🔢 Numara rozeti görünümü
                    </span>
                    <div
                      className="qe-form__field"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 8,
                      }}
                    >
                      <label style={colLabelStyle}>
                        <span style={colSpanStyle}>Boyut (px)</span>
                        <input
                          type="number"
                          min={16}
                          max={80}
                          value={ap.numberBadgeSize ?? 28}
                          onChange={(e) =>
                            setAppearance({
                              numberBadgeSize: Math.max(
                                16,
                                Math.min(80, Number(e.target.value) || 28)
                              ),
                            })
                          }
                          className="qe-input"
                          style={{ fontSize: 12 }}
                        />
                      </label>
                      <label style={colLabelStyle}>
                        <span style={colSpanStyle}>Kalınlık</span>
                        <select
                          className="qe-input"
                          value={ap.numberBadgeFontWeight ?? 700}
                          onChange={(e) =>
                            setAppearance({
                              numberBadgeFontWeight: Number(e.target.value) as
                                | 400
                                | 500
                                | 600
                                | 700
                                | 800,
                            })
                          }
                          style={{ fontSize: 12 }}
                        >
                          <option value="400">İnce</option>
                          <option value="500">Orta</option>
                          <option value="600">Yarı kalın</option>
                          <option value="700">Kalın</option>
                          <option value="800">Çok kalın</option>
                        </select>
                      </label>
                    </div>
                    <div
                      className="qe-form__field"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 8,
                      }}
                    >
                      <label style={colLabelStyle}>
                        <span style={colSpanStyle}>Konum</span>
                        <select
                          className="qe-input"
                          value={ap.numberBadgePosition ?? "center"}
                          onChange={(e) =>
                            setAppearance({
                              numberBadgePosition: e.target.value as
                                | "top-left"
                                | "top-right"
                                | "bottom-right"
                                | "bottom-left"
                                | "center",
                            })
                          }
                          style={{ fontSize: 12 }}
                        >
                          <option value="center">Orta</option>
                          <option value="top-left">Üst sol</option>
                          <option value="top-right">Üst sağ</option>
                          <option value="bottom-left">Alt sol</option>
                          <option value="bottom-right">Alt sağ</option>
                        </select>
                      </label>
                      <label style={colLabelStyle}>
                        <span style={colSpanStyle}>Köşe yuvarlaklığı (px)</span>
                        <input
                          type="number"
                          min={0}
                          max={999}
                          value={
                            ap.numberBadgeRadius ??
                            Math.round((ap.numberBadgeSize ?? 28) / 2)
                          }
                          onChange={(e) =>
                            setAppearance({
                              numberBadgeRadius: Math.max(
                                0,
                                Math.min(999, Number(e.target.value) || 0)
                              ),
                            })
                          }
                          className="qe-input"
                          style={{ fontSize: 12 }}
                        />
                      </label>
                    </div>
                    <div
                      className="qe-form__field"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 8,
                      }}
                    >
                      <label style={colLabelStyle}>
                        <span style={colSpanStyle}>Yazı rengi</span>
                        <input
                          type="color"
                          value={ap.numberBadgeColor ?? "#ffffff"}
                          onChange={(e) =>
                            setAppearance({ numberBadgeColor: e.target.value })
                          }
                          style={{
                            width: "100%",
                            height: 28,
                            padding: 0,
                            border: "1px solid #cbd5e1",
                            borderRadius: 6,
                            cursor: "pointer",
                          }}
                        />
                      </label>
                      <label style={colLabelStyle}>
                        <span style={colSpanStyle}>Arka plan</span>
                        <input
                          type="color"
                          value={ap.numberBadgeBg ?? "#1d4ed8"}
                          onChange={(e) =>
                            setAppearance({ numberBadgeBg: e.target.value })
                          }
                          style={{
                            width: "100%",
                            height: 28,
                            padding: 0,
                            border: "1px solid #cbd5e1",
                            borderRadius: 6,
                            cursor: "pointer",
                          }}
                        />
                      </label>
                    </div>
                  </div>

                  {/* Object types — each type: name, count, image, delete */}
                  <div className="qe-form__field">
                    <span className="qe-form__label">
                      Nesne Tipleri ({groups.length})
                    </span>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {groups.map((g) => (
                        <div
                          key={g.type}
                          style={{
                            border: "1px solid #cbd5e1",
                            borderRadius: 6,
                            padding: 8,
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                          }}
                        >
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 80px 28px",
                              gap: 6,
                              alignItems: "center",
                            }}
                          >
                            <input
                              className="qe-input"
                              type="text"
                              defaultValue={g.type}
                              placeholder="Tür (elma)"
                              onBlur={(e) =>
                                renameGroup(g.type, e.target.value.trim())
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  renameGroup(
                                    g.type,
                                    (e.target as HTMLInputElement).value.trim()
                                  );
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                              style={{ fontSize: 12 }}
                              title="Tür adı (Enter veya blur ile uygula)"
                            />
                            <input
                              className="qe-input"
                              type="number"
                              min={1}
                              max={50}
                              value={g.count}
                              onChange={(e) =>
                                setGroupCount(g.type, Number(e.target.value) || 1)
                              }
                              style={{ fontSize: 12 }}
                              title="Adet (1-50)"
                            />
                            <button
                              type="button"
                              onClick={() => deleteGroup(g.type)}
                              className="qe-button qe-button--ghost"
                              style={{
                                color: "#ef4444",
                                padding: "2px 6px",
                                fontSize: 12,
                              }}
                              title="Bu türü tamamen sil"
                            >
                              ✕
                            </button>
                          </div>
                          {/* Image: URL or local upload (stored as data URL) */}
                          <div
                            style={{
                              display: "flex",
                              gap: 6,
                              alignItems: "center",
                            }}
                          >
                            <input
                              className="qe-input"
                              type="text"
                              placeholder="Resim URL'i"
                              defaultValue={g.imageUrl}
                              onBlur={(e) =>
                                setGroupImage(g.type, e.target.value.trim())
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  setGroupImage(
                                    g.type,
                                    (e.target as HTMLInputElement).value.trim()
                                  );
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                              style={{ fontSize: 12, flex: 1 }}
                              title="Resim URL'i (Enter/blur ile uygula)"
                            />
                            <label
                              className="qe-button qe-button--ghost"
                              style={{
                                fontSize: 11,
                                padding: "6px 8px",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}
                              title="Bilgisayardan resim yükle"
                            >
                              Yükle
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const reader = new FileReader();
                                  reader.onload = () => {
                                    if (typeof reader.result === "string") {
                                      setGroupImage(g.type, reader.result);
                                    }
                                  };
                                  reader.readAsDataURL(file);
                                  e.target.value = "";
                                }}
                              />
                            </label>
                          </div>
                          <small
                            style={{
                              color: g.imageUrl ? "#22c55e" : "#94a3b8",
                              fontSize: 11,
                            }}
                          >
                            {g.imageUrl ? "✓ Resim yüklendi" : "Resim yüklenmedi"}
                          </small>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={addGroup}
                      className="qe-button qe-button--ghost"
                      style={{ marginTop: 6, width: "100%" }}
                    >
                      + Yeni Tip Ekle
                    </button>
                  </div>
                  <small style={{ color: "#94a3b8", fontSize: 11 }}>
                    Adeti değiştirmek otomatik olarak o sayı kadar nesne oluşturur
                    ve hedef alana grid olarak yerleştirir.
                  </small>
                </>
              );
            })()}
          </AccordionSection>
        ) : null}

        {/* Klavye (Keypad) Görünümü */}
        {nonNullElement.type === "keypad" ? (
          <AccordionSection
            title="Klavye Görünümü"
            isOpen={openSection === "keypad"}
            onToggle={() =>
              setOpenSection(openSection === "keypad" ? null : "keypad")
            }
          >
            {(() => {
              const data = elementData as {
                layout?: unknown;
                appearance?: Record<string, unknown>;
              };
              const keypadLayout: KeypadLayout =
                data.layout === "1-5" ||
                data.layout === "0-9" ||
                data.layout === "math"
                  ? data.layout
                  : "0-9";
              const ap = (data.appearance ?? {}) as {
                backgroundColor?: string;
                borderRadius?: number;
                padding?: number;
                gap?: number;
                keyBg?: string;
                keyHoverBg?: string;
                keyColor?: string;
                keyBorderColor?: string;
                keyBorderWidth?: number;
                keyBorderRadius?: number;
                keyFontFamily?: string;
                keyFontSize?: number;
                keyFontWeight?: 400 | 500 | 600 | 700 | 800;
              };
              const setAp = (patch: Record<string, unknown>) =>
                updateElementData({ appearance: { ...ap, ...patch } });

              return (
                <div className="qe-properties-section__content">
                  <div className="qe-form__field">
                    <span className="qe-form__label">Klavye Türü</span>
                    <select
                      className="qe-input"
                      value={keypadLayout}
                      onChange={(e) =>
                        updateElementData({
                          layout: e.target.value as KeypadLayout,
                        })
                      }
                    >
                      <option value="1-5">Sayılar 1-5</option>
                      <option value="0-9">Sayılar 0-9</option>
                      <option value="math">Dört İşlem</option>
                    </select>
                  </div>

                  {renderColorControl(
                    "Klavye Zemin Rengi",
                    ap.backgroundColor ?? "#1e3a8a",
                    (next) => setAp({ backgroundColor: next }),
                    { allowTransparent: true }
                  )}

                  <div className="qe-form__field">
                    <span className="qe-form__label">Köşe Yuvarlama (px)</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      value={ap.borderRadius ?? 12}
                      onChange={(e) =>
                        setAp({
                          borderRadius: Math.max(0, Number(e.target.value) || 0),
                        })
                      }
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                    }}
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">İç Boşluk</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        value={ap.padding ?? 8}
                        onChange={(e) =>
                          setAp({
                            padding: Math.max(0, Number(e.target.value) || 0),
                          })
                        }
                      />
                    </div>
                    <div className="qe-form__field">
                      <span className="qe-form__label">Tuşlar Arası</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        value={ap.gap ?? 6}
                        onChange={(e) =>
                          setAp({
                            gap: Math.max(0, Number(e.target.value) || 0),
                          })
                        }
                      />
                    </div>
                  </div>

                  <hr
                    style={{
                      border: "none",
                      borderTop: "1px solid #e5e7eb",
                      margin: "12px 0",
                    }}
                  />

                  {renderColorControl(
                    "Tuş Zemin Rengi",
                    ap.keyBg ?? "#ffffff",
                    (next) => setAp({ keyBg: next })
                  )}
                  {renderColorControl(
                    "Tuş Yazı Rengi",
                    ap.keyColor ?? "#1e3a8a",
                    (next) => setAp({ keyColor: next })
                  )}
                  {renderColorControl(
                    "Tuş Hover Zemin",
                    ap.keyHoverBg ?? "#e2e8f0",
                    (next) => setAp({ keyHoverBg: next })
                  )}
                  {renderColorControl(
                    "Tuş Kenar Rengi",
                    ap.keyBorderColor ?? "#cbd5e1",
                    (next) => setAp({ keyBorderColor: next })
                  )}

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                    }}
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">
                        Tuş Kenar Kalınlığı
                      </span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        value={ap.keyBorderWidth ?? 1}
                        onChange={(e) =>
                          setAp({
                            keyBorderWidth: Math.max(
                              0,
                              Number(e.target.value) || 0
                            ),
                          })
                        }
                      />
                    </div>
                    <div className="qe-form__field">
                      <span className="qe-form__label">Tuş Köşe Yuvarlama</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        value={ap.keyBorderRadius ?? 8}
                        onChange={(e) =>
                          setAp({
                            keyBorderRadius: Math.max(
                              0,
                              Number(e.target.value) || 0
                            ),
                          })
                        }
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                    }}
                  >
                    <div
                      className="qe-form__field"
                      style={{ gridColumn: "1 / -1" }}
                    >
                      <span className="qe-form__label">Font Ailesi</span>
                      <select
                        className="qe-input"
                        value={ap.keyFontFamily ?? "inherit"}
                        onChange={(e) =>
                          setAp({
                            keyFontFamily:
                              e.target.value === "inherit"
                                ? undefined
                                : e.target.value,
                          })
                        }
                      >
                        <option value="inherit">Varsayılan</option>
                        {FONT_OPTIONS.map((font) => (
                          <option key={font.value} value={font.value}>
                            {font.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="qe-form__field">
                      <span className="qe-form__label">Yazı Boyutu</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={8}
                        value={ap.keyFontSize ?? 22}
                        onChange={(e) =>
                          setAp({
                            keyFontSize: Math.max(8, Number(e.target.value) || 8),
                          })
                        }
                      />
                    </div>
                    <div className="qe-form__field">
                      <span className="qe-form__label">Yazı Kalınlığı</span>
                      <select
                        className="qe-input"
                        value={ap.keyFontWeight ?? 700}
                        onChange={(e) =>
                          setAp({
                            keyFontWeight: Number(e.target.value) as
                              | 400
                              | 500
                              | 600
                              | 700
                              | 800,
                          })
                        }
                      >
                        <option value={400}>Normal</option>
                        <option value={500}>Orta</option>
                        <option value={600}>Yarı Kalın</option>
                        <option value={700}>Kalın</option>
                        <option value={800}>Ekstra Kalın</option>
                      </select>
                    </div>
                  </div>
                </div>
              );
            })()}
          </AccordionSection>
        ) : null}

        {/* Oynat (Sesli Metin) Ayarları */}
        {nonNullElement.type === "play-button" ? (
          <AccordionSection
            title="Oynat Butonu"
            isOpen={openSection === "play-button"}
            onToggle={() =>
              setOpenSection(
                openSection === "play-button" ? null : "play-button"
              )
            }
          >
            {(() => {
              const data = elementData as {
                text?: string;
                language?: string;
                appearance?: Record<string, unknown>;
              };
              const ap = (data.appearance ?? {}) as {
                buttonFill?: string;
                buttonBorder?: string;
                buttonBorderWidth?: number;
                buttonBorderRadius?: number;
                triangleFill?: string;
                triangleBorder?: string;
                triangleBorderWidth?: number;
              };
              const setAp = (patch: Record<string, unknown>) =>
                updateElementData({ appearance: { ...ap, ...patch } });

              return (
                <div className="qe-properties-section__content">
                  <div className="qe-form__field">
                    <span className="qe-form__label">
                      Seslendirilecek Metin
                    </span>
                    <textarea
                      className="qe-input"
                      rows={3}
                      value={data.text ?? ""}
                      onChange={(e) =>
                        updateElementData({ text: e.target.value })
                      }
                      placeholder="Butona tıklandığında okunacak metni yazın…"
                      style={{ resize: "vertical", minHeight: 60 }}
                    />
                    <small style={{ color: "#94a3b8", fontSize: 11 }}>
                      Önizleme veya öğrenci modunda butona tıklayınca bu metin
                      sesli okunur.
                    </small>
                  </div>

                  <div className="qe-form__field">
                    <span className="qe-form__label">Dil</span>
                    <select
                      className="qe-input"
                      value={data.language ?? "tr-TR"}
                      onChange={(e) =>
                        updateElementData({ language: e.target.value })
                      }
                    >
                      <option value="tr-TR">Türkçe</option>
                      <option value="en-US">English (US)</option>
                      <option value="en-GB">English (UK)</option>
                      <option value="de-DE">Deutsch</option>
                      <option value="fr-FR">Français</option>
                      <option value="es-ES">Español</option>
                      <option value="ar-SA">العربية</option>
                    </select>
                  </div>

                  <hr
                    style={{
                      border: "none",
                      borderTop: "1px solid #e5e7eb",
                      margin: "12px 0",
                    }}
                  />

                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 12,
                      marginBottom: 6,
                      color: "#475569",
                    }}
                  >
                    Buton Çerçevesi
                  </div>
                  {renderColorControl(
                    "Dolgu Rengi",
                    ap.buttonFill ?? "#1e3a8a",
                    (next) => setAp({ buttonFill: next })
                  )}
                  {renderColorControl(
                    "Kenarlık Rengi",
                    ap.buttonBorder ?? "#1e3a8a",
                    (next) => setAp({ buttonBorder: next })
                  )}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                    }}
                  >
                    <div className="qe-form__field">
                      <span className="qe-form__label">Kenarlık Kalınlığı</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        value={ap.buttonBorderWidth ?? 2}
                        onChange={(e) =>
                          setAp({
                            buttonBorderWidth: Math.max(
                              0,
                              Number(e.target.value) || 0
                            ),
                          })
                        }
                      />
                    </div>
                    <div className="qe-form__field">
                      <span className="qe-form__label">Köşe Yuvarlama</span>
                      <input
                        className="qe-input"
                        type="number"
                        min={0}
                        max={50}
                        value={ap.buttonBorderRadius ?? 14}
                        onChange={(e) =>
                          setAp({
                            buttonBorderRadius: Math.max(
                              0,
                              Math.min(50, Number(e.target.value) || 0)
                            ),
                          })
                        }
                      />
                    </div>
                  </div>

                  <hr
                    style={{
                      border: "none",
                      borderTop: "1px solid #e5e7eb",
                      margin: "12px 0",
                    }}
                  />

                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 12,
                      marginBottom: 6,
                      color: "#475569",
                    }}
                  >
                    Oynat Üçgeni
                  </div>
                  {renderColorControl(
                    "Dolgu Rengi",
                    ap.triangleFill ?? "#ffffff",
                    (next) => setAp({ triangleFill: next })
                  )}
                  {renderColorControl(
                    "Kenarlık Rengi",
                    ap.triangleBorder ?? "#ffffff",
                    (next) => setAp({ triangleBorder: next })
                  )}
                  <div className="qe-form__field">
                    <span className="qe-form__label">Kenarlık Kalınlığı</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      value={ap.triangleBorderWidth ?? 0}
                      onChange={(e) =>
                        setAp({
                          triangleBorderWidth: Math.max(
                            0,
                            Number(e.target.value) || 0
                          ),
                        })
                      }
                    />
                  </div>
                </div>
              );
            })()}
          </AccordionSection>
        ) : null}

        {/* Küp Çubuğu (cube-bar) Ayarları */}
        {nonNullElement.type === "cube-bar" ? (
          <AccordionSection
            title="Küp Çubuğu Ayarları"
            isOpen={openSection === "cube-bar"}
            onToggle={() =>
              setOpenSection(openSection === "cube-bar" ? null : "cube-bar")
            }
          >
            {(() => {
              const d = elementData as {
                slotCount?: number;
                value?: number;
                expectedTotal?: number;
                cubeColor?: string;
                addedCubeColor?: string;
                emptySlotColor?: string;
                barBackground?: string;
                barBorderColor?: string;
              };
              const slotCount = d.slotCount ?? 10;
              const setSlotCount = (n: number) => {
                const nextValue = Math.min(d.value ?? 0, n);
                // Yuva sayısı değişince element genişliğini de güncelle
                // (kenar ~60px + stud + padding).
                onUpdate(nonNullElement.id, {
                  data: { ...elementData, slotCount: n, value: nextValue },
                  size: { width: n * 60 + 36, height: 84 },
                });
              };
              return (
                <div className="qe-properties-section__content">
                  <div className="qe-form__field">
                    <span className="qe-form__label">Yuva Sayısı</span>
                    <div className="qe-style-toggle-group">
                      {[5, 10].map((n) => (
                        <button
                          key={n}
                          type="button"
                          className={`qe-style-toggle ${
                            slotCount === n ? "qe-style-toggle--active" : ""
                          }`}
                          onClick={() => setSlotCount(n)}
                        >
                          {n} Yuva
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="qe-form__field">
                    <span className="qe-form__label">Başlangıç Küp Sayısı</span>
                    <input
                      className="qe-input"
                      type="number"
                      min={0}
                      max={slotCount}
                      value={d.value ?? 0}
                      onChange={(e) =>
                        updateElementData({
                          value: Math.max(
                            0,
                            Math.min(slotCount, Number(e.target.value) || 0)
                          ),
                        })
                      }
                    />
                    <small style={{ color: "#94a3b8", fontSize: 11 }}>
                      0 - {slotCount} arası. Soru başında dolu görünür.
                    </small>
                  </div>

                  {renderColorControl(
                    "Başlangıç Küp Rengi",
                    d.cubeColor ?? "#ec4899",
                    (next) => updateElementData({ cubeColor: next })
                  )}
                  {renderColorControl(
                    "Eklenen Küp Rengi",
                    d.addedCubeColor ?? "#60a5fa",
                    (next) => updateElementData({ addedCubeColor: next })
                  )}
                  {renderColorControl(
                    "Boş Yuva Rengi",
                    d.emptySlotColor ?? "#ffffff",
                    (next) => updateElementData({ emptySlotColor: next })
                  )}
                  {renderColorControl(
                    "Çubuk Arka Planı",
                    d.barBackground ?? "#ffffff",
                    (next) => updateElementData({ barBackground: next }),
                    { allowTransparent: true }
                  )}
                  {renderColorControl(
                    "Çubuk Kenar Rengi",
                    d.barBorderColor ?? "#cbd5e1",
                    (next) => updateElementData({ barBorderColor: next })
                  )}
                </div>
              );
            })()}
          </AccordionSection>
        ) : null}

        {/* Küp Kaynağı (cube-source) Ayarları */}
        {nonNullElement.type === "cube-source" ? (
          <AccordionSection
            title="Küp Kaynağı Ayarları"
            isOpen={openSection === "cube-source"}
            onToggle={() =>
              setOpenSection(
                openSection === "cube-source" ? null : "cube-source"
              )
            }
          >
            {(() => {
              const d = elementData as {
                cubeColor?: string;
                borderColor?: string;
                circleFill?: string;
              };
              return (
                <div className="qe-properties-section__content">
                  <small
                    style={{
                      display: "block",
                      color: "#94a3b8",
                      fontSize: 11,
                      marginBottom: 6,
                    }}
                  >
                    Öğrenci bu kaynaktan sürükleyerek çubuğa küp ekler
                    (sınırsız). Renkleri, çubuktaki "eklenen küp" renkleriyle
                    aynı tutmak görsel bütünlük sağlar.
                  </small>
                  {renderColorControl(
                    "Küp Rengi",
                    d.cubeColor ?? "#60a5fa",
                    (next) => updateElementData({ cubeColor: next })
                  )}
                  {renderColorControl(
                    "Kenar Rengi",
                    d.borderColor ?? "#2563eb",
                    (next) => updateElementData({ borderColor: next })
                  )}
                  {renderColorControl(
                    "Daire Rengi",
                    d.circleFill ?? "#93c5fd",
                    (next) => updateElementData({ circleFill: next })
                  )}
                </div>
              );
            })()}
          </AccordionSection>
        ) : null}

        {/* Geometric Shape Settings Section */}
        {nonNullElement.type === "shape" ? (
          <AccordionSection
            title="Şekil Ayarları"
            isOpen={openSection === "shape-settings"}
            onToggle={() =>
              setOpenSection(
                openSection === "shape-settings" ? null : "shape-settings"
              )
            }
          >
            {(() => {
              const kind = (elementData.kind as string) ?? "rectangle";
              const fill = (elementData.fill as string) ?? "#dbeafe";
              const stroke = (elementData.stroke as string) ?? "#1d4ed8";
              const strokeWidth =
                typeof elementData.strokeWidth === "number"
                  ? elementData.strokeWidth
                  : 2;
              const strokeStyle =
                (elementData.strokeStyle as string) ?? "solid";

              // Default aspect-lock status mirrors the renderer logic.
              const LOCK_DEFAULT_KINDS = new Set([
                "circle",
                "square",
                "triangle",
                "pentagon",
                "hexagon",
                "octagon",
                "star",
                "rhombus",
                "heart",
                "cube",
                "cylinder",
                "cone",
                "sphere",
                "pyramid",
              ]);
              const lockDefault = LOCK_DEFAULT_KINDS.has(kind);
              const locked =
                typeof elementData.lockAspectRatio === "boolean"
                  ? (elementData.lockAspectRatio as boolean)
                  : lockDefault;

              const SHAPES_2D: Array<[string, string]> = [
                ["rectangle", "Dikdörtgen"],
                ["square", "Kare"],
                ["circle", "Daire"],
                ["ellipse", "Elips"],
                ["triangle", "Üçgen"],
                ["right-triangle", "Dik Üçgen"],
                ["pentagon", "Beşgen"],
                ["hexagon", "Altıgen"],
                ["octagon", "Sekizgen"],
                ["star", "Yıldız"],
                ["rhombus", "Eşkenar Dörtgen"],
                ["trapezoid", "Yamuk"],
                ["parallelogram", "Paralelkenar"],
                ["arrow", "Ok"],
                ["line", "Çizgi"],
                ["heart", "Kalp"],
                ["crescent", "Hilal"],
                ["polygon", "Serbest Çokgen"],
              ];
              const SHAPES_3D: Array<[string, string]> = [
                ["cube", "Küp"],
                ["cylinder", "Silindir"],
                ["cone", "Koni"],
                ["sphere", "Küre"],
                ["pyramid", "Piramit"],
              ];

              return (
                <>
                  <div className="qe-form__field">
                    <span className="qe-form__label">Şekil</span>
                    <select
                      className="qe-input"
                      value={kind}
                      onChange={(e) => {
                        const newKind = e.target.value;
                        const patch: Record<string, unknown> = {
                          kind: newKind,
                        };
                        // When switching to a free polygon for the first time,
                        // seed a default 5-vertex shape.
                        if (newKind === "polygon") {
                          patch.editVertices = true;
                          if (!Array.isArray(elementData.points)) {
                            patch.points = [
                              { x: 50, y: 10 },
                              { x: 88, y: 38 },
                              { x: 73, y: 88 },
                              { x: 27, y: 88 },
                              { x: 12, y: 38 },
                            ];
                          }
                        }
                        updateElementData(patch);
                      }}
                    >
                      <optgroup label="2D">
                        {SHAPES_2D.map(([v, l]) => (
                          <option key={v} value={v}>
                            {l}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="3D Cisim">
                        {SHAPES_3D.map(([v, l]) => (
                          <option key={v} value={v}>
                            {l}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  {/* Aspect ratio lock toggle. */}
                  <div className="qe-form__field">
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        cursor: "pointer",
                      }}
                      title="Açıkken şekil 1:1 viewBox oranını korur; kapalıyken kutuya esner."
                    >
                      <input
                        type="checkbox"
                        checked={locked}
                        onChange={(e) =>
                          updateElementData({
                            lockAspectRatio: e.target.checked,
                          })
                        }
                      />
                      <span>Oranı koru (büyütüp küçültürken bozulmasın)</span>
                    </label>
                  </div>

                  {/* Convert preset shape → editable polygon. */}
                  {kind !== "polygon" &&
                  canExplodeToPolygon(kind as ShapeKind) ? (
                    <div className="qe-form__field">
                      <button
                        type="button"
                        className="qe-button qe-button--ghost"
                        onClick={() => {
                          const pts =
                            POLYGON_POINTS_FOR_SHAPE[kind as ShapeKind];
                          if (!pts) return;
                          updateElementData({
                            kind: "polygon",
                            points: pts.map((p) => ({ ...p })),
                            editVertices: true,
                          });
                        }}
                        style={{ width: "100%", fontSize: 12 }}
                        title="Şekli serbest polygon'a çevir — her köşeyi ayrı ayrı sürükleyebilirsin"
                      >
                        ✂ Köşelere Ayır (düzenlenebilir polygon)
                      </button>
                      <small style={{ color: "#94a3b8", fontSize: 11 }}>
                        Bu şeklin köşelerini tek tek düzenlemek için polygon'a
                        dönüştür. Geri almak için <kbd>⌘Z</kbd>.
                      </small>
                    </div>
                  ) : null}

                  {/* Polygon-specific: list vertices, allow add / remove / edit. */}
                  {kind === "polygon" ? (
                    <>
                    {/* Vertex-edit mode toggle. While on, the corner handles are
                        draggable on the canvas; turn it off to resize/move the
                        shape box normally. */}
                    <div className="qe-form__field">
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          cursor: "pointer",
                        }}
                        title="Açıkken köşe noktalarını canvasta sürükleyebilirsin; kapalıyken şekli normal şekilde büyütüp küçültebilirsin."
                      >
                        <input
                          type="checkbox"
                          checked={elementData.editVertices === true}
                          onChange={(e) =>
                            updateElementData({
                              editVertices: e.target.checked,
                            })
                          }
                        />
                        <span>Köşe düzenleme modu (köşeleri sürükle)</span>
                      </label>
                      <small style={{ color: "#94a3b8", fontSize: 11 }}>
                        Şekli yeniden boyutlandırmak için bu modu kapat.
                      </small>
                    </div>
                    <div className="qe-form__field">
                      <span className="qe-form__label">
                        Köşeler (
                        {Array.isArray(elementData.points)
                          ? (elementData.points as unknown[]).length
                          : 0}
                        )
                      </span>
                      {/* Column headers so it's clear which input is X and which is Y. */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "auto 1fr 1fr auto",
                          gap: 4,
                          alignItems: "center",
                          marginBottom: 2,
                        }}
                      >
                        <span style={{ width: 16 }} />
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#64748b",
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                          }}
                        >
                          X (yatay)
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#64748b",
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                          }}
                        >
                          Y (dikey)
                        </span>
                        <span />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                          maxHeight: 220,
                          overflowY: "auto",
                        }}
                      >
                        {(
                          (elementData.points as
                            | Array<{ x: number; y: number }>
                            | undefined) ?? []
                        ).map((p, i) => (
                          <div
                            key={i}
                            style={{
                              display: "grid",
                              gridTemplateColumns: "auto 1fr 1fr auto",
                              gap: 4,
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                color: "#64748b",
                                width: 16,
                              }}
                            >
                              {i + 1}
                            </span>
                            <input
                              className="qe-input"
                              type="number"
                              min={0}
                              max={100}
                              step={1}
                              value={Math.round(p.x)}
                              onChange={(e) => {
                                const v = Number(e.target.value);
                                if (!Number.isFinite(v)) return;
                                const pts = (
                                  elementData.points as Array<{
                                    x: number;
                                    y: number;
                                  }>
                                ).map((pp, idx) =>
                                  idx === i
                                    ? {
                                        ...pp,
                                        x: Math.max(0, Math.min(100, v)),
                                      }
                                    : pp
                                );
                                updateElementData({ points: pts });
                              }}
                              style={{ fontSize: 11 }}
                              title="X (0-100)"
                            />
                            <input
                              className="qe-input"
                              type="number"
                              min={0}
                              max={100}
                              step={1}
                              value={Math.round(p.y)}
                              onChange={(e) => {
                                const v = Number(e.target.value);
                                if (!Number.isFinite(v)) return;
                                const pts = (
                                  elementData.points as Array<{
                                    x: number;
                                    y: number;
                                  }>
                                ).map((pp, idx) =>
                                  idx === i
                                    ? {
                                        ...pp,
                                        y: Math.max(0, Math.min(100, v)),
                                      }
                                    : pp
                                );
                                updateElementData({ points: pts });
                              }}
                              style={{ fontSize: 11 }}
                              title="Y (0-100)"
                            />
                            <button
                              type="button"
                              className="qe-button qe-button--ghost"
                              onClick={() => {
                                const pts = (
                                  elementData.points as Array<{
                                    x: number;
                                    y: number;
                                  }>
                                ).filter((_, idx) => idx !== i);
                                if (pts.length < 3) return;
                                updateElementData({ points: pts });
                              }}
                              style={{
                                padding: "2px 6px",
                                fontSize: 11,
                                color: "#ef4444",
                              }}
                              title="Bu köşeyi sil"
                              disabled={
                                Array.isArray(elementData.points)
                                  ? (elementData.points as unknown[]).length <=
                                    3
                                  : true
                              }
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                        <button
                          type="button"
                          className="qe-button qe-button--ghost"
                          onClick={() => {
                            const pts =
                              (elementData.points as Array<{
                                x: number;
                                y: number;
                              }>) ?? [];
                            const a = pts[pts.length - 1] ?? { x: 50, y: 50 };
                            const b = pts[0] ?? { x: 50, y: 50 };
                            const mid = {
                              x: (a.x + b.x) / 2,
                              y: (a.y + b.y) / 2,
                            };
                            updateElementData({ points: [...pts, mid] });
                          }}
                          style={{ flex: 1, fontSize: 11 }}
                        >
                          + Köşe ekle
                        </button>
                        <button
                          type="button"
                          className="qe-button qe-button--ghost"
                          onClick={() => {
                            updateElementData({
                              points: [
                                { x: 50, y: 10 },
                                { x: 88, y: 38 },
                                { x: 73, y: 88 },
                                { x: 27, y: 88 },
                                { x: 12, y: 38 },
                              ],
                            });
                          }}
                          style={{ flex: 1, fontSize: 11 }}
                        >
                          Sıfırla (5'gen)
                        </button>
                      </div>
                      <small style={{ color: "#94a3b8", fontSize: 11 }}>
                        Sol sütun X (yatay), sağ sütun Y (dikey). Köşeleri
                        canvasta direkt sürükleyerek de hareket ettirebilirsin.
                        Koordinatlar 0–100 viewBox cinsinden.
                      </small>
                    </div>
                    </>
                  ) : null}

                  <div className="qe-form__field">
                    <span className="qe-form__label">Dolgu Rengi</span>
                    <div style={{ display: "flex", gap: 6 }}>
                      <input
                        type="color"
                        value={fill}
                        onChange={(e) =>
                          updateElementData({ fill: e.target.value })
                        }
                        style={{
                          width: 36,
                          height: 28,
                          padding: 0,
                          border: "1px solid #cbd5e1",
                          borderRadius: 4,
                          cursor: "pointer",
                        }}
                      />
                      <input
                        className="qe-input"
                        type="text"
                        value={fill}
                        onChange={(e) =>
                          updateElementData({ fill: e.target.value })
                        }
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>

                  <div className="qe-form__field">
                    <span className="qe-form__label">Kenar Rengi</span>
                    <div style={{ display: "flex", gap: 6 }}>
                      <input
                        type="color"
                        value={stroke}
                        onChange={(e) =>
                          updateElementData({ stroke: e.target.value })
                        }
                        style={{
                          width: 36,
                          height: 28,
                          padding: 0,
                          border: "1px solid #cbd5e1",
                          borderRadius: 4,
                          cursor: "pointer",
                        }}
                      />
                      <input
                        className="qe-input"
                        type="text"
                        value={stroke}
                        onChange={(e) =>
                          updateElementData({ stroke: e.target.value })
                        }
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>

                  <div className="qe-form__field">
                    <span className="qe-form__label">
                      Kenar Kalınlığı ({strokeWidth}px)
                    </span>
                    <input
                      type="range"
                      min={0}
                      max={20}
                      step={0.5}
                      value={strokeWidth}
                      onChange={(e) =>
                        updateElementData({
                          strokeWidth: Number(e.target.value),
                        })
                      }
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div className="qe-form__field">
                    <span className="qe-form__label">Kenar Tipi</span>
                    <div className="qe-style-toggle-group">
                      {(["solid", "dashed", "dotted"] as const).map((s) => (
                        <button
                          key={s}
                          type="button"
                          className={`qe-style-toggle ${
                            strokeStyle === s ? "qe-style-toggle--active" : ""
                          }`}
                          onClick={() => updateElementData({ strokeStyle: s })}
                        >
                          {s === "solid"
                            ? "Düz"
                            : s === "dashed"
                            ? "Kesik"
                            : "Noktalı"}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Corner rounding — only meaningful for polygon-based shapes. */}
                  {isRoundableShape(kind as ShapeKind) ? (
                    <div className="qe-form__field">
                      <span className="qe-form__label">
                        Köşe Yuvarlama (
                        {typeof elementData.cornerRadius === "number"
                          ? elementData.cornerRadius
                          : 0}
                        )
                      </span>
                      <input
                        type="range"
                        min={0}
                        max={50}
                        step={1}
                        value={
                          typeof elementData.cornerRadius === "number"
                            ? elementData.cornerRadius
                            : 0
                        }
                        onChange={(e) =>
                          updateElementData({
                            cornerRadius: Number(e.target.value),
                          })
                        }
                        style={{ width: "100%" }}
                      />
                      <small style={{ color: "#94a3b8", fontSize: 11 }}>
                        0 = sivri köşe, 50 = en yuvarlak.
                      </small>
                    </div>
                  ) : null}
                </>
              );
            })()}
          </AccordionSection>
        ) : null}

        {/* Color Palette Settings Section */}
        {nonNullElement.type === "color-palette" ? (
          <AccordionSection
            title="Renk Kartelası"
            isOpen={openSection === "color-palette"}
            onToggle={() =>
              setOpenSection(
                openSection === "color-palette" ? null : "color-palette"
              )
            }
          >
            {(() => {
              const palette = (elementData.colors as string[] | undefined) ?? [];
              const currentCount = palette.length;
              const currentCols =
                (elementData.columns as number | undefined) ?? 6;
              const preset12 = [
                "#ef4444",
                "#f97316",
                "#f59e0b",
                "#eab308",
                "#84cc16",
                "#22c55e",
                "#06b6d4",
                "#3b82f6",
                "#8b5cf6",
                "#ec4899",
                "#000000",
                "#ffffff",
              ];
              const presetsFor = (count: number) => {
                if (count === 6) return preset12.filter((_, i) => i % 2 === 0);
                if (count === 8) {
                  return [
                    "#ef4444",
                    "#f97316",
                    "#eab308",
                    "#22c55e",
                    "#06b6d4",
                    "#3b82f6",
                    "#8b5cf6",
                    "#000000",
                  ];
                }
                if (count === 10) return preset12.slice(0, 10);
                return preset12;
              };
              const suggestedCols = (count: number) =>
                count === 6 ? 3 : count === 8 ? 4 : count === 10 ? 5 : 6;
              const setCount = (count: number) => {
                const existing = palette.slice(0, count);
                const filler = presetsFor(count).slice(existing.length);
                updateElementData({
                  colors: [...existing, ...filler],
                  columns: suggestedCols(count),
                });
              };
              const setColorAt = (index: number, color: string) => {
                const next = [...palette];
                next[index] = color;
                updateElementData({ colors: next });
              };

              return (
                <>
                  <div className="qe-form__field">
                    <span className="qe-form__label">Renk Sayısı</span>
                    <div className="qe-style-toggle-group">
                      {[6, 8, 10, 12].map((count) => (
                        <button
                          key={count}
                          type="button"
                          className={`qe-style-toggle ${
                            currentCount === count
                              ? "qe-style-toggle--active"
                              : ""
                          }`}
                          onClick={() => setCount(count)}
                        >
                          {count}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="qe-form__field">
                    <span className="qe-form__label">
                      Sütun Sayısı ({currentCols})
                    </span>
                    <input
                      className="qe-input"
                      type="range"
                      min={1}
                      max={Math.max(1, currentCount)}
                      value={currentCols}
                      onChange={(e) =>
                        updateElementData({ columns: Number(e.target.value) })
                      }
                    />
                  </div>

                  <div className="qe-form__field">
                    <span className="qe-form__label">Hazır Paletler</span>
                    <button
                      type="button"
                      className="qe-button qe-button--ghost"
                      onClick={() => setCount(currentCount || 12)}
                      style={{ fontSize: 11 }}
                    >
                      Standart {currentCount || 12} renk
                    </button>
                  </div>

                  <div className="qe-form__field">
                    <span className="qe-form__label">Renkleri Düzenle</span>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: 6,
                      }}
                    >
                      {palette.map((color, index) => (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <input
                            type="color"
                            value={color}
                            onChange={(e) => setColorAt(index, e.target.value)}
                            style={{
                              width: 28,
                              height: 28,
                              padding: 0,
                              border: "1px solid #e5e7eb",
                              borderRadius: 4,
                              cursor: "pointer",
                            }}
                          />
                          <input
                            className="qe-input"
                            type="text"
                            value={color}
                            onChange={(e) => setColorAt(index, e.target.value)}
                            style={{ flex: 1, minWidth: 0, fontSize: 11 }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <label
                    className="qe-form__field"
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <input
                      type="checkbox"
                      checked={elementData.showLabels === true}
                      onChange={(e) =>
                        updateElementData({ showLabels: e.target.checked })
                      }
                    />
                    <span className="qe-form__label">
                      Renk kodlarını üzerinde göster
                    </span>
                  </label>
                </>
              );
            })()}
          </AccordionSection>
        ) : null}

        {/* Selected table cell(s) — Excel-style content & formatting */}
        {nonNullElement.type === "table" && selectedTableCells.length > 0 ? (
          (() => {
            const multi = selectedTableCells.length > 1;
            const primary = selectedTableCells[0];
            const sharedText = multi ? "" : primary.text ?? "";
            const sharedFontSize = commonCellStyle("fontSize");
            const sharedFontWeight = commonCellStyle("fontWeight");
            const sharedItalic = commonCellStyle("italic");
            const sharedUnderline = commonCellStyle("underline");
            const sharedAlign = commonCellStyle("textAlign");
            const sharedVAlign = commonCellStyle("verticalAlign");
            const sharedColor = commonCellStyle("color");
            const sharedBg = commonCellStyle("backgroundColor");

            const title = multi
              ? `Seçili Hücreler (${selectedTableCells.length})`
              : `Seçili Hücre (${primary.row + 1}, ${primary.col + 1})`;

            return (
              <AccordionSection
                title={title}
                isOpen={openSection === "selected-cell"}
                onToggle={() =>
                  setOpenSection(
                    openSection === "selected-cell" ? null : "selected-cell"
                  )
                }
              >
                {multi ? (
                  <div
                    style={{
                      padding: "6px 10px",
                      marginBottom: 8,
                      background: "#1e293b",
                      color: "#cbd5e1",
                      fontSize: 11,
                      borderRadius: 4,
                    }}
                  >
                    Stil değişiklikleri seçili tüm hücrelere uygulanır. Boş
                    görünen alanlar hücreler arasında farklı değerlere sahip
                    demektir.
                  </div>
                ) : null}

                {/* İçerik alanı yalnızca tek hücre seçiliyken görünür. */}
                {!multi ? (
                  <div className="qe-form__field">
                    <span className="qe-form__label">İçerik</span>
                    <input
                      className="qe-input"
                      type="text"
                      placeholder="Hücreye yazılacak metin"
                      value={sharedText}
                      onChange={(e) =>
                        updateSelectedCells({ text: e.target.value })
                      }
                    />
                  </div>
                ) : (
                  <div className="qe-form__field">
                    <button
                      type="button"
                      className="qe-button qe-button--ghost"
                      onClick={() => updateSelectedCells({ text: "" })}
                      style={{ width: "100%" }}
                    >
                      Tüm seçili hücrelerin içeriğini temizle
                    </button>
                  </div>
                )}

                {/* Add a rich element into the cell (not just text). */}
                {!multi ? (
                  <div className="qe-form__field">
                    <span className="qe-form__label">Hücreye Öğe Ekle</span>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 6,
                      }}
                    >
                      {(
                        [
                          ["text-basic", "Metin Bloğu"],
                          ["image-basic", "Resim"],
                          ["audio-basic", "Ses"],
                          ["slide-basic", "Video"],
                          ["mathtype-basic", "Matematik"],
                          ["shape-basic", "Geometrik Şekil"],
                        ] as Array<[ElementTemplateKey, string]>
                      ).map(([tpl, label]) => (
                        <button
                          key={tpl}
                          type="button"
                          className="qe-button qe-button--ghost"
                          onClick={() => addElementToCell(tpl)}
                          style={{ fontSize: 12 }}
                        >
                          + {label}
                        </button>
                      ))}
                    </div>
                    <small style={{ color: "#94a3b8", fontSize: 11 }}>
                      Öğe hücreye eklenir ve tablo "kilitli" moda geçer; öğeyi
                      tıklayıp özelliklerini (kaynak, formül vb.) düzenleyebilir,
                      sürükleyip yeniden boyutlandırabilirsin.
                    </small>
                  </div>
                ) : null}

                <div className="qe-form__field">
                  <span className="qe-form__label">Yazı Boyutu</span>
                  <input
                    className="qe-input"
                    type="number"
                    min="6"
                    max="200"
                    value={sharedFontSize ?? ""}
                    placeholder={multi ? "(farklı)" : "varsayılan"}
                    onChange={(e) => {
                      const raw = e.target.value.trim();
                      const next =
                        raw === ""
                          ? undefined
                          : Math.max(6, Math.min(200, Number(raw)));
                      updateSelectedCells({ cellStyle: { fontSize: next } });
                    }}
                  />
                </div>

                <div className="qe-form__field">
                  <span className="qe-form__label">Stil</span>
                  <div className="qe-style-toggle-group">
                    <button
                      type="button"
                      className={`qe-style-toggle ${
                        (sharedFontWeight ?? 400) >= 600
                          ? "qe-style-toggle--active"
                          : ""
                      }`}
                      onClick={() =>
                        updateSelectedCells({
                          cellStyle: {
                            fontWeight:
                              (sharedFontWeight ?? 400) >= 600 ? 400 : 700,
                          },
                        })
                      }
                      title="Kalın"
                    >
                      <strong>B</strong>
                    </button>
                    <button
                      type="button"
                      className={`qe-style-toggle ${
                        sharedItalic ? "qe-style-toggle--active" : ""
                      }`}
                      onClick={() =>
                        updateSelectedCells({
                          cellStyle: { italic: !sharedItalic },
                        })
                      }
                      title="İtalik"
                    >
                      <em>I</em>
                    </button>
                    <button
                      type="button"
                      className={`qe-style-toggle ${
                        sharedUnderline ? "qe-style-toggle--active" : ""
                      }`}
                      onClick={() =>
                        updateSelectedCells({
                          cellStyle: { underline: !sharedUnderline },
                        })
                      }
                      title="Altı çizili"
                    >
                      <u>U</u>
                    </button>
                  </div>
                </div>

                <div className="qe-form__field">
                  <span className="qe-form__label">Yatay Hizalama</span>
                  <div className="qe-style-toggle-group">
                    {(["left", "center", "right"] as const).map((align) => (
                      <button
                        key={align}
                        type="button"
                        className={`qe-style-toggle ${
                          sharedAlign === align ? "qe-style-toggle--active" : ""
                        }`}
                        onClick={() =>
                          updateSelectedCells({
                            cellStyle: { textAlign: align },
                          })
                        }
                        title={
                          align === "left"
                            ? "Sola"
                            : align === "center"
                            ? "Ortaya"
                            : "Sağa"
                        }
                      >
                        {align === "left"
                          ? "⬅"
                          : align === "center"
                          ? "↔"
                          : "➡"}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="qe-form__field">
                  <span className="qe-form__label">Dikey Hizalama</span>
                  <div className="qe-style-toggle-group">
                    {(["top", "middle", "bottom"] as const).map((vAlign) => (
                      <button
                        key={vAlign}
                        type="button"
                        className={`qe-style-toggle ${
                          sharedVAlign === vAlign
                            ? "qe-style-toggle--active"
                            : ""
                        }`}
                        onClick={() =>
                          updateSelectedCells({
                            cellStyle: { verticalAlign: vAlign },
                          })
                        }
                        title={
                          vAlign === "top"
                            ? "Üst"
                            : vAlign === "middle"
                            ? "Orta"
                            : "Alt"
                        }
                      >
                        {vAlign === "top"
                          ? "⬆"
                          : vAlign === "middle"
                          ? "↕"
                          : "⬇"}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="qe-form__field">
                  <span className="qe-form__label">Yazı Rengi</span>
                  <input
                    className="qe-input"
                    type="color"
                    value={sharedColor ?? "#000000"}
                    onChange={(e) =>
                      updateSelectedCells({
                        cellStyle: { color: e.target.value },
                      })
                    }
                  />
                </div>

                <div className="qe-form__field">
                  <span className="qe-form__label">Arka Plan</span>
                  <input
                    className="qe-input"
                    type="color"
                    value={sharedBg ?? "#ffffff"}
                    onChange={(e) =>
                      updateSelectedCells({
                        cellStyle: { backgroundColor: e.target.value },
                      })
                    }
                  />
                </div>

                <div className="qe-form__field">
                  <button
                    type="button"
                    className="qe-button qe-button--ghost"
                    onClick={() =>
                      updateSelectedCells({
                        cellStyle: null as unknown as TableCell["cellStyle"],
                      })
                    }
                    style={{ width: "100%" }}
                  >
                    {multi
                      ? "Tüm seçili hücrelerin stilini sıfırla"
                      : "Hücre stilini sıfırla"}
                  </button>
                </div>
              </AccordionSection>
            );
          })()
        ) : null}

        {/* Table Settings Section */}
        {nonNullElement.type === "table" && tableAppearance ? (
          <AccordionSection
            title={t("props.tableSettings")}
            isOpen={openSection === "table-settings"}
            onToggle={() =>
              setOpenSection(
                openSection === "table-settings" ? null : "table-settings"
              )
            }
          >
            {/* Lock Table Toggle */}
            <div className="qe-form__field">
              <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={(elementData.locked as boolean) || false}
                  onChange={(e) => updateElementData({ locked: e.target.checked })}
                  style={{ cursor: "pointer" }}
                />
                <span className="qe-form__label" style={{ margin: 0 }}>
                  {t("props.tableLock")}
                </span>
              </label>
            </div>
                {/* Boyut Ayarları */}
                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.dimensions")}</span>
                  <div className="qe-inline-group">
                    <label className="qe-inline-input">
                      <span className="qe-inline-input__label">{t("props.row")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min="1"
                        max="20"
                        value={tableRows}
                        onChange={(e) => {
                          const rows = Math.max(1, Math.min(20, Number(e.target.value)));
                          updateTableDimensions(rows, tableColumns);
                        }}
                      />
                    </label>
                    <label className="qe-inline-input">
                      <span className="qe-inline-input__label">{t("props.column")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min="1"
                        max="20"
                        value={tableColumns}
                        onChange={(e) => {
                          const cols = Math.max(1, Math.min(20, Number(e.target.value)));
                          updateTableDimensions(tableRows, cols);
                        }}
                      />
                    </label>
                  </div>
                </div>

                {/* Dış Kenarlık Ayarları */}
                <div className="qe-form__field">
                  <span className="qe-form__label" style={{ fontWeight: 600, marginBottom: "8px", display: "block" }}>
                    {t("props.outerBorder")}
                  </span>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={tableAppearance.showOuterBorder}
                      onChange={(e) =>
                        updateTableAppearance({ showOuterBorder: e.target.checked })
                      }
                      style={{
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                      }}
                    />
                    <span className="qe-form__label" style={{ margin: 0 }}>
                      {t("props.showOuterBorder")}
                    </span>
                  </label>
                </div>

                {tableAppearance.showOuterBorder && (
                  <>
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.width")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min="0"
                        max="10"
                        value={tableAppearance.outerBorderWidth}
                        onChange={(e) =>
                          updateTableAppearance({
                            outerBorderWidth: Number(e.target.value),
                          })
                        }
                      />
                    </div>

                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.style")}</span>
                      <select
                        className="qe-input"
                        value={tableAppearance.outerBorderStyle}
                        onChange={(e) =>
                          updateTableAppearance({
                            outerBorderStyle: e.target.value as any,
                          })
                        }
                      >
                        <option value="solid">{t("props.borderStyleSolid")}</option>
                        <option value="dashed">{t("props.borderStyleDashed")}</option>
                        <option value="dotted">{t("props.borderStyleDotted")}</option>
                        <option value="none">None</option>
                      </select>
                    </div>

                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.color")}</span>
                      <input
                        className="qe-input"
                        type="color"
                        value={tableAppearance.outerBorderColor}
                        onChange={(e) =>
                          updateTableAppearance({ outerBorderColor: e.target.value })
                        }
                      />
                    </div>
                  </>
                )}

                {/* İç Kenarlık Ayarları (Hücreler) */}
                <div className="qe-form__field" style={{ marginTop: "16px" }}>
                  <span className="qe-form__label" style={{ fontWeight: 600, marginBottom: "8px", display: "block" }}>
                    {t("props.innerBorders")}
                  </span>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={tableAppearance.showCellBorders}
                      onChange={(e) =>
                        updateTableAppearance({ showCellBorders: e.target.checked })
                      }
                      style={{
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                      }}
                    />
                    <span className="qe-form__label" style={{ margin: 0 }}>
                      {t("props.showCellBorders")}
                    </span>
                  </label>
                </div>

                {tableAppearance.showCellBorders && (
                  <>
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.width")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min="0"
                        max="10"
                        value={tableAppearance.cellBorderWidth}
                        onChange={(e) =>
                          updateTableAppearance({
                            cellBorderWidth: Number(e.target.value),
                          })
                        }
                      />
                    </div>

                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.style")}</span>
                      <select
                        className="qe-input"
                        value={tableAppearance.cellBorderStyle}
                        onChange={(e) =>
                          updateTableAppearance({
                            cellBorderStyle: e.target.value as any,
                          })
                        }
                      >
                        <option value="solid">{t("props.borderStyleSolid")}</option>
                        <option value="dashed">{t("props.borderStyleDashed")}</option>
                        <option value="dotted">{t("props.borderStyleDotted")}</option>
                        <option value="none">None</option>
                      </select>
                    </div>

                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.color")}</span>
                      <input
                        className="qe-input"
                        type="color"
                        value={tableAppearance.cellBorderColor}
                        onChange={(e) =>
                          updateTableAppearance({ cellBorderColor: e.target.value })
                        }
                      />
                    </div>
                  </>
                )}

                {/* Spacing */}
                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.spacings")}</span>
                  <div className="qe-inline-group">
                    <label className="qe-inline-input">
                      <span className="qe-inline-input__label">{t("props.row")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min="0"
                        max="50"
                        value={tableAppearance.rowSpacing}
                        onChange={(e) =>
                          updateTableAppearance({
                            rowSpacing: Number(e.target.value),
                          })
                        }
                      />
                    </label>
                    <label className="qe-inline-input">
                      <span className="qe-inline-input__label">{t("props.column")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        min="0"
                        max="50"
                        value={tableAppearance.columnSpacing}
                        onChange={(e) =>
                          updateTableAppearance({
                            columnSpacing: Number(e.target.value),
                          })
                        }
                      />
                    </label>
                  </div>
                </div>

                {/* Cell Padding */}
                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.cellPadding")}</span>
                  <input
                    className="qe-input"
                    type="number"
                    min="0"
                    max="50"
                    value={tableAppearance.cellPadding}
                    onChange={(e) =>
                      updateTableAppearance({
                        cellPadding: Number(e.target.value),
                      })
                    }
                  />
                </div>

                {/* Background Colors */}
                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.cellBackgroundColor")}</span>
                  <input
                    className="qe-input"
                    type="color"
                    value={tableAppearance.cellBackgroundColor}
                    onChange={(e) =>
                      updateTableAppearance({
                        cellBackgroundColor: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Header Settings */}
                <div className="qe-form__field">
                  <span className="qe-form__label">Header Arka Plan Rengi</span>
                  <input
                    className="qe-input"
                    type="color"
                    value={tableAppearance.headerBackgroundColor || "#f1f5f9"}
                    onChange={(e) =>
                      updateTableAppearance({
                        headerBackgroundColor: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Alternate Row Color */}
                <div className="qe-form__field">
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={tableAppearance.alternateRowColor}
                      onChange={(e) =>
                        updateTableAppearance({
                          alternateRowColor: e.target.checked,
                        })
                      }
                      style={{
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                      }}
                    />
                    <span className="qe-form__label" style={{ margin: 0 }}>
                      {t("props.alternateRowColor")}
                    </span>
                  </label>
                </div>

                {tableAppearance.alternateRowColor && (
                  <div className="qe-form__field">
                    <span className="qe-form__label">
                      {t("props.alternateRowColor")}
                    </span>
                    <input
                      className="qe-input"
                      type="color"
                      value={
                        tableAppearance.alternateRowBackgroundColor || "#f8fafc"
                      }
                      onChange={(e) =>
                        updateTableAppearance({
                          alternateRowBackgroundColor: e.target.value,
                        })
                      }
                    />
                  </div>
                )}

                {/* Border Radius */}
                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.borderRadius")}</span>
                  <input
                    className="qe-input"
                    type="number"
                    min="0"
                    max="50"
                    value={tableAppearance.borderRadius}
                    onChange={(e) =>
                      updateTableAppearance({
                        borderRadius: Number(e.target.value),
                      })
                    }
                  />
                </div>
          </AccordionSection>
        ) : null}


        {/* Paint Swatch Settings Section */}
        {nonNullElement.type === "paint-swatch" && paintSwatchAppearance ? (
          <div
            className={`qe-properties-section ${
              openSection === "paint-swatch-settings"
                ? "qe-properties-section--active"
                : ""
            }`}
          >
            <div
              className={`qe-properties-section__title qe-properties-section__title--collapsible ${
                openSection === "paint-swatch-settings"
                  ? "qe-properties-section__title--open"
                  : "qe-properties-section__title--closed"
              }`}
              onClick={() =>
                setOpenSection(
                  openSection === "paint-swatch-settings"
                    ? null
                    : "paint-swatch-settings"
                )
              }
            >
              <span>{t("props.paintSwatchSettings")}</span>
              <svg
                className={`qe-properties-section__icon ${
                  openSection === "paint-swatch-settings"
                    ? "qe-properties-section__icon--open"
                    : ""
                }`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <AccordionContent isOpen={openSection === "paint-swatch-settings"}>
              <div
                className="qe-properties-section__content"
                style={{ padding: 0 }}
              >
                <div className="qe-form__field">
                  {renderColorControl(
                    t("props.color"),
                    paintSwatchAppearance.color,
                    (color) => updatePaintSwatchAppearance({ color })
                  )}
                </div>
                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.size")}</span>
                  <input
                    className="qe-input"
                    type="number"
                    {...createNumberInputHandlers(
                      paintSwatchAppearance.size,
                      20,
                      200,
                      (value: number) => {
                        updatePaintSwatchAppearance({ size: value });
                        updateElementSize({
                          width: value,
                          height: value,
                        });
                      }
                    )}
                  />
                </div>
                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.borderRadius")}</span>
                  <input
                    className="qe-input"
                    type="number"
                    {...createNumberInputHandlers(
                      paintSwatchAppearance.borderRadius,
                      0,
                      50,
                      (value: number) =>
                        updatePaintSwatchAppearance({ borderRadius: value })
                    )}
                  />
                </div>
                <div className="qe-form__field">
                  <span className="qe-form__label">{t("props.borderWidth")}</span>
                  <input
                    className="qe-input"
                    type="number"
                    {...createNumberInputHandlers(
                      paintSwatchAppearance.borderWidth,
                      0,
                      10,
                      (value: number) =>
                        updatePaintSwatchAppearance({ borderWidth: value })
                    )}
                  />
                </div>
                <div className="qe-form__field">
                  {renderColorControl(
                    t("props.borderColor"),
                    paintSwatchAppearance.borderColor,
                    (color) =>
                      updatePaintSwatchAppearance({ borderColor: color })
                  )}
                </div>
                <div className="qe-form__field">
                  <span className="qe-form__label">
                    {t("props.maxUsage")}
                  </span>
                  <input
                    className="qe-input"
                    type="number"
                    {...createNumberInputHandlers(
                      paintSwatchAppearance.maxUses ?? 0,
                      0,
                      999,
                      (value: number) =>
                        updatePaintSwatchAppearance({ maxUses: value })
                    )}
                  />
                </div>
                <div className="qe-form__field">
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={paintSwatchAppearance.showLabel}
                      onChange={(e) =>
                        updatePaintSwatchAppearance({
                          showLabel: e.target.checked,
                        })
                      }
                      style={{
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                      }}
                    />
                    <span className="qe-form__label" style={{ margin: 0 }}>
                      {t("props.showLabelToggle")}
                    </span>
                  </label>
                </div>
                {paintSwatchAppearance.showLabel && (
                  <>
                    <div className="qe-form__field">
                      <label className="qe-form__label">{t("props.labelText")}</label>
                      <input
                        type="text"
                        className="qe-input"
                        value={paintSwatchAppearance.label || ""}
                        onChange={(e) =>
                          updatePaintSwatchAppearance({ label: e.target.value })
                        }
                      />
                    </div>
                    <div className="qe-form__field">
                      {renderColorControl(
                        t("props.labelColor"),
                        paintSwatchAppearance.labelColor || "#1f2430",
                        (color) =>
                          updatePaintSwatchAppearance({ labelColor: color })
                      )}
                    </div>
                    <div className="qe-form__field">
                      <span className="qe-form__label">{t("props.labelFontSize")}</span>
                      <input
                        className="qe-input"
                        type="number"
                        {...createNumberInputHandlers(
                          paintSwatchAppearance.labelFontSize || 12,
                          8,
                          24,
                          (value: number) =>
                            updatePaintSwatchAppearance({
                              labelFontSize: value,
                            })
                        )}
                      />
                    </div>
                  </>
                )}
              </div>
            </AccordionContent>
          </div>
        ) : null}

        {/* Katmanlar */}
        <AccordionSection
          title={`${t("props.layers")} (${elements.length})`}
          isOpen={isLayersAccordionOpen}
          onToggle={() => setIsLayersAccordionOpen(!isLayersAccordionOpen)}
        >
          <div className="qe-layer-list">
            {sortedElements.map((element) => {
              const isActive = element.id === selectedElementId;
              const isDragged = element.id === draggedElementId;
              const label = deriveElementLabel(element);

              return (
                <div
                  key={element.id}
                  role="button"
                  tabIndex={0}
                  className={`qe-layer-item ${
                    isActive ? "qe-layer-item--active" : ""
                  } ${isDragged ? "qe-layer-item--dragging" : ""}`}
                  draggable={!isDragged}
                  onDragStart={(e) => handleDragStart(e, element.id)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, element.id)}
                  onClick={() => onSelect(element.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelect(element.id);
                    }
                  }}
                >
                  <div className="qe-layer-item__body">
                    <div className="qe-layer-item__name">{label}</div>
                    <div className="qe-layer-item__meta">
                      {element.type} • Z: {element.zIndex}
                    </div>
                  </div>
                  <div className="qe-layer-item__actions">
                    <button
                      type="button"
                      className="qe-icon-button qe-icon-button--small"
                      title={t("props.layerMoveUp")}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleLayerReorder(element.id, "forward");
                      }}
                    >
                      △
                    </button>
                    <button
                      type="button"
                      className="qe-icon-button qe-icon-button--small"
                      title={t("props.layerMoveDown")}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleLayerReorder(element.id, "backward");
                      }}
                    >
                      ▽
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </AccordionSection>
      </form>

      <MathTypeEditorModal
        isOpen={isMathEditorOpen}
        initialValue={
          editingMathOptionId
            ? // Editing a choice option
              (elementData.options as ChoiceOption[])?.find(
                (opt) => opt.id === editingMathOptionId
              )?.mathml || (elementData.options as ChoiceOption[])?.find(
                (opt) => opt.id === editingMathOptionId
              )?.latex || ""
            : editingOrderListItem
            ? editingOrderListItem.mathml || editingOrderListItem.latex || ""
            : // Editing the main MathType element
              isMathTypeElement
              ? mathTypeStyle?.mathml || mathTypeStyle?.latex || ""
              : ""
        }
        onClose={() => {
          setIsMathEditorOpen(false);
          setEditingMathOptionId(null);
          setEditingMathOrderListItemId(null);
        }}
        onSave={(mathml) => {
          if (editingMathOptionId && nonNullElement.type === "choice") {
            // Save to choice option
            updateChoiceOption(editingMathOptionId, { mathml });
          } else if (
            editingMathOrderListItemId &&
            nonNullElement.type === "order-list"
          ) {
            const items =
              (elementData.items as Array<OrderListItem>) || [];
            const updatedItems = items.map((item) =>
              item.id === editingMathOrderListItemId
                ? { ...item, mathml }
                : item
            );
            updateElementData({ items: updatedItems });
          } else if (!editingMathOptionId && isMathTypeElement) {
            // Save to main MathType element
            updateMathTypeStyle({ mathml });
          }
          setEditingMathOrderListItemId(null);
        }}
      />
      {isMediaLibraryOpen && mediaLibraryContext && (
        <MediaLibraryModal
          onClose={() => {
            setIsMediaLibraryOpen(false);
            setMediaLibraryContext(null);
          }}
          onSelect={(mediaItem) => {
            mediaLibraryContext.onSelect(mediaItem.url);
            setIsMediaLibraryOpen(false);
            setMediaLibraryContext(null);
          }}
          currentUrl={mediaLibraryContext.currentUrl}
        />
      )}
      {isSoundLibraryOpen && soundLibraryContext && (
        <SoundLibraryModal
          onClose={() => {
            setIsSoundLibraryOpen(false);
            setSoundLibraryContext(null);
          }}
          onSelect={(mediaItem) => {
            soundLibraryContext.onSelect(mediaItem.url);
            setIsSoundLibraryOpen(false);
            setSoundLibraryContext(null);
          }}
          currentUrl={soundLibraryContext.currentUrl}
        />
      )}
      {isVideoLibraryOpen && videoLibraryContext && (
        <VideoLibraryModal
          onClose={() => {
            setIsVideoLibraryOpen(false);
            setVideoLibraryContext(null);
          }}
          onSelect={(mediaItem) => {
            videoLibraryContext.onSelect(mediaItem.url);
            setIsVideoLibraryOpen(false);
            setVideoLibraryContext(null);
          }}
          currentUrl={videoLibraryContext.currentUrl}
        />
      )}
      {imagePreview && (
        <ImagePreviewModal
          imageUrl={imagePreview.url}
          imageName={imagePreview.name}
          onClose={() => setImagePreview(null)}
        />
      )}
    </>
  );
};

export { ElementPropertiesPanel };
export default ElementPropertiesPanel;
